import { action, computed, makeObservable, observable, runInAction, toJS } from "mobx";
import * as Sentry from "@sentry/react";
import { RootStore } from "src/stores/RootStore";
import {
    activeRoleStates,
    IAccount,
    IAccounting,
    IAssignedUser,
    ICostCenter,
    ICreateInvoiceResponse,
    ICreditor,
    ICreditorInfo,
    IInvoiceFile,
    IInvoiceInfo,
    IInvoicePaymentInfo,
    IInvoiceProperties,
    IInvoiceType,
    IInvoiceUpsertVariables,
    IHistory,
    IHistoryChangeEntry,
    IHistoryEmail,
    IHistoryEmailAttachment,
    IHistoryEmailLog,
    IAiDocumentDetails,
    IOrder,
    IPaymentAccount,
    IQRExtractInvoiceData,
    IRealestate,
    ITicket,
    IUnitData,
    IWorkflowUser,
    IVatData,
    IPayoutAccountData,
    INumberOfVisas,
    IInvoiceDuplicate,
    IAiDetection,
    IAiDetectionCandidate,
    IAiFieldDetection,
    IAiDetectWithFileResponse,
    AiDetectionOutcome,
    AiDetectionSource,
    AiDetectionState
} from "./InvoiceTypes";
import {
    INVOICE_ACCOUNT_UNIT_OF_MEASURE,
    INVOICE_ACCOUNTING_FIELD_TYPE,
    INVOICE_FIELD_TYPE,
    INVOICE_FILE_STATE,
    INVOICE_ORIGIN,
    INVOICE_PROPERTY,
    INVOICE_STATE,
    INVOICE_TYPE,
    LOADING_TYPE,
    LOADING_TYPE_ACCOUNTING,
    RUNNING_SUBSCRIPTION_TPYE,
    WORKFLOWUSER_ROLE,
    HISTORY_TYPE,
    INVOICE_ACCOUNT_VAT_CONFIG,
    VAT_RATE_TYPE,
    IBAN_TYPE,
    PAYMENT_INFO_TYPE,
    INVOICE_PAYMENT_INFO_PROPERTY,
    INVOICE_FILE_TYPE
} from "./InvoiceEnums";
import { findDifferences } from "src/utils/Object";
import { getFailedDeliveryEmailLog } from "src/utils/EmailDeliveryStatus";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import {
    GET_INVOICE_BY_NUMBER,
    UPSERT_INVOICE,
    UPSERT_INVOICE_COMMENT,
    GET_INVOICE_NUMBER_BY_ID,
    SUBSCRIBE_TO_INVOICE_WORKFLOWUSER_UPDATES,
    SUBSCRIBE_TO_INVOICE_STATE_UPDATE,
    SUBSCRIBE_TO_INVOICE_HISTORY_UPDATE,
    UPSERT_ACCOUNTINGS,
    DELETE_ACCOUNTING,
    GET_HISTORY_OF_INVOICE,
    GET_NUMBER_OF_VISAS,
    CHECK_EXISTING_ACCOUNTINGS_BY_OTHER_USER,
    DELETE_ACCOUNTINGS_BY_INVCOICE_ID,
    UPDATE_INVOICE_COMMENT_IS_PINNED,
    SUBSCRIBE_TO_INVOICE_IS_PROCESSING_UPDATE,
    MARK_DETECTION_USER_SOURCE,
    REFRESH_INVOICE_SEARCH_VECTOR
} from "src/api/cred";
import { MarkDetectionUserSource, MarkDetectionUserSourceVariables } from "src/api/generated/MarkDetectionUserSource";
import { getInvoiceStateById } from "./InvoiceListStore";
import {
    GetInvoiceByNumber,
    GetInvoiceByNumberVariables,
    GetInvoiceByNumber_cred_invoices,
    GetInvoiceByNumber_cred_invoices_creditor,
    GetInvoiceByNumber_cred_invoices_ticket,
    GetInvoiceByNumber_cred_invoices_history,
    GetInvoiceByNumber_cred_invoices_invoicefiles,
    GetInvoiceByNumber_cred_invoices_ai_detections
} from "src/api/generated/GetInvoiceByNumber";
import { GetInvoiceNumberById, GetInvoiceNumberByIdVariables } from "src/api/generated/GetInvoiceNumberById";
import i18next from "i18next";
import { cloneDeep, isEmpty, isObject } from "lodash";
import {
    getDateStringForFormElement,
    validateIBAN,
    validateInvoiceReferenceNumberWithType,
    formatDateHistoryEntry,
    getTimeStampForHistoryEntry,
    calculateNetAmountFromGrossAmount,
    calculateGrossAmountFromNetAmount,
    isValidVatProportion,
    vatProportionToNumber,
    getIBANType,
    getPaymentInfoType,
    formatAmountNumberToCurrencyString,
    formatDate
} from "./Utils";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleId, getRoleKey, Role, ErpType } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import { Route } from "src/config/routes";
import { UpsertInvoice, UpsertInvoiceVariables } from "src/api/generated/UpsertInvoice";
import { GetHistoryOfInvoice, GetHistoryOfInvoiceVariables } from "src/api/generated/GetHistoryOfInvoice";
import { DELETE_FILE, SET_INVOICE_FILE_STATE_DELETED } from "src/api/ticket";
import { DeleteFile, DeleteFileVariables } from "src/api/generated/DeleteFile";
import { Subscription } from "zen-observable-ts";
import {
    SubscribeToInvoiceWorkflowUserUpdates,
    SubscribeToInvoiceWorkflowUserUpdates_workflowusers,
    SubscribeToInvoiceWorkflowUserUpdatesVariables
} from "src/api/generated/SubscribeToInvoiceWorkflowUserUpdates";
import { UpsertInvoiceComment, UpsertInvoiceCommentVariables } from "src/api/generated/UpsertInvoiceComment";
import {
    SubscribeToInvoiceStateUpdate,
    SubscribeToInvoiceStateUpdate_invoice,
    SubscribeToInvoiceStateUpdateVariables
} from "src/api/generated/SubscribeToInvoiceStateUpdate";
import {
    SubscribeToInvoiceHistoryUpdate,
    SubscribeToInvoiceHistoryUpdateVariables
} from "src/api/generated/SubscribeToInvoiceHistoryUpdate";
import { cred_accountings_insert_input } from "src/api/generated/globalTypes";
import { UpsertAccountings, UpsertAccountingsVariables } from "src/api/generated/UpsertAccountings";
import { v4 as uuidv4 } from "uuid";
import { DeleteAccounting, DeleteAccountingVariables } from "src/api/generated/DeleteAccounting";
import { format, utcToZonedTime } from "date-fns-tz";
import { addDays } from "date-fns";
import { GetNumberOfVisas } from "src/api/generated/GetNumberOfVisas";
import {
    IAbortInvoiceProcessRequestBody,
    IConcludeHumanTaskRequestBody,
    IGetDuplicatesRequestBody,
    IGetDuplicatesResponse
} from "src/network/NetworkTypes";
import {
    SetInvoiceFileStateDeleted,
    SetInvoiceFileStateDeletedVariables
} from "src/api/generated/SetInvoiceFileStateDeleted";
import Axios from "src/network/Axios";
import { IFile } from "src/types";
import { FILE_ENTITY, FILE_STATE } from "src/enums";
import {
    CheckExistingAccountingsByOtherUser,
    CheckExistingAccountingsByOtherUserVariables
} from "src/api/generated/CheckExistingAccountingsByOtherUser";
import {
    DeleteAccountingsByInvoiceId,
    DeleteAccountingsByInvoiceIdVariables
} from "src/api/generated/DeleteAccountingsByInvoiceId";
import { SCREEN_SIZE } from "src/stores/UIStore";
import {
    UpdateInvoiceCommentIsPinned,
    UpdateInvoiceCommentIsPinnedVariables
} from "src/api/generated/UpdateInvoiceCommentIsPinned";
import {
    SubscribeToInvoiceIsProcessingUpdate,
    SubscribeToInvoiceIsProcessingUpdate_invoice,
    SubscribeToInvoiceIsProcessingUpdateVariables
} from "src/api/generated/SubscribeToInvoiceIsProcessingUpdate";

import {
    RefreshInvoiceSearchVector,
    RefreshInvoiceSearchVectorVariables
} from "src/api/generated/RefreshInvoiceSearchVector";

const uuidRegex = {
    INVOICE_ID: /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
};

export class InvoiceStore {
    rootStore: RootStore;

    invoiceTypes: IInvoiceType[] = [
        {
            name: `${i18next.t(`screens.kredi_flow.invoice_type.${String(INVOICE_TYPE.CREDIT)}`)}`,
            type: INVOICE_TYPE.CREDIT
        },
        {
            name: `${i18next.t(`screens.kredi_flow.invoice_type.${String(INVOICE_TYPE.INVOICE)}`)}`,
            type: INVOICE_TYPE.INVOICE
        }
    ];

    currentInvoice: Invoice | undefined;
    isEditing: boolean = false;
    isAccountingEditing: boolean = false;
    loadingType: LOADING_TYPE = LOADING_TYPE.NONE;
    loadingTypeAccounting: LOADING_TYPE_ACCOUNTING = LOADING_TYPE_ACCOUNTING.NONE;
    runningSubscriptionType = RUNNING_SUBSCRIPTION_TPYE.NONE;

    error?: {
        loadingInvoice: string;
    } = undefined;

    NEW_INVOICE_ID = "new-invoice";

    numberOfDisplayedItems: number = 100;
    sliceSizeOfDisplayedItems: number = 50;

    userDefinedAfterSaveFunction?: () => void;
    userDefinedAfterSaveAccountingsFunction?: () => void;

    scrollToInvoiceDetails: boolean = false;

    newlyCreatedInvoiceNumber?: number = undefined;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            error: observable,
            currentInvoice: observable,
            isEditing: observable,
            isAccountingEditing: observable,
            loadingType: observable,
            loadingTypeAccounting: observable,
            runningSubscriptionType: observable,
            numberOfDisplayedItems: observable,
            sliceSizeOfDisplayedItems: observable,
            userDefinedAfterSaveFunction: observable,
            userDefinedAfterSaveAccountingsFunction: observable,
            scrollToInvoiceDetails: observable,
            newlyCreatedInvoiceNumber: observable,

            setCurrentInvoice: action,
            init: action,
            initForm: action,
            setRealestateFormField: action,
            setPayoutAccountFormField: action,
            setUnitFormField: action,
            setTicketFormField: action,
            setOrderFormField: action,
            resetFormFields: action,
            resetRunningSubscriptionType: action,
            resetAccountingFormFields: action,
            setIsEditing: action,
            setIsAccountingEditing: action,
            startAccountingEditing: action,
            setLoadingType: action,
            setLoadingTypeAccounting: action,
            setRunningSubscriptionType: action,
            setNewlyCreatedInvoiceNumber: action,
            setNumberOfDisplayedItems: action,
            setSliceSizeOfDisplayedItems: action,
            resetCreditorFormField: action,
            resetRealestateFormField: action,
            resetPayoutAccountFormField: action,
            resetUnitFormField: action,
            resetPaymentAccountFormField: action,
            resetPaymentAccountIdFormField: action,
            resetTicketFormField: action,
            resetOrderFormField: action,
            resetAccountingAccountFormFields: action,
            resetAccountingUnitFormFields: action,
            resetAccountingCostcenterFormFields: action,
            resetAccountingVatCodeFormFields: action,
            resetAccountingVatRateFormFields: action,
            resetAllAccountingAccountFormFields: action,
            resetAllAccountingVatFormFields: action,
            resetAllAccountingRealestateFormFields: action,
            resetAllAccountingCostcenterFormFields: action,
            resetPayoutAccountFormFields: action,
            discardCurrentInvoiceChanges: action,
            discardCurrentInvoiceAccountingsChanges: action,
            setUserDefinedAfterSaveFunction: action,
            loadRealestateData: action,
            loadUnitData: action,
            updateInvoiceAndFormWithSelectedRealestate: action,
            updateInvoiceAndFormWithSelectedUnit: action,
            updateInvoiceAndFormWithSelectedTicket: action,
            updateInvoiceAndFormWithSelectedOrder: action,
            updateAccountingWithSelectedRealestate: action,
            updateAccountingWithSelectedUnit: action,
            updateInvoiceStoreWithSelectedCreditor: action,
            updateAccountingWithSelectedAccount: action,
            updateAccountingWithBookingDate: action,
            updateAccountingWithBookingText: action,
            updateAccountingWithQuantity: action,
            updateAccountingWithExtraCostDate: action,
            updateAccountingWithSelectedCostcenter: action,
            updateAccountingWithSelectedVatCode: action,
            updateAccountingWithSelectedVatRate: action,
            updateAccountingWithAmount: action,
            updateAccountingWithNetAmount: action,
            updateAccountingWithVatAmount: action,
            updateAccountingWithVatProportion: action,
            updateInvoiceWithSelectedPayoutAccountIT2: action,
            extractQrCodeDataFromPDF: action,
            aiDetectWithFile: action,
            mapExtractedDataOnCurrentInvoice: action,
            mapAiDocumentFallbackOnCurrentInvoice: action,
            saveCurrentInvoice: action,
            fireAuditStampIfNeeded: action,
            validateAndSaveInvoiceAccountings: action,
            validateAndSaveInvoiceAccountingsWithoutEvent: action,
            saveCurrentInvoiceAccountings: action,
            setScrollToInvoiceDetails: action,

            isInvoiceBannerDisplayed: computed,
            isInvoiceLocked: computed
        });
    }

    init = async (invoiceNumber: string) => {
        this.setLoadingType(LOADING_TYPE.INITIALIZING);
        this.setNewlyCreatedInvoiceNumber(undefined);

        await this.rootStore.creditorSearchStore.init();
        await this.rootStore.ticketSearchStore.init();
        await this.rootStore.orderSearchStore.init();
        await this.rootStore.assignInvoiceStore.init();
        await this.rootStore.approveRejectInvoiceStore.init();
        await this.loadRealestateData();

        this.resetFormFields();
        this.resetAccountingFormFields();
        this.resetRunningSubscriptionType();

        const initiatedInvoice: Invoice | undefined = await this.initForm(invoiceNumber);
        if (initiatedInvoice) {
            this.initializeFormFields(initiatedInvoice);
            this.initializeAccountingFormFields(initiatedInvoice.accountings ?? []);
            initiatedInvoice.initializeDisplayAmounts();

            this.rootStore.paymentAccountSearchStore.init(initiatedInvoice);

            if (invoiceNumber !== this.NEW_INVOICE_ID) {
                if (this.currentInvoice?.invoiceWorkflowUsersSubscription) {
                    this.currentInvoice.stopInvoiceWorkflowUsersSubscription();
                }

                if (
                    this.currentInvoice?.state !== undefined &&
                    this.currentInvoice.state < INVOICE_STATE.ASSIGN_WORKFLOW_USERS
                ) {
                    this.currentInvoice?.startInvoiceWorkflowUsersSubscription();
                }

                this.currentInvoice?.startInvoiceStateUpdateSubscription();

                if (this.currentInvoice?.state !== undefined && this.currentInvoice.state < INVOICE_STATE.PAID) {
                    this.currentInvoice?.startInvoiceIsWorkflowProcessingSubscription();
                }

                await this.currentInvoice?.loadNumberOfVisas();

                const needsToConcludeHumanTaskAssignRealestateAndOtherData =
                    this.currentInvoice?.state === INVOICE_STATE.ASSIGN_REALESTATE &&
                    this.currentInvoice.realestate?.id;

                if (needsToConcludeHumanTaskAssignRealestateAndOtherData) {
                    this.currentInvoice?.concludeHumanTaskAssignRealestateAndOtherData(false);
                }
            }

            runInAction(() => {
                if (
                    this.currentInvoice &&
                    this.currentInvoice.invoiceFieldsOriginal &&
                    this.currentInvoice.invoiceFieldsOriginal.accountings
                ) {
                    this.currentInvoice.invoiceFieldsOriginal.accountings = cloneDeep(
                        toJS(this.currentInvoice?.accountings)
                    );
                }
            });

            this.currentInvoice?.resetAllFieldsWithChanges();
            this.currentInvoice?.resetAllFieldsWithChangesAccounting();
        }

        this.setLoadingType(LOADING_TYPE.NONE);
    };

    initializeAccountingFormFields = async (accountings: IAccounting[]) => {
        const hasLoadedAccountings = accountings && accountings.length > 0 && accountings[0].date ? true : false;
        this.isAccountingEditing = false;

        if (hasLoadedAccountings && accountings && accountings.length > 0) {
            this.currentInvoice?.initializeDefaultAccountingVariables();

            // All accountings of an invoice share one booking date and setAccountingDate already
            // writes it to every row - once is enough (it used to run once per row).
            const accountingDate = accountings.find((accounting) => accounting.date)?.date;
            if (accountingDate) {
                this.currentInvoice?.setAccountingDate(accountingDate);
            }

            // NOTE: the per-row updates below run with runByInit = true, which keeps them from
            // triggering the account/booking-text tag loads. Those tags are only shown in edit mode
            // and are loaded once when editing starts (startAccountingEditing) - loading them here
            // made every row fan out over all rows (O(n²) GraphQL requests, #23888).
            for (const [index, accounting] of accountings.entries()) {
                if (accounting.id) {
                    this.currentInvoice?.setAccountingId(accounting.id, index);
                }

                if (accounting.realestate) {
                    await this.updateAccountingWithSelectedRealestate(accounting.realestate, false, index, true);
                }

                if (accounting.unit) {
                    this.updateAccountingWithSelectedUnit(accounting.unit, false, index);
                }

                if (accounting.account) {
                    this.updateAccountingWithSelectedAccount(accounting.account, false, index, true);
                }

                if (accounting.costCenter) {
                    this.updateAccountingWithSelectedCostcenter(accounting.costCenter, false, index);
                }

                if (accounting.text) {
                    this.updateAccountingWithBookingText(accounting.text, index);
                }

                if (accounting.amount) {
                    this.currentInvoice?.setAccountingAmount(Number(accounting.amount).toFixed(2), index);
                }

                if (accounting.quantity) {
                    this.updateAccountingWithQuantity(Number(accounting.quantity).toFixed(2), index);
                }

                if (accounting.extraCostDate) {
                    this.updateAccountingWithExtraCostDate(accounting.extraCostDate, index);
                }

                if (accounting.vatCodeId) {
                    // Realestate is checked because it is used/needed when loading the Vat Data
                    if (accounting.realestate) {
                        await this.rootStore.accountingVatStore.loadVatData(index);

                        const vatData = this.rootStore.accountingVatStore.vatData.find(
                            (vat) => vat.codeId === accounting.vatCodeId
                        );

                        if (vatData) {
                            this.updateAccountingWithSelectedVatCode(vatData, index, true);
                        }

                        if (accounting.vatRate) {
                            const currentlyValidVatRateDataByVatCodeId =
                                this.rootStore.accountingVatStore.getCurrentlyValidVatRateDataByVatCodeId(
                                    accounting.vatCodeId
                                );

                            if (currentlyValidVatRateDataByVatCodeId) {
                                this.updateAccountingWithSelectedVatRate(
                                    currentlyValidVatRateDataByVatCodeId,
                                    index,
                                    true
                                );
                            }
                        }
                    }
                }

                if (accounting.netAmount) {
                    const updateAccountingAmount = accounting.amount ? false : true;
                    this.updateAccountingWithNetAmount(
                        Number(accounting.netAmount).toFixed(2),
                        index,
                        updateAccountingAmount
                    );
                }

                if (accounting.vatAmount) {
                    this.updateAccountingWithVatAmount(Number(accounting.vatAmount).toFixed(2), index);
                }

                if (accounting.vatProportion) {
                    this.currentInvoice?.setAccountingVatProportion(accounting.vatProportion, index);
                }
            }
        } else if (!this.isInvoiceLocked) {
            this.currentInvoice?.initializeDefaultAccountingObjects();
            this.setIsAccountingEditing(true);
        }
    };

    initForm = async (invoiceNumber: string): Promise<Invoice | undefined> => {
        // Invoice that was newly created or loaded
        let initiatedInvoice: Invoice | undefined = undefined;

        if (invoiceNumber === this.NEW_INVOICE_ID) {
            this.resetFormFields();
            this.setIsEditing(true);
            initiatedInvoice = new Invoice(this.rootStore);
            this.setCurrentInvoice(initiatedInvoice);
        } else {
            initiatedInvoice = await this.loadCurrentInvoice(invoiceNumber);

            if (initiatedInvoice) {
                this.setCurrentInvoice(initiatedInvoice);
                initiatedInvoice.resetAllFieldsWithChanges();
            }
        }

        return initiatedInvoice;
    };

    discardCurrentInvoiceChanges = async () => {
        const invoiceNumber =
            !this.currentInvoice?.isNewInvoice || this.currentInvoice?.number !== undefined
                ? String(this.currentInvoice?.number)
                : this.NEW_INVOICE_ID;

        this.resetFormFields();

        await this.initForm(invoiceNumber);
    };

    discardCurrentInvoiceAccountingsChanges = () => {
        if (this.currentInvoice?.accountings) {
            this.currentInvoice.accountings = [this.currentInvoice.getDefaultAccountingObject()];
        }

        this.initializeAccountingFormFields(
            toJS(cloneDeep(this.currentInvoice?.invoiceFieldsOriginal.accountings)) ?? []
        );
        this.currentInvoice?.resetAllFieldsWithChangesAccounting();
    };

    resetRunningSubscriptionType = () => {
        this.setRunningSubscriptionType(RUNNING_SUBSCRIPTION_TPYE.NONE);
    };

    resetFormFields = () => {
        this.currentInvoice?.reset();

        this.resetCreditorFormField();
        this.resetPaymentAccountFormField();
        this.resetPaymentAccountIdFormField();
        this.resetRealestateFormField();
        this.resetPayoutAccountFormField();
        this.resetUnitFormField();
        this.resetTicketFormField();
        this.resetOrderFormField();
    };

    resetCreditorFormField = () => {
        this.currentInvoice?.updateProperty(INVOICE_PROPERTY.CREDITOR, undefined);
    };

    resetPaymentAccountFormField = () => {
        this.currentInvoice?.updateProperty(INVOICE_PROPERTY.PAYMENT_ACCOUNT, undefined);

        this.currentInvoice?.updateProperty(INVOICE_PROPERTY.PAYMENT_ACCOUNT_ID, undefined);

        if (!isEmpty(this.currentInvoice?.paymentInfo) && this.currentInvoice?.paymentInfo.iban) {
            this.currentInvoice?.updatePaymentInfoProperty(INVOICE_PAYMENT_INFO_PROPERTY.IBAN, undefined);
        }

        this.rootStore.paymentAccountSearchStore.currentSelectedIBAN = "";
    };

    resetPaymentAccountIdFormField = () => {
        this.currentInvoice?.updateProperty(INVOICE_PROPERTY.PAYMENT_ACCOUNT_ID, undefined);
    };

    resetTicketFormField = () => {
        const ticketSearchStore = this.rootStore.ticketSearchStore;
        ticketSearchStore.setCurrentTicketSearchQuery("");
        ticketSearchStore.setSelectedTicketQueryString("");

        this.currentInvoice?.updateProperty(INVOICE_PROPERTY.TICKET, undefined);

        this.resetOrderFormField();
    };

    resetOrderFormField = () => {
        const orderSearchStore = this.rootStore.orderSearchStore;
        orderSearchStore.setCurrentOrderSearchQuery("");
        orderSearchStore.setSelectedOrderQueryString("");

        orderSearchStore.resetOrderSearchFilter();

        this.currentInvoice?.updateProperty(INVOICE_PROPERTY.ORDER, undefined);
    };

    resetRealestateFormField = () => {
        this.currentInvoice?.updateProperty(INVOICE_PROPERTY.REALESTATE, undefined);

        const realestateSearchStore = this.rootStore.realestateSearchStore;
        realestateSearchStore.setCurrentRealestateSearchQuery("");
        realestateSearchStore.setSelectedRealestateQueryString("");
        realestateSearchStore.setSelectedRealestateName("");

        this.resetUnitFormField();
        this.resetPayoutAccountFormField();
    };

    resetPayoutAccountFormField = () => {
        this.currentInvoice?.updateProperty(INVOICE_PROPERTY.PAYOUT_ACCOUNT_ID, undefined);
        const accountingPayoutAccountStore = this.rootStore.accountingPayoutAccountStore;
        accountingPayoutAccountStore.reset();
    };

    resetUnitFormField = () => {
        this.currentInvoice?.updateProperty(INVOICE_PROPERTY.UNIT, undefined);

        const unitSearchStore = this.rootStore.unitSearchStore;
        unitSearchStore.setCurrentUnitSearchQuery("");
        unitSearchStore.setSelectedUnitQueryString("");
    };

    resetAccountingFormFields = () => {
        this.resetAllAccountingRealestateFormFields();
        this.resetAllAccountingUnitFormFields();
        this.resetAllAccountingAccountFormFields();
        this.resetAllAccountingCostcenterFormFields();
        this.resetAllAccountingVatFormFields();
        // Booking-text tags were never reset between invoices - rows of the previous invoice leaked
        // into the next one and its request cache would answer for the wrong creditor.
        this.rootStore.accountingBookingTextStore.reset();
    };

    resetAllAccountingCostcenterFormFields = () => {
        this.rootStore.accountingCostcenterStore.reset();
    };

    resetAllAccountingRealestateFormFields = () => {
        this.rootStore.accountingRealestateSearchStore.reset();
    };

    resetAllAccountingUnitFormFields = () => {
        this.rootStore.accountingUnitSearchStore.reset();
    };

    resetAllAccountingAccountFormFields = () => {
        this.rootStore.accountingAccountSearchStore.reset();
    };

    resetAllAccountingVatFormFields = () => {
        this.rootStore.accountingVatStore.reset();
    };

    loadCurrentInvoice = async (invoiceNumber: string): Promise<Invoice | undefined> => {
        this.setIsEditing(false);

        let loadedInvoice: Invoice | undefined = undefined;

        /* LOAD INVOICE FROM SERVER */
        this.resetFormFields();
        this.error = undefined;

        const { data }: { data: GetInvoiceByNumber } = await apolloClientInstance.query<
            GetInvoiceByNumber,
            GetInvoiceByNumberVariables
        >({
            query: GET_INVOICE_BY_NUMBER,
            fetchPolicy: "no-cache",
            variables: {
                invoiceNumber: Number(invoiceNumber)
            }
        });

        if (data.cred_invoices.length === 0) {
            const errorMessage = i18next.t("screens.kredi_flow.invoice_details.error.invoice_not_found");
            this.error = {
                loadingInvoice: errorMessage
            };

            return undefined;
        }

        const newInvoiceData = await this.getInvoiceObjectFromGetInvoiceByNumberQuery(data.cred_invoices[0]);

        loadedInvoice = new Invoice(this.rootStore, newInvoiceData);

        return loadedInvoice;
    };

    loadRealestateData = async () => {
        await this.rootStore.realestateSearchStore.loadRealestateData();
    };

    loadUnitData = async (realestateId: string) => {
        await this.rootStore.unitSearchStore.loadUnitData(realestateId);
    };

    getInvoiceObjectFromGetInvoiceByNumberQuery = async (
        invoice: GetInvoiceByNumber_cred_invoices
    ): Promise<IInvoiceProperties> => {
        const { zip, city } =
            invoice.realestate && invoice.realestate.houses.length > 0
                ? { zip: invoice.realestate.houses[0].zip ?? "", city: invoice.realestate.houses[0].city ?? "" }
                : { zip: "", city: "" };

        const realestate: IRealestate | undefined = invoice.realestate
            ? {
                  id: invoice.realestate.id,
                  number: invoice.realestate.number,
                  name: invoice.realestate.name,
                  zip,
                  city,
                  consolidation: invoice.realestate.consolidation ?? undefined,
                  consolidationId: invoice.realestate.consolidationid ?? undefined
              }
            : undefined;

        const unit: IUnitData | undefined = invoice.unit
            ? {
                  houseId: invoice.unit.houseid,
                  unitId: invoice.unit.id,
                  unitNumber: invoice.unit.number ?? undefined,
                  unitName: invoice.unit.name ?? "",
                  tenancyData: undefined
              }
            : undefined;

        const creditor = this.getCreditorObjectFromGetInvoiceByNumberQueryCreditorResult(invoice.creditor);

        const paymentInfo: IInvoicePaymentInfo | undefined =
            this.getPaymentInfoObjectFromGetInvoiceByNumberQueryCreditorResult(invoice.paymentinfo);

        const ticket = invoice.ticket
            ? this.getTicketInfoObjectFromGetInvoiceByNumberQueryCreditorResult(invoice.ticket)
            : undefined;

        const order: IOrder | undefined = invoice.order
            ? {
                  id: invoice.order.orderid ?? undefined,
                  number: invoice.order.number ?? undefined,
                  date: invoice.order.date ?? undefined,
                  date_long: invoice.order.date_long ?? undefined,
                  contractorId: invoice.order.contractorid ?? undefined,
                  title: invoice.order.title ?? undefined,
                  instruction: invoice.order.instruction ?? undefined,
                  contractorName1: invoice.order.name1 ?? undefined,
                  contractorName2: invoice.order.name2 ?? undefined,
                  ticket: invoice.order.ticket
                      ? this.getTicketInfoObjectFromGetInvoiceByNumberQueryCreditorResult(invoice.order.ticket)
                      : undefined,
                  contractorType: invoice.order.contractortype ?? undefined
              }
            : undefined;

        const history: IHistory[] = this.getHistoryObjectFromQueryData(invoice.history);

        const invoiceFileFromDb: GetInvoiceByNumber_cred_invoices_invoicefiles | undefined = invoice.invoicefiles.find(
            (invoiceFile) => invoiceFile.type === INVOICE_FILE_TYPE.INVOICE
        );

        const invoiceFile: IInvoiceFile | undefined =
            invoiceFileFromDb && invoiceFileFromDb.fileid
                ? {
                      fileId: invoiceFileFromDb.fileid,
                      invoiceFileId: invoiceFileFromDb.invoicefileid,
                      type: invoiceFileFromDb.type ?? undefined,
                      state: INVOICE_FILE_STATE.LOADED_FROM_DB
                  }
                : undefined;

        const invoiceAttachmentFilesFromDb: GetInvoiceByNumber_cred_invoices_invoicefiles[] | undefined =
            invoice.invoicefiles.filter((invoiceFile) => invoiceFile.type === INVOICE_FILE_TYPE.INVOICE_ATTACHMENT);

        const invoiceAttachmentFiles: IFile[] =
            invoiceAttachmentFilesFromDb && invoiceAttachmentFilesFromDb.length > 0
                ? invoiceAttachmentFilesFromDb.map((invoiceFile) => {
                      const file: IFile = {
                          fileId: invoiceFile.fileid,
                          entityFileId: invoiceFile.invoicefileid,
                          type: invoiceFile.type ?? undefined,
                          state: FILE_STATE.LOADED_FROM_DB,
                          name: invoiceFile.name ?? "",
                          extension: invoiceFile.extension ?? "",
                          mimetype: invoiceFile.mimetype ?? ""
                      };

                      return file;
                  })
                : [];

        if (invoiceFile && invoiceFile.fileId) {
            const downloadedInvoiceFile = await this.downloadInvoiceFile(invoiceFile.fileId);
            if (downloadedInvoiceFile) {
                const fileBlob = downloadedInvoiceFile.data;
                const objUrl = window.URL.createObjectURL(fileBlob);
                invoiceFile.url = objUrl;
            }
        }

        const workflowUsers: IWorkflowUser[] = invoice.workflowusers.map((workflowUser) => {
            return {
                role: workflowUser.role,
                id: workflowUser.id,
                signaturestamp: workflowUser.signaturestamp,
                user: {
                    id: workflowUser.user.id,
                    name1: workflowUser.user.name1 ?? "",
                    name2: workflowUser.user.name2 ?? ""
                }
            };
        });

        const accountings: IAccounting[] = invoice.accountings.map((accounting) => {
            const { zip, city } =
                accounting.realestate && accounting.realestate.houses.length > 0
                    ? {
                          zip: accounting.realestate.houses[0].zip ?? "",
                          city: accounting.realestate.houses[0].city ?? ""
                      }
                    : { zip: "", city: "" };

            const accountingRealestate: IRealestate | undefined = accounting.realestate
                ? {
                      id: accounting.realestate.id,
                      number: accounting.realestate.number,
                      name: accounting.realestate.name,
                      zip,
                      city,
                      consolidation: accounting.realestate.consolidation ?? undefined,
                      consolidationId: accounting.realestate.consolidationid ?? undefined
                  }
                : undefined;

            const accountingUnit: IUnitData | undefined = accounting.unit
                ? {
                      houseId: accounting.unit.houseid,
                      unitId: accounting.unit.id,
                      unitNumber: accounting.unit.number ?? undefined,
                      unitName: accounting.unit.name ?? ""
                  }
                : undefined;

            const accountingAccount: IAccount | undefined = accounting.account
                ? {
                      id: accounting.account.id,
                      number: accounting.account.number,
                      name: accounting.account.name,
                      requiresCostCenter: accounting.account.requirescostcenter,
                      requiresQuantity: accounting.account.requiresquantitiy,
                      unitOfMeasure: accounting.account.unitofmeasure ?? INVOICE_ACCOUNT_UNIT_OF_MEASURE.N_A,
                      extraCostDate: accounting.account.extracostdate,
                      vatConfig: accounting.account.vatconfig ?? INVOICE_ACCOUNT_VAT_CONFIG.WITHOUT_VAT,
                      defaultVatCodeId: accounting.account.defaultvatcodeid ?? undefined,
                      validFrom: accounting.account.validfrom ?? undefined,
                      validTo: accounting.account.validto ?? undefined,
                      realestateId: accounting.account.realestate?.id ?? undefined
                  }
                : undefined;

            const accountingCostCenter: ICostCenter | undefined = accounting.costcenter
                ? {
                      id: accounting.costcenter.id,
                      name: accounting.costcenter.name,
                      short: accounting.costcenter.short
                  }
                : undefined;

            let vatRate = undefined;

            if (accounting.vatrate !== undefined && accounting.vatrate !== null) {
                vatRate = String(accounting.vatrate);
            }

            return {
                id: accounting.id,
                date: accounting.date,
                amount: String(accounting.amount),
                text: accounting.text,
                quantity:
                    accounting.quantity || accounting.quantity === 0
                        ? Number(accounting.quantity).toFixed(2)
                        : undefined,
                extraCostDate: accounting.extracostdate,
                realestate: accountingRealestate,
                unit: accountingUnit,
                account: accountingAccount,
                costCenter: accountingCostCenter,
                vatCodeId: accounting.vatcodeid,
                vatRate: vatRate,
                netAmount: accounting.netamount ? String(accounting.netamount) : undefined,
                vatAmount: accounting.vatamount ? String(accounting.vatamount) : undefined,
                vatProportion:
                    accounting.vatproportion !== null && accounting.vatproportion !== undefined
                        ? String(accounting.vatproportion)
                        : undefined
            };
        });

        let duplicates: IInvoiceDuplicate[] = [];

        if (invoice.duplicates) {
            duplicates = invoice.duplicates
                .map((duplicate: any) => {
                    return {
                        invoiceId: duplicate.InvoiceId,
                        invoiceNumber: duplicate.InvoiceNumber,
                        amount: duplicate.Amount
                    };
                })
                .sort((a: IInvoiceDuplicate, b: IInvoiceDuplicate) => {
                    return a.invoiceNumber - b.invoiceNumber;
                });
        }

        const aiDetection: IAiDetection | undefined = this.rootStore.navStore.hasFeatureKrediFlowAiExtract
            ? this.buildAiDetectionFromQuery(invoice.ai_detections)
            : undefined;

        const newInvoiceData: IInvoiceProperties = {
            id: invoice.id,
            origin: invoice.origin,
            invoiceType: invoice.type as INVOICE_TYPE,
            number: invoice.number,
            state: getInvoiceStateById(invoice.state),
            invoiceNumber: invoice.invoicenumber ?? undefined,
            amount: invoice.amount ?? undefined,
            date: invoice.date,
            dueDate: invoice.duedate,
            paidDate: invoice.paiddate,
            realestate: realestate,
            unit: unit,
            creditor: creditor,
            history: history,
            paymentaccountid: invoice.paymentaccountid ?? undefined,
            paymentAccount: invoice.paymentaccount ?? undefined,
            payoutaccountid: invoice.payoutaccountid ?? undefined,
            paymentInfo: paymentInfo,
            ticket: ticket,
            order: order,
            invoiceFile: invoiceFile,
            invoiceAttachmentFiles: invoiceAttachmentFiles,
            aiDetection: aiDetection,
            workflowUsers: workflowUsers,
            workflowInstance: invoice.workflowinstance ?? undefined,
            accountings: accountings.length > 0 ? accountings : undefined,
            duplicates: duplicates,
            numberOfVisa: invoice.numberofvisa ?? undefined,
            isProcessing: invoice.isprocessing ?? false,
            paymentPurpose: invoice.paymentpurpose ?? undefined
        };

        return newInvoiceData;
    };

    /**
     * Build an in-memory IAiDetection from the cred_invoice_ai_detections row(s)
     * returned by GetInvoiceByNumber. Defensive: returns undefined for empty
     * arrays, pending/failed state, and missing outcome — those all map to
     * "no usable detection" in the UI (regular dropdown, no banner).
     */
    buildAiDetectionFromQuery = (
        detectionsFromQuery: GetInvoiceByNumber_cred_invoices_ai_detections[]
    ): IAiDetection | undefined => {
        if (!detectionsFromQuery || detectionsFromQuery.length === 0) {
            return undefined;
        }

        // The query returns one row per field (realestateid + creditorid), newest
        // first. Keep the latest completed row per field.
        const latestByField = new Map<string, GetInvoiceByNumber_cred_invoices_ai_detections>();
        for (const detectionRow of detectionsFromQuery) {
            if (detectionRow.state !== "completed" || !detectionRow.outcome) {
                continue;
            }
            if (!latestByField.has(detectionRow.field)) {
                latestByField.set(detectionRow.field, detectionRow);
            }
        }

        const realestateRow = latestByField.get("realestateid");
        const creditorRow = latestByField.get("creditorid");

        // A field is missing when its AI lookup failed; a detection with at least
        // one completed field is still usable (per-field state only, no anchor).
        if (!realestateRow && !creditorRow) {
            return undefined;
        }

        const toFieldDetection = (row: GetInvoiceByNumber_cred_invoices_ai_detections): IAiFieldDetection => {
            const candidates: IAiDetectionCandidate[] = (row.candidates ?? []).map((candidate) => ({
                id: candidate.id,
                rank: candidate.rank,
                entityid: candidate.entityid,
                displayname: candidate.displayname ?? "",
                payload: candidate.payload ?? {}
            }));

            return {
                detectionid: row.id,
                outcome: row.outcome as AiDetectionOutcome,
                candidateCount: candidates.length,
                candidates,
                acceptedsource: (row.acceptedsource as AiDetectionSource | null) ?? null,
                acceptedcandidateid: row.acceptedcandidateid ?? null,
                wasAutoAppliedByAi: false
            };
        };

        const results: IAiDetection["results"] = {};
        if (realestateRow) {
            results.realestateid = toFieldDetection(realestateRow);
        }
        if (creditorRow) {
            results.creditorid = toFieldDetection(creditorRow);
        }

        return {
            // Only completed rows made it into `results` (see the filter above).
            state: "completed" as AiDetectionState,
            results,
            uploadMetadata: undefined
        };
    };

    downloadInvoiceFile = async (fileId: string): Promise<any | undefined> => {
        let downloadedInvoiceFile = undefined;

        try {
            const response = await Axios.getAuthInstance().get(`${NetworkConfig.downloadFileVersion2}/${fileId}`, {
                urlWithoutParams: NetworkConfig.downloadFileVersion2,
                responseType: "blob",
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });
            downloadedInvoiceFile = response;
        } catch (error) {
            console.error("Error downloading invoice file: ", error);
        }

        return downloadedInvoiceFile;
    };

    getTicketInfoObjectFromGetInvoiceByNumberQueryCreditorResult = (
        ticketResultFromQuery: GetInvoiceByNumber_cred_invoices_ticket
    ): ITicket | undefined => {
        const ticket: ITicket | undefined = ticketResultFromQuery
            ? {
                  id: ticketResultFromQuery.incidentid ?? undefined,
                  number: ticketResultFromQuery.number ?? undefined,
                  title: ticketResultFromQuery.title ?? undefined,
                  date: ticketResultFromQuery.date ?? undefined,
                  date_long: ticketResultFromQuery.date_long ?? undefined,
                  realestateid: ticketResultFromQuery.realestateid ?? undefined,
                  unitid: ticketResultFromQuery.unitid ?? undefined
              }
            : undefined;

        return ticket;
    };

    getPaymentInfoObjectFromGetInvoiceByNumberQueryCreditorResult = (
        invoicePaymentInfo: any
    ): IInvoicePaymentInfo | undefined => {
        let paymentInfo: IInvoicePaymentInfo | undefined = undefined;

        if (invoicePaymentInfo && isObject(invoicePaymentInfo) && !isEmpty(invoicePaymentInfo)) {
            paymentInfo = {};

            if ("iban" in invoicePaymentInfo && typeof invoicePaymentInfo.iban === "string") {
                paymentInfo.iban = invoicePaymentInfo.iban;
            }

            if ("type" in invoicePaymentInfo && typeof invoicePaymentInfo.type === "string") {
                paymentInfo.type = invoicePaymentInfo.type;
            }

            if ("reference" in invoicePaymentInfo && typeof invoicePaymentInfo.reference === "string") {
                paymentInfo.reference = invoicePaymentInfo.reference;
            }
        }

        return paymentInfo;
    };

    getCreditorObjectFromGetInvoiceByNumberQueryCreditorResult = (
        creditorResultFromQuery: GetInvoiceByNumber_cred_invoices_creditor | null
    ): ICreditor | undefined => {
        const creditor: ICreditor | undefined = creditorResultFromQuery
            ? {
                  id: creditorResultFromQuery.creditorid,
                  name: creditorResultFromQuery.creditorname ?? "",
                  number: creditorResultFromQuery.creditornumber ?? undefined,
                  personId: creditorResultFromQuery.personid ?? undefined,
                  name1: creditorResultFromQuery.name1 ?? undefined,
                  name2: creditorResultFromQuery.name2 ?? undefined,
                  mobile: creditorResultFromQuery.mobile ?? undefined,
                  phonebusiness: creditorResultFromQuery.phonebusiness ?? undefined,
                  email: creditorResultFromQuery.email ?? undefined,
                  gender: creditorResultFromQuery.gender ?? undefined,
                  origin: creditorResultFromQuery.person_origin ?? undefined,
                  street: creditorResultFromQuery.street ?? undefined,
                  housenumber: creditorResultFromQuery.housenumber ?? undefined,
                  zip: creditorResultFromQuery.zip ?? undefined,
                  city: creditorResultFromQuery.city ?? undefined,
                  paymentAccounts: creditorResultFromQuery.paymentaccounts ?? undefined
              }
            : undefined;

        return creditor;
    };

    initializeFormFields = async (invoice: Invoice) => {
        await this.setRealestateFormField(invoice);
        await this.setPayoutAccountFormField(invoice);
        await this.setUnitFormField(invoice);
        await this.setTicketFormField(invoice);
        await this.setOrderFormField(invoice);
    };

    getHistoryChangeEntries = (history: GetInvoiceByNumber_cred_invoices_history): IHistoryChangeEntry[] => {
        const changeObjectArray: IHistoryChangeEntry[] = [];

        Object.entries(history.new_values).forEach(([key, value]) => {
            const changeObject: IHistoryChangeEntry = {};

            let oldValue = history.old_values && history.old_values[key] ? history.old_values[key] : undefined;
            let newValue = value;

            switch (key) {
                case INVOICE_PROPERTY.STATE.toLowerCase():
                    oldValue = oldValue
                        ? i18next.t(`screens.kredi_flow.state.${getInvoiceStateById(Number(oldValue))}`)
                        : "-";
                    newValue = newValue
                        ? i18next.t(`screens.kredi_flow.state.${getInvoiceStateById(Number(newValue))}`)
                        : "-";
                    break;

                case INVOICE_PROPERTY.TYPE.toLowerCase():
                case INVOICE_PROPERTY.INVOICE_TYPE.toLowerCase():
                    oldValue = oldValue
                        ? `${i18next.t(`screens.kredi_flow.invoice_type.${String(oldValue)}`)}`
                        : undefined;
                    newValue = newValue
                        ? `${i18next.t(`screens.kredi_flow.invoice_type.${String(newValue)}`)}`
                        : undefined;
                    break;

                case INVOICE_PROPERTY.ORIGIN.toLowerCase():
                    oldValue = oldValue ? `${i18next.t(`screens.kredi_flow.origin.${String(oldValue)}`)}` : undefined;
                    newValue = newValue ? `${i18next.t(`screens.kredi_flow.origin.${String(newValue)}`)}` : undefined;
                    break;

                case INVOICE_PROPERTY.INVOICE_INFO.toLowerCase():
                case INVOICE_PROPERTY.CREDITOR_INFO.toLowerCase():
                case INVOICE_PROPERTY.PAYMENT_INFO.toLowerCase():
                    // TODO: jle -> build nested components if needed
                    break;
            }

            const keysWithNestedObjects = [
                INVOICE_PROPERTY.INVOICE_INFO.toLowerCase(),
                INVOICE_PROPERTY.CREDITOR_INFO.toLowerCase(),
                INVOICE_PROPERTY.PAYMENT_INFO.toLowerCase(),
                INVOICE_PROPERTY.INSERT_USER_ID.toLowerCase()
            ];

            if (!keysWithNestedObjects.includes(key)) {
                changeObject.description = i18next.exists(`screens.kredi_flow.history.${key}`)
                    ? i18next.t(`screens.kredi_flow.history.${key}`).toString()
                    : key;
                changeObject.oldValue = oldValue ?? "-";
                changeObject.newValue = newValue;

                changeObjectArray.push(changeObject);
            }
        });

        return changeObjectArray;
    };

    getHistoryChangeText = (historyType: HISTORY_TYPE, history: GetInvoiceByNumber_cred_invoices_history): string => {
        let changeText = "";

        switch (historyType) {
            case HISTORY_TYPE.INVOICE_CREATED:
                changeText = i18next.t("screens.kredi_flow.history.invoice_created");
                break;
            case HISTORY_TYPE.INVOICE_STATUS_CHANGED:
                changeText = i18next.t("screens.kredi_flow.history.invoice_status_changed");
                break;
            case HISTORY_TYPE.INVOICE_REJECTED:
                changeText = i18next.t("screens.kredi_flow.history.invoice_rejected");
                break;
            case HISTORY_TYPE.INVOICE_CANCELLED:
                changeText = i18next.t("screens.kredi_flow.history.invoice_canceled");
                break;
            case HISTORY_TYPE.INVOICE_DATA_CHANGED:
            case HISTORY_TYPE.INVOICE_AMOUNT_CHANGED:
            case HISTORY_TYPE.INVOICE_PAIDDATE_CHANGED:
            case HISTORY_TYPE.INVOICE_DUEDATE_CHANGED:
            case HISTORY_TYPE.INVOICE_CREDITOR_CHANGED:
            case HISTORY_TYPE.INVOICE_PAYMENTACCOUNT_CHANGED:
            case HISTORY_TYPE.INVOICE_PAYOUTACCOUNT_CHANGED:
            case HISTORY_TYPE.INVOICE_ORDER_CHANGED:
            case HISTORY_TYPE.INVOICE_UNIT_CHANGED:
            case HISTORY_TYPE.INVOICE_REALESTATE_CHANGED:
                changeText = i18next.t("screens.kredi_flow.history.invoice_updated");
                break;
            case HISTORY_TYPE.INVOICE_NEW_WORKFLOWUSER_1:
                changeText = i18next.t("screens.kredi_flow.history.new_workflow_user_visum_1");
                break;
            case HISTORY_TYPE.INVOICE_NEW_WORKFLOWUSER_2:
                changeText = i18next.t("screens.kredi_flow.history.new_workflow_user_visum_2");
                break;
            case HISTORY_TYPE.INVOICE_NEW_WORKFLOWUSER_3:
                changeText = i18next.t("screens.kredi_flow.history.new_workflow_user_visum_3");
                break;
            case HISTORY_TYPE.INVOICE_NEW_WORKFLOWUSER_4:
                changeText = i18next.t("screens.kredi_flow.history.new_workflow_user_visum_4");
                break;
            case HISTORY_TYPE.INVOICE_NEW_DOCUMENT:
                changeText = i18next.t("screens.kredi_flow.history.new_document");
                break;
            case HISTORY_TYPE.INVOICE_NEW_ACCOUNTING:
                changeText = i18next.t("screens.kredi_flow.history.new_accounting");
                break;
            case HISTORY_TYPE.INVOICE_CHANGED_WORKFLOWUSERS:
                changeText = i18next.t("screens.kredi_flow.history.invoice_change_workflow_user");
                break;
            case HISTORY_TYPE.INVOICE_CHANGED_WORKFLOWUSERS_ROLE:
                changeText = i18next.t("screens.kredi_flow.history.invoice_change_workflow_user_role");
                break;
            case HISTORY_TYPE.INVOICE_CHANGED_WORKFLOWUSERS_SIGNATURE_1:
                changeText = i18next.t("screens.kredi_flow.history.signature_1");
                break;
            case HISTORY_TYPE.INVOICE_CHANGED_WORKFLOWUSERS_SIGNATURE_2:
                changeText = i18next.t("screens.kredi_flow.history.signature_2");
                break;
            case HISTORY_TYPE.INVOICE_CHANGED_WORKFLOWUSERS_SIGNATURE_3:
                changeText = i18next.t("screens.kredi_flow.history.signature_3");
                break;
            case HISTORY_TYPE.INVOICE_CHANGED_ACCOUNTING:
                changeText = i18next.t("screens.kredi_flow.history.accounting_changed");
                break;
            case HISTORY_TYPE.INVOICE_COMMENT:
                changeText = history.comment_text ?? "";
                break;
            case HISTORY_TYPE.INVOICE_DELETED_DOCUMENT:
                changeText = i18next.t("screens.kredi_flow.history.deleted_document");
                break;
        }
        return changeText;
    };

    getHistoryObjectFromQueryData = (historyQueryData: GetInvoiceByNumber_cred_invoices_history[]): IHistory[] => {
        const historyObjectArray: IHistory[] = historyQueryData.map((history) => {
            const historyType = HISTORY_TYPE[history.type as keyof typeof HISTORY_TYPE];

            const isHistoryTypeComment = historyType === HISTORY_TYPE.INVOICE_COMMENT;
            const isCommentPinned = isHistoryTypeComment && history.comment_is_pinned ? true : false;

            const isHistoryTypeEmailOutbound = historyType === HISTORY_TYPE.INVOICE_EMAIL_OUTBOUND;

            const isHistoryTypeEmailInbound = historyType === HISTORY_TYPE.INVOICE_EMAIL_INBOUND;

            const isHistoryTypeEmail = isHistoryTypeEmailOutbound || isHistoryTypeEmailInbound;

            let changeObjectArray: IHistoryChangeEntry[] = [];

            if (history.new_values !== undefined && history.new_values !== null) {
                changeObjectArray = this.getHistoryChangeEntries(history);
            }

            const changeText = this.getHistoryChangeText(historyType, history);

            let emailDetails: IHistoryEmail | undefined = undefined;

            if (isHistoryTypeEmail) {
                const attachments: IHistoryEmailAttachment[] = [];

                if (isHistoryTypeEmailInbound && history.inboundfiles && history.inboundfiles.length > 0) {
                    history.inboundfiles.forEach((inboundfile) => {
                        const fileName = inboundfile.name ?? "";
                        const fileId = inboundfile.id ?? "";

                        attachments.push({
                            fileName: fileName,
                            fileId: fileId
                        });
                    });
                }

                const sendingErrors: IHistoryEmailLog[] = [];

                if (history.failed_outbound_email_recipients && history.failed_outbound_email_recipients.length > 0) {
                    history.failed_outbound_email_recipients.forEach((recipient) => {
                        // The query only prefilters candidates (recipients with at least one hard failure
                        // event). Whether the delivery actually failed is decided by the latest
                        // outcome-relevant event, so e.g. "processed" cannot mask a bounce (#22919).
                        const emailLog = getFailedDeliveryEmailLog(recipient.emaillogs);

                        if (emailLog) {
                            sendingErrors.push({
                                recipient: recipient.emailaddress ?? "",
                                emailLogId: emailLog.id ?? "",
                                event: emailLog.event ?? "",
                                message: emailLog.message ?? ""
                            });
                        }
                    });
                }

                emailDetails = {
                    body: history.email_body ?? undefined,
                    subject: history.email_subject ?? undefined,
                    sender: history.email_sender ?? undefined,
                    recipients: history.email_recipients ?? undefined,
                    type: history.email_type ?? undefined,
                    attachments: attachments.length > 0 ? attachments : undefined,
                    sendingErrors: sendingErrors.length > 0 ? sendingErrors : undefined
                };
            }

            let userName = isHistoryTypeComment
                ? (history.comment_username ?? undefined)
                : (history.history_username ?? undefined);

            // Machine writers as the history trigger records them (cred.history."user"):
            // "WorkflowService" is what Micro.KrediFlow stamps, "WorkflowUser" the file
            // insert preset, "WWWorkflowClient" the workflow client; "WorkFlowService"
            // (capital F) never matched the real value and is kept only for old rows.
            // "KrediFlowAI" is the AI apply path (#24471).
            const MACHINE_WRITER_LABELS: Record<string, string> = {
                WorkflowService: "Workflow",
                WorkFlowService: "Workflow",
                WWWorkflowClient: "Workflow",
                WorkflowUser: "Workflow",
                KrediFlowAI: "KI"
            };
            userName = isHistoryTypeEmail ? "Workflow" : ((userName && MACHINE_WRITER_LABELS[userName]) ?? userName);

            const adjustTimezoneUtcToZH = !isHistoryTypeEmailOutbound && !isHistoryTypeComment ? true : false;
            const historyObject: IHistory = {
                type: HISTORY_TYPE[history.type as keyof typeof HISTORY_TYPE],
                date: formatDateHistoryEntry(history.inserted, adjustTimezoneUtcToZH),
                timestamp: getTimeStampForHistoryEntry(history.inserted, adjustTimezoneUtcToZH),
                changeText: changeText,
                user: userName,
                changeEntry: changeObjectArray,
                email: emailDetails ?? undefined,
                entityid: history.entityid,
                isPinned: isCommentPinned
            };

            return historyObject;
        });

        historyObjectArray.sort((a, b) => {
            const bTime = b.timestamp?.getTime() ?? 0;
            const aTime = a.timestamp?.getTime() ?? 0;
            return bTime - aTime;
        });

        return historyObjectArray;
    };

    updateInvoiceAndFormWithSelectedRealestate = async (
        selectedRealestate: IRealestate,
        setTriggerFocusOnUnitInput: boolean = true
    ) => {
        const realestateSearchStore = this.rootStore.realestateSearchStore;

        const newRealestateFieldValue = realestateSearchStore.getFormattedRealestateFieldValue(selectedRealestate);

        realestateSearchStore.setCurrentRealestateSearchQuery(newRealestateFieldValue);
        realestateSearchStore.setSelectedRealestateQueryString(newRealestateFieldValue);
        realestateSearchStore.setSelectedRealestateName(selectedRealestate.name);

        this.loadUnitData(selectedRealestate.id);

        if (this.currentInvoice) {
            this.currentInvoice.updateProperty(INVOICE_PROPERTY.REALESTATE, selectedRealestate);
            await this.loadRealestateThumbnailFile(selectedRealestate.id);

            if (this.currentInvoice.isUnitSelected) {
                this.resetUnitFormField();
            }

            if (
                this.currentInvoice.isTicketSelected &&
                this.currentInvoice.ticket?.realestateid !== selectedRealestate.id
            ) {
                this.resetTicketFormField();
            }

            await this.currentInvoice.loadNumberOfVisas();
        }

        this.rootStore.unitSearchStore.setTriggerFocusOnUnitInput(setTriggerFocusOnUnitInput);
    };

    updateAccountingWithSelectedRealestate = async (
        selectedRealestate: IRealestate,
        triggerFocusNextAccountingAccountInput: boolean,
        accountingRowIndex: number,
        runByInit: boolean = false
    ) => {
        const accountingRealestateSearchStore = this.rootStore.accountingRealestateSearchStore;

        const newRealestateFieldValue =
            accountingRealestateSearchStore.getFormattedRealestateFieldValue(selectedRealestate);

        accountingRealestateSearchStore.setCurrentRealestateSearchQuery(newRealestateFieldValue, accountingRowIndex);
        accountingRealestateSearchStore.setSelectedRealestateQueryString(newRealestateFieldValue, accountingRowIndex);
        accountingRealestateSearchStore.setSelectedRealestateName(selectedRealestate.name, accountingRowIndex);

        if (this.currentInvoice) {
            this.currentInvoice.setAccountingRealestate(selectedRealestate, accountingRowIndex);
        }

        const erpType = this.rootStore.authStore.user?.erpType;

        // Resets must run synchronously after the realestate is set, BEFORE any awaits.
        // Otherwise a failed/slow GraphQL call below can leave the row with a new realestate
        // and the previous (now-stale) account still selected.
        if (!runByInit) {
            // RIMO: If the realastate is changed and there is already an account selected, then the account will be reset
            // IT2: If the realastate is changed and there is already an account selected, then the user will be asked if he wants to reset all accounting entries (RealestateChangedWarningModal
            if (
                erpType === ErpType.RIMO &&
                accountingRowIndex !== undefined &&
                this.currentInvoice?.isAccountingAccountSelected(accountingRowIndex)
            ) {
                this.resetAccountingAccountFormFields(accountingRowIndex);
            }

            // If the realastate is changed and there is already an unit selected, then the unit field will be reset
            this.resetAccountingUnitFormFields(accountingRowIndex);
        }

        try {
            await this.rootStore.accountingUnitSearchStore.loadUnitData(selectedRealestate.id);
        } catch (error) {
            Sentry.captureException(error);
        }

        this.currentInvoice?.validateAccountingsOnFieldLevel();

        // Tag suggestions are edit-mode UI. During initialisation of saved accountings they are not
        // needed (and for IT2 each call re-loads ALL rows, so per-row init calls were O(n²)); they
        // are loaded once when editing starts instead.
        if (!runByInit) {
            try {
                await this.currentInvoice?.loadAccountTagsAfterRealestateChange(accountingRowIndex, erpType);
            } catch (error) {
                Sentry.captureException(error);
            }

            try {
                await this.currentInvoice?.loadAccountingHistoryTagsAfterRealestateChange(accountingRowIndex, erpType);
            } catch (error) {
                Sentry.captureException(error);
            }
        }

        if (triggerFocusNextAccountingAccountInput) {
            if (erpType === ErpType.IT2) {
                accountingRealestateSearchStore.setTriggerFocusNextAccountingAccountInput(true);
            } else if (erpType === ErpType.RIMO) {
                accountingRealestateSearchStore.setTriggerFocusNextAccountingAccountInput(true, accountingRowIndex);
            }
        }
    };

    updateAccountingWithSelectedUnit = async (
        selectedUnit: IUnitData | undefined,
        triggerFocusNextAccountingInput: boolean,
        accountingRowIndex: number
    ) => {
        const accountingUnitSearchStore = this.rootStore.accountingUnitSearchStore;

        const newUnitFieldValue = selectedUnit
            ? accountingUnitSearchStore.getFormattedUnitFieldValue(selectedUnit)
            : "";

        accountingUnitSearchStore.setCurrentUnitSearchQuery(newUnitFieldValue, accountingRowIndex);
        accountingUnitSearchStore.setSelectedUnitQueryString(newUnitFieldValue, accountingRowIndex);
        accountingUnitSearchStore.setSelectedUnitName(selectedUnit?.unitName ?? "", accountingRowIndex);

        if (this.currentInvoice) {
            this.currentInvoice.setAccountingUnit(selectedUnit, accountingRowIndex);
        }

        this.currentInvoice?.validateAccountingsOnFieldLevel();

        if (triggerFocusNextAccountingInput) {
            accountingUnitSearchStore.setTriggerFocusNextAccountingExtraCostDateInput(true, accountingRowIndex);
        }
    };

    updateAccountingWithSelectedAccount = async (
        selectedAccount: IAccount,
        triggerFocusNextInput: boolean,
        accountingRowIndex: number,
        runByInit: boolean = false
    ) => {
        const accountingAccountSearchStore = this.rootStore.accountingAccountSearchStore;

        const newAccountFieldValue = accountingAccountSearchStore.getFormattedAccountFieldValue(selectedAccount);

        accountingAccountSearchStore.setCurrentAccountSearchQuery(newAccountFieldValue, accountingRowIndex);
        accountingAccountSearchStore.setSelectedAccountQueryString(newAccountFieldValue, accountingRowIndex);
        accountingAccountSearchStore.setSelectedAccountName(selectedAccount.name ?? "", accountingRowIndex);

        if (this.currentInvoice) {
            this.currentInvoice.setAccountingAccount(selectedAccount, accountingRowIndex);

            if (!runByInit) {
                // An account change only affects the booking-text suggestions of THIS row. This used
                // to reload every row's tags on every call - during init that was O(n²) (#23888).
                this.currentInvoice.loadAccountingHistoryTagsAfterAccountChange(accountingRowIndex);

                this.currentInvoice.setAccountingCostcenter(undefined, accountingRowIndex);
                this.currentInvoice.setAccountingQuantity(undefined, accountingRowIndex);
                this.currentInvoice.setAccountingExtraCostDate(undefined, accountingRowIndex);
            }
        }

        const accountingCostcenterSearchStore = this.rootStore.accountingCostcenterStore;
        const loadedCostcenterData = await accountingCostcenterSearchStore.loadCostcenterData(accountingRowIndex);

        this.currentInvoice?.validateAccountingsOnFieldLevel();

        if (loadedCostcenterData && loadedCostcenterData.length === 1) {
            this.updateAccountingWithSelectedCostcenter(
                loadedCostcenterData[0],
                runByInit ? false : true,
                accountingRowIndex
            );
        }

        if ((!loadedCostcenterData || loadedCostcenterData.length === 0) && selectedAccount.requiresCostCenter) {
            this.currentInvoice?.setIsMissingRequiredCostCenterData(true, accountingRowIndex);
        } else {
            this.currentInvoice?.setIsMissingRequiredCostCenterData(false, accountingRowIndex);
        }

        if (triggerFocusNextInput) {
            accountingAccountSearchStore.setTriggerFocusNextInput(true, accountingRowIndex);
        }

        const isExtraCostDateRequired = selectedAccount.extraCostDate;

        if (isExtraCostDateRequired) {
            const extraCostDateAlreadySet = this.currentInvoice?.getAccountingExtraCostDate(accountingRowIndex);
            if (!extraCostDateAlreadySet) {
                this.currentInvoice?.setAccountingExtraCostDate(this.currentInvoice.accountingDate, accountingRowIndex);
            }
        }

        if (!runByInit) {
            this.resetAccountingVatCodeFormFields(accountingRowIndex);

            if (selectedAccount.vatConfig !== INVOICE_ACCOUNT_VAT_CONFIG.WITHOUT_VAT) {
                await this.rootStore.accountingVatStore.loadVatData(accountingRowIndex);

                const defaultVatCodeId = selectedAccount.defaultVatCodeId;

                if (defaultVatCodeId) {
                    const vatData = this.rootStore.accountingVatStore.vatData.find(
                        (vatData) => vatData.codeId === defaultVatCodeId
                    );

                    if (vatData) {
                        this.updateAccountingWithSelectedVatCode(vatData, accountingRowIndex);
                    }
                }
            }
        }
    };

    updateAccountingWithBookingDate = async (bookingDate: string) => {
        if (this.currentInvoice) {
            this.currentInvoice.setAccountingDate(bookingDate);
            this.currentInvoice.validateIfAccountsAreValidForBookingDate();

            await this.currentInvoice.loadAccountTagsAfterBookingDateChange();
        }

        this.currentInvoice?.validateAccountingsOnFieldLevel();
    };

    updateAccountingWithBookingText = async (bookingText: string, accountingRowIndex: number) => {
        if (this.currentInvoice) {
            this.currentInvoice.setAccountingBookingText(bookingText, accountingRowIndex);
        }

        this.currentInvoice?.validateAccountingsOnFieldLevel();
    };

    updateAccountingWithQuantity = (quantity: string, accountingRowIndex: number) => {
        if (this.currentInvoice) {
            this.currentInvoice.setAccountingQuantity(quantity, accountingRowIndex);
        }

        this.currentInvoice?.validateAccountingsOnFieldLevel();
    };

    updateAccountingWithExtraCostDate = (extraCostDate: string, accountingRowIndex: number) => {
        if (this.currentInvoice) {
            this.currentInvoice.setAccountingExtraCostDate(extraCostDate, accountingRowIndex);
        }

        this.currentInvoice?.validateAccountingsOnFieldLevel();
    };

    updateAccountingWithSelectedCostcenter = async (
        selectedCostcenter: ICostCenter,
        triggerFocusNextInput: boolean,
        accountingRowIndex: number
    ) => {
        const accountingCostcenterSearchStore = this.rootStore.accountingCostcenterStore;

        const newAccountFieldValue =
            accountingCostcenterSearchStore.getFormattedCostcenterFieldValue(selectedCostcenter);

        accountingCostcenterSearchStore.setCurrentCostcenterSearchQuery(newAccountFieldValue, accountingRowIndex);
        accountingCostcenterSearchStore.setSelectedCostcenterQueryString(newAccountFieldValue, accountingRowIndex);
        accountingCostcenterSearchStore.setSelectedCostcenterName(selectedCostcenter.name ?? "", accountingRowIndex);

        if (this.currentInvoice) {
            this.currentInvoice.setAccountingCostcenter(selectedCostcenter, accountingRowIndex);
        }

        this.currentInvoice?.validateAccountingsOnFieldLevel();

        if (triggerFocusNextInput) {
            accountingCostcenterSearchStore.setTriggerFocusNextInput(true, accountingRowIndex);
        }
    };

    updateAccountingWithSelectedVatCode = async (
        vatData: IVatData,
        accountingRowIndex: number,
        runByInit: boolean = false
    ) => {
        const accountingVatStore = this.rootStore.accountingVatStore;

        accountingVatStore.setCurrentVatCodeSearchQuery(vatData.codeShort ?? "", accountingRowIndex);
        accountingVatStore.setSelectedVatCodeQueryString(vatData.codeShort ?? "", accountingRowIndex);
        accountingVatStore.setSelectedVatCodeVatRateType(
            vatData.vatRateType ?? VAT_RATE_TYPE.UNDEFINED,
            accountingRowIndex
        );

        if (this.currentInvoice && vatData.codeId && !runByInit) {
            this.currentInvoice.setAccountingVatCode(vatData.codeId, accountingRowIndex);

            // A code without an input-tax share has no deviating share either (#24071).
            if (!vatData.proportionally) {
                this.currentInvoice.setAccountingVatProportion(undefined, accountingRowIndex);
            }

            const currentlyValidVatRateDataByVatCodeId = accountingVatStore.getCurrentlyValidVatRateDataByVatCodeId(
                vatData.codeId
            );

            if (currentlyValidVatRateDataByVatCodeId) {
                this.updateAccountingWithSelectedVatRate(currentlyValidVatRateDataByVatCodeId, accountingRowIndex);
            }
        }
    };

    updateAccountingWithSelectedVatRate = async (
        vatData: IVatData,
        accountingRowIndex: number,
        runByInit: boolean = false
    ) => {
        const accountingVatStore = this.rootStore.accountingVatStore;

        accountingVatStore.setCurrentVatRateSearchQuery(vatData.rate ?? "", accountingRowIndex);
        accountingVatStore.setSelectedVatRateQueryString(vatData.rate ?? "", accountingRowIndex);

        if (this.currentInvoice && vatData.rate && !runByInit) {
            this.currentInvoice.setAccountingVatRate(vatData.rate, accountingRowIndex);

            const amount = this.currentInvoice.getAccountingAmount(accountingRowIndex);

            if (amount) {
                const netAmount = calculateNetAmountFromGrossAmount(Number(amount), Number(vatData.rate));
                this.currentInvoice.setAccountingNetAmount(netAmount, accountingRowIndex);
            }
        }
    };

    updateAccountingWithAmount = (amount: string, accountingRowIndex: number) => {
        if (this.currentInvoice) {
            this.currentInvoice.setAccountingAmount(amount, accountingRowIndex);

            if (this.currentInvoice.canSeeAccountingNetAmountField(accountingRowIndex)) {
                const selectedVatRate = Number(this.currentInvoice.getAccountingVatRate(accountingRowIndex));
                const netAmount = calculateNetAmountFromGrossAmount(Number(amount), selectedVatRate);
                this.currentInvoice.setAccountingNetAmount(netAmount, accountingRowIndex);
            }
        }
    };

    updateAccountingWithNetAmount = (
        vatAmount: string,
        accountingRowIndex: number,
        updateAccountingAmount: boolean = true
    ) => {
        if (this.currentInvoice) {
            this.currentInvoice.setAccountingNetAmount(vatAmount, accountingRowIndex);

            if (updateAccountingAmount) {
                const selectedVatRate = Number(this.currentInvoice.getAccountingVatRate(accountingRowIndex));
                const amount = calculateGrossAmountFromNetAmount(Number(vatAmount), selectedVatRate);
                this.currentInvoice.setAccountingAmount(amount, accountingRowIndex);
            }
        }
    };

    updateAccountingWithVatAmount = (vatAmount: string, accountingRowIndex: number) => {
        if (this.currentInvoice) {
            this.currentInvoice.setAccountingVatAmount(vatAmount, accountingRowIndex);
        }
    };

    updateAccountingWithVatProportion = (vatProportion: string, accountingRowIndex: number) => {
        if (this.currentInvoice) {
            this.currentInvoice.setAccountingVatProportion(
                vatProportion === "" ? undefined : vatProportion,
                accountingRowIndex
            );
        }

        this.currentInvoice?.validateAccountingsOnFieldLevel();
    };

    updateInvoiceWithSelectedPayoutAccountIT2 = async (
        payoutAccountData?: IPayoutAccountData,
        runByInit: boolean = false
    ) => {
        const erpType = this.rootStore.authStore.user?.erpType;
        const payoutAccountStore = this.rootStore.accountingPayoutAccountStore;

        if (this.currentInvoice && !runByInit) {
            this.currentInvoice.updateProperty(INVOICE_PROPERTY.PAYOUT_ACCOUNT_ID, payoutAccountData?.id ?? undefined);
            this.currentInvoice.setManualSaveOfInvoiceIsNeeded(true);
        }

        if (erpType === ErpType.IT2) {
            payoutAccountStore.setCurrentPayoutAccountSearchQueryIT2(payoutAccountData?.iban ?? "");
            payoutAccountStore.setSelectedPayoutAccountQueryStringIT2(payoutAccountData?.iban ?? "");
        }
    };

    resetAccountingAccountFormFields = (accountingRowIndex: number) => {
        const accountingAccountSearchStore = this.rootStore.accountingAccountSearchStore;

        accountingAccountSearchStore.setCurrentAccountSearchQuery("", accountingRowIndex);
        accountingAccountSearchStore.setSelectedAccountQueryString("", accountingRowIndex);
        accountingAccountSearchStore.setSelectedAccountName("", accountingRowIndex);

        if (this.currentInvoice) {
            this.currentInvoice.setAccountingAccount(undefined, accountingRowIndex);
            this.currentInvoice.loadAccountingHistoryTagsAfterAccountChange(accountingRowIndex);
        }
    };

    resetAccountingUnitFormFields = (accountingRowIndex: number) => {
        const accountingUnitSearchStore = this.rootStore.accountingUnitSearchStore;

        accountingUnitSearchStore.setCurrentUnitSearchQuery("", accountingRowIndex);
        accountingUnitSearchStore.setSelectedUnitQueryString("", accountingRowIndex);
        accountingUnitSearchStore.setSelectedUnitName("", accountingRowIndex);

        if (this.currentInvoice) {
            this.currentInvoice.setAccountingUnit(undefined, accountingRowIndex);
            this.currentInvoice.loadAccountingHistoryTagsAfterAccountChange(accountingRowIndex);
        }
    };

    resetPayoutAccountFormFields = () => {
        const accountingPayoutAccountStore = this.rootStore.accountingPayoutAccountStore;
        accountingPayoutAccountStore.reset();

        if (this.currentInvoice) {
            this.updateInvoiceWithSelectedPayoutAccountIT2(undefined);
        }
    };

    resetAccountingCostcenterFormFields = (accountingRowIndex: number) => {
        const accountingCostcenterSearchStore = this.rootStore.accountingCostcenterStore;

        accountingCostcenterSearchStore.setCurrentCostcenterSearchQuery("", accountingRowIndex);
        accountingCostcenterSearchStore.setSelectedCostcenterQueryString("", accountingRowIndex);
        accountingCostcenterSearchStore.setSelectedCostcenterName("", accountingRowIndex);

        if (this.currentInvoice) {
            this.currentInvoice.setAccountingCostcenter(undefined, accountingRowIndex);
        }
    };

    resetAccountingVatCodeFormFields = (accountingRowIndex: number) => {
        const accountingVatStore = this.rootStore.accountingVatStore;

        accountingVatStore.setCurrentVatCodeSearchQuery("", accountingRowIndex);
        accountingVatStore.setSelectedVatCodeQueryString("", accountingRowIndex);

        if (this.currentInvoice) {
            this.currentInvoice.setAccountingVatCode(undefined, accountingRowIndex);
            this.currentInvoice.setAccountingVatProportion(undefined, accountingRowIndex);
        }
    };

    resetAccountingVatRateFormFields = (accountingRowIndex: number) => {
        const accountingVatStore = this.rootStore.accountingVatStore;

        accountingVatStore.setCurrentVatRateSearchQuery("", accountingRowIndex);
        accountingVatStore.setSelectedVatRateQueryString("", accountingRowIndex);

        if (this.currentInvoice) {
            this.currentInvoice.setAccountingVatRate(undefined, accountingRowIndex);
        }
    };

    updateInvoiceAndFormWithSelectedUnit = async (selectedUnit: IUnitData) => {
        const unitSearchStore = this.rootStore.unitSearchStore;

        const newUnitFieldValue = unitSearchStore.getFormattedUnitName(selectedUnit);

        unitSearchStore.setCurrentUnitSearchQuery(newUnitFieldValue);
        unitSearchStore.setSelectedUnitQueryString(newUnitFieldValue);

        if (this.currentInvoice) {
            this.currentInvoice.updateProperty(INVOICE_PROPERTY.UNIT, selectedUnit);
        }
    };

    updateInvoiceAndFormWithSelectedTicket = async (selectedTicket: ITicket, updateOrderField: boolean = true) => {
        const newTicketFieldValue = this.rootStore.ticketSearchStore.getFormattedTicketFieldValue(selectedTicket);

        this.rootStore.ticketSearchStore.setCurrentTicketSearchQuery(newTicketFieldValue);
        this.rootStore.ticketSearchStore.setSelectedTicketQueryString(newTicketFieldValue);

        this.rootStore.orderSearchStore.setOrderSearchFilterTicket(true);

        const wasTicketChanged = this.currentInvoice?.ticket?.id !== selectedTicket.id;

        if (this.currentInvoice) {
            this.currentInvoice.updateProperty(INVOICE_PROPERTY.TICKET, selectedTicket);

            // Set the realestate in the form to the realestate of the selected ticket
            if (selectedTicket.realestateid) {
                const wasRealestateChanged = selectedTicket.realestateid !== this.currentInvoice.realestate?.id;

                if (wasRealestateChanged) {
                    const realestate = this.rootStore.realestateSearchStore.realestateData.find(
                        (realestate) => realestate.id === selectedTicket.realestateid
                    );

                    if (realestate) {
                        await this.updateInvoiceAndFormWithSelectedRealestate(realestate, false);
                    }
                }

                // Set the unit in the form to the unit of the selected ticket
                if (
                    selectedTicket.realestateid &&
                    selectedTicket.unitid &&
                    selectedTicket.unitid !== this.currentInvoice.unit?.unitId
                ) {
                    if (wasRealestateChanged) {
                        await this.rootStore.unitSearchStore.loadUnitData(selectedTicket.realestateid);
                    }

                    const unit = this.rootStore.unitSearchStore.unitData.find(
                        (unit) => unit.unitId === selectedTicket.unitid
                    );

                    if (unit) {
                        this.updateInvoiceAndFormWithSelectedUnit(unit);
                    }
                } else if (!selectedTicket.unitid) {
                    this.resetUnitFormField();
                }
            } else {
                this.resetRealestateFormField();
            }

            if (this.currentInvoice.order && wasTicketChanged && updateOrderField) {
                this.resetOrderFormField();
            }
        }
    };

    updateInvoiceAndFormWithSelectedOrder = async (selectedOrder: IOrder) => {
        const newOrderFieldValue = this.rootStore.orderSearchStore.getFormattedOrderFieldValue(selectedOrder);

        this.rootStore.orderSearchStore.setCurrentOrderSearchQuery(newOrderFieldValue);
        this.rootStore.orderSearchStore.setSelectedOrderQueryString(newOrderFieldValue);

        if (this.currentInvoice) {
            this.currentInvoice.updateProperty(INVOICE_PROPERTY.ORDER, selectedOrder);

            if (selectedOrder.ticket) {
                this.updateInvoiceAndFormWithSelectedTicket(selectedOrder.ticket, false);
            }
        }
    };

    updateInvoiceStoreWithSelectedCreditor = async (creditor: ICreditor | undefined) => {
        this.rootStore.creditorSearchStore.setCurrentCreditorSearchQuery("");

        if (this.currentInvoice) {
            this.resetCreditorFormField();
            this.currentInvoice.updateProperty(INVOICE_PROPERTY.CREDITOR, creditor);

            // Reset Payment Account (Not manually entered IBAN)
            this.currentInvoice?.updateProperty(INVOICE_PROPERTY.PAYMENT_ACCOUNT, undefined);
            this.currentInvoice?.updateProperty(INVOICE_PROPERTY.PAYMENT_ACCOUNT_ID, undefined);
            this.rootStore.paymentAccountSearchStore.resetPaymentAccountFields();

            if (this.currentInvoice?.paymentInfo && !this.currentInvoice.paymentInfo.iban) {
                this.rootStore.paymentAccountSearchStore.currentSelectedIBAN = "";
            }

            if (creditor?.id && this.currentInvoice.invoiceNumber && this.currentInvoice.duplicates?.length === 0) {
                await this.currentInvoice.getDuplicatesAndDisplayModal();
            }
        }

        if (creditor) {
            this.rootStore.paymentAccountSearchStore.setPaymentAccountData(creditor.paymentAccounts ?? []);
            await this.currentInvoice?.loadAccountTagsAfterCreditorChange();
            await this.currentInvoice?.loadAccountingHistoryTagsAfterCreditorChange();
        }
    };

    setRealestateFormField = async (invoice: Invoice) => {
        const realestate = invoice.realestate;

        if (realestate) {
            await this.loadUnitData(realestate.id);

            const realestateSearchStore = this.rootStore.realestateSearchStore;

            await this.loadRealestateThumbnailFile(realestate.id);

            const newRealestateFieldValue = realestateSearchStore.getFormattedRealestateFieldValue(realestate);
            realestateSearchStore.setCurrentRealestateSearchQuery(newRealestateFieldValue);

            realestateSearchStore.setSelectedRealestateQueryString(newRealestateFieldValue);
            realestateSearchStore.setSelectedRealestateName(realestate.name);

            // setting loading payoutaccount data if not set in invoice
            if (!invoice.payoutaccountid) {
                const defaultAccountingRealestate =
                    await this.currentInvoice?.getDefaultAccountingRealestateByRealestate(realestate);

                if (defaultAccountingRealestate) {
                    await this.currentInvoice?.loadPayoutAccountDataAndSetIfNeeded(defaultAccountingRealestate.id);
                }
            }
        }
    };

    setPayoutAccountFormField = async (invoice: Invoice) => {
        const realestate = invoice.realestate;

        if (realestate) {
            const defaultAccountingRealestate =
                await this.currentInvoice?.getDefaultAccountingRealestateByRealestate(realestate);

            if (defaultAccountingRealestate) {
                const accountingPayoutAccountStore = this.rootStore.accountingPayoutAccountStore;
                const loadedPayoutAccountData = await accountingPayoutAccountStore.loadPayoutAccountData(
                    defaultAccountingRealestate.id
                );

                if (loadedPayoutAccountData) {
                    const payoutAccountId = invoice.payoutaccountid;

                    // If payout account is set in invoice (that was loaded), then we try to set it
                    // If not set, we try to set a default payout account
                    if (payoutAccountId) {
                        const payoutAccount = loadedPayoutAccountData.find(
                            (payoutAccount) => payoutAccount.id === payoutAccountId
                        );

                        if (payoutAccount) {
                            await this.updateInvoiceWithSelectedPayoutAccountIT2(payoutAccount, true);
                        }
                    } else if (loadedPayoutAccountData.length > 1) {
                        const defaultPayoutAccount = loadedPayoutAccountData.find(
                            (payoutAccount) => payoutAccount.default === true
                        );

                        if (defaultPayoutAccount) {
                            this.rootStore.invoiceStore.updateInvoiceWithSelectedPayoutAccountIT2(defaultPayoutAccount);
                        }
                    }
                }
            }
        }
    };

    setUnitFormField = async (invoice: Invoice) => {
        const unit = invoice.unit;

        if (unit) {
            const unitSearchStore = this.rootStore.unitSearchStore;

            const newUnitFieldValue = unitSearchStore.getFormattedUnitName(unit);

            unitSearchStore.setCurrentUnitSearchQuery(newUnitFieldValue);
            unitSearchStore.setSelectedUnitQueryString(newUnitFieldValue);
        }
    };

    setTicketFormField = async (invoice: Invoice) => {
        const ticketSearchStore = this.rootStore.ticketSearchStore;

        const ticket = invoice.ticket;

        if (ticket) {
            const newTicketFieldValue = ticketSearchStore.getFormattedTicketFieldValue(ticket);

            ticketSearchStore.setCurrentTicketSearchQuery(newTicketFieldValue);
            ticketSearchStore.setSelectedTicketQueryString(newTicketFieldValue);
        }

        if (invoice.realestate) {
            ticketSearchStore.setTicketSearchFilterRealestate(true);
        }
    };

    setOrderFormField = async (invoice: Invoice) => {
        const orderSearchStore = this.rootStore.orderSearchStore;

        const order = invoice.order;

        if (order) {
            const newOrderFieldValue = orderSearchStore.getFormattedOrderFieldValue(order);
            orderSearchStore.setCurrentOrderSearchQuery(newOrderFieldValue);
            orderSearchStore.setSelectedOrderQueryString(newOrderFieldValue);
        }

        if (invoice.isCreditorSelected) {
            orderSearchStore.setOrderSearchFilterCreditor(true);
            orderSearchStore.setOrderSearchFilterRealestate(false);
            orderSearchStore.setOrderSearchFilterTicket(false);
        }

        if (invoice.isRealestateSelected) {
            orderSearchStore.setOrderSearchFilterRealestate(true);
            orderSearchStore.setOrderSearchFilterCreditor(false);
            orderSearchStore.setOrderSearchFilterTicket(false);
        }

        if (invoice.isTicketSelected) {
            orderSearchStore.setOrderSearchFilterTicket(true);
            orderSearchStore.setOrderSearchFilterCreditor(false);
            orderSearchStore.setOrderSearchFilterRealestate(false);
        }
    };

    loadRealestateThumbnailFile = async (realestateId: string) => {
        const realestateThumbnailFileId =
            await this.rootStore.realestateSearchStore.getRealestateThumbnailFileId(realestateId);

        this.currentInvoice?.updateProperty(INVOICE_PROPERTY.REALESTATE_THUMBNAIL_FILEID, realestateThumbnailFileId);

        return realestateThumbnailFileId;
    };

    extractQrCodeDataFromPDF = async (pdfBase64String: string): Promise<IQRExtractInvoiceData | undefined> => {
        let qrExtractInvoiceData: IQRExtractInvoiceData | undefined = undefined;

        const requestBody = {
            invoicepdf: pdfBase64String
        };

        try {
            const response = await Axios.getAuthInstance().post(NetworkConfig.extractDataSinglePdfUrl, requestBody, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });

            const result = response.data;
            const qrExtractInvoiceDataResponse: IQRExtractInvoiceData[] = result.data;

            if (qrExtractInvoiceDataResponse.length > 0) {
                // Display info that more than 1 qr was detected and that information from first qr code was taken
                if (qrExtractInvoiceDataResponse.length > 1) {
                    this.rootStore.uiStore.printStatusMessage(
                        `${i18next.t("screens.kredi_flow.mapping.more_than_one_invoice_found_info")}`,
                        MessageType.INFO
                    );
                }

                qrExtractInvoiceData = qrExtractInvoiceDataResponse[0];
            }
        } catch (error) {
            console.error("Error extracting data from the pdf file: ", error);
        }

        return qrExtractInvoiceData;
    };

    aiDetectWithFile = async (
        base64String: string,
        file: File,
        signal: AbortSignal
    ): Promise<IAiDetectWithFileResponse["data"] | undefined> => {
        const extension = (file.name.split(".").pop() ?? "pdf").toLowerCase();
        const requestBody = {
            file: base64String,
            filename: file.name,
            extension,
            mimetype: file.type || "application/pdf"
        };

        try {
            const response = await Axios.getAuthInstance().post<IAiDetectWithFileResponse>(
                NetworkConfig.aiDetectWithFileUrl,
                requestBody,
                {
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    },
                    signal,
                    timeout: 130_000
                }
            );

            return response.data?.data;
        } catch (error: any) {
            if (error?.name === "AbortError" || error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
                return undefined;
            }
            console.error("Error in AI detection: ", error);
            return undefined;
        }
    };

    mapExtractedDataOnCurrentInvoice = (extractedData: IQRExtractInvoiceData, overrideData: boolean) => {
        if (this.currentInvoice) {
            const creditorIdOverrideValue = this.decideOverrideValue(
                this.currentInvoice.creditor?.id,
                extractedData.creditorid,
                overrideData
            );

            if (creditorIdOverrideValue) {
                const creditorFromCustomer = this.rootStore.creditorSearchStore.creditorData.find((creditor) => {
                    return creditor.id === creditorIdOverrideValue;
                });

                if (creditorFromCustomer) {
                    this.updateInvoiceStoreWithSelectedCreditor(creditorFromCustomer);
                } else {
                    this.currentInvoice.creditor = undefined;
                }
            } else {
                this.currentInvoice.creditor = undefined;
            }

            const paymentAccountIdOverrideValue = this.decideOverrideValue(
                this.currentInvoice.paymentAccount?.id,
                extractedData.paymentaccountid,
                overrideData
            );

            if (paymentAccountIdOverrideValue) {
                const paymentAccountFromCustomer = this.rootStore.paymentAccountSearchStore.paymentAccountData.find(
                    (paymentAccount) => {
                        return paymentAccount.id === paymentAccountIdOverrideValue;
                    }
                );

                if (paymentAccountFromCustomer) {
                    this.rootStore.paymentAccountSearchStore.updateInvoiceStoreWithSelectedPaymentAccount(
                        paymentAccountFromCustomer
                    );
                } else {
                    this.currentInvoice.paymentAccount = undefined;
                }
            } else {
                this.currentInvoice.paymentAccount = undefined;
            }

            this.currentInvoice.amount = this.decideOverrideValue(
                this.currentInvoice.amount,
                extractedData.amount,
                overrideData
            );

            this.currentInvoice.invoiceNumber = this.decideOverrideValue(
                this.currentInvoice.invoiceNumber,
                extractedData.invoicenumber ?? undefined,
                overrideData
            );

            const dateOverrideValue = this.decideOverrideValue(
                this.currentInvoice.date,
                extractedData.date ? getDateStringForFormElement(extractedData.date) : undefined,
                overrideData
            );
            this.currentInvoice.date = dateOverrideValue ? getDateStringForFormElement(dateOverrideValue) : undefined;
            this.currentInvoice.followInvoiceDateWithBookingDate(this.currentInvoice.date);

            const paidDateOverrideValue = this.decideOverrideValue(
                this.currentInvoice.paidDate,
                extractedData.paiddate ? getDateStringForFormElement(extractedData.paiddate) : undefined,
                overrideData
            );

            this.currentInvoice.paidDate = paidDateOverrideValue
                ? getDateStringForFormElement(paidDateOverrideValue)
                : undefined;

            let dueDateOverrideValue = this.decideOverrideValue(
                this.currentInvoice.dueDate,
                extractedData.duedate ? getDateStringForFormElement(extractedData.duedate) : undefined,
                overrideData
            );

            // If a date and no dueDate is set, then the dueDate is set to the date + 30 days
            if (dateOverrideValue && !dueDateOverrideValue) {
                dueDateOverrideValue = format(
                    addDays(new Date(getDateStringForFormElement(dateOverrideValue)), 30),
                    "yyyy-MM-dd"
                );
            }

            this.currentInvoice.dueDate = dueDateOverrideValue
                ? getDateStringForFormElement(dueDateOverrideValue)
                : undefined;

            if (isObject(extractedData.paymentinfo) && !isEmpty(extractedData.paymentinfo)) {
                const referenceOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.paymentInfo?.reference,
                    extractedData.paymentinfo.reference,
                    overrideData
                );

                let ibanOverrideValue = undefined;

                if (!extractedData.paymentaccountid && !this.currentInvoice.paymentAccount?.iban) {
                    ibanOverrideValue = this.decideOverrideValue(
                        this.currentInvoice.paymentInfo?.iban,
                        extractedData.paymentinfo.iban,
                        overrideData
                    );

                    this.rootStore.paymentAccountSearchStore.setCurrentSelectedIBAN(ibanOverrideValue ?? "");
                }

                const typeOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.paymentInfo?.type,
                    extractedData.paymentinfo.type,
                    overrideData
                );

                this.currentInvoice.paymentInfo = {
                    reference: referenceOverrideValue,
                    iban: ibanOverrideValue,
                    type: typeOverrideValue
                };
            }

            if (
                isObject(extractedData.creditorinfo) &&
                !isEmpty(extractedData.creditorinfo) &&
                !extractedData.creditorid
            ) {
                const nameOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.creditorInfo?.name,
                    extractedData.creditorinfo.name,
                    overrideData
                );

                const numberOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.creditorInfo?.number,
                    extractedData.creditorinfo.number,
                    overrideData
                );

                const streetOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.creditorInfo?.street,
                    extractedData.creditorinfo.street,
                    overrideData
                );

                const housenumberOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.creditorInfo?.housenumber,
                    extractedData.creditorinfo.housenumber,
                    overrideData
                );

                const zipOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.creditorInfo?.zip,
                    extractedData.creditorinfo.zip,
                    overrideData
                );

                const cityOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.creditorInfo?.city,
                    extractedData.creditorinfo.city,
                    overrideData
                );

                const countryOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.creditorInfo?.country,
                    extractedData.creditorinfo.country,
                    overrideData
                );

                this.currentInvoice.creditorInfo = {
                    name: nameOverrideValue,
                    number: numberOverrideValue,
                    street: streetOverrideValue,
                    housenumber: housenumberOverrideValue,
                    zip: zipOverrideValue,
                    city: cityOverrideValue,
                    country: countryOverrideValue
                };
            }

            if (isObject(extractedData.invoiceinfo) && !isEmpty(extractedData.invoiceinfo)) {
                const vatDateOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.invoiceInfo?.vatdate,
                    extractedData.invoiceinfo.vatdate,
                    overrideData
                );

                const vatRateOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.invoiceInfo?.vatrate,
                    extractedData.invoiceinfo.vatrate,
                    overrideData
                );

                const conditionsOverrideValue = this.decideOverrideValue(
                    this.currentInvoice.invoiceInfo?.conditions,
                    extractedData.invoiceinfo.conditions,
                    overrideData
                );

                this.currentInvoice.invoiceInfo = {
                    vatdate: vatDateOverrideValue ? getDateStringForFormElement(vatDateOverrideValue) : undefined,
                    vatrate: vatRateOverrideValue,
                    conditions: conditionsOverrideValue
                };
            }

            const realestateIdOverrideValue = this.decideOverrideValue(
                this.currentInvoice.realestate?.id,
                extractedData.realestateid,
                overrideData
            );

            if (realestateIdOverrideValue) {
                const realestateFromCustomer = this.rootStore.realestateSearchStore.realestateData.find(
                    (realestate) => {
                        return realestate.id === realestateIdOverrideValue;
                    }
                );

                if (realestateFromCustomer) {
                    this.updateInvoiceAndFormWithSelectedRealestate(realestateFromCustomer);
                } else {
                    this.resetRealestateFormField();
                }
            } else {
                this.resetRealestateFormField();
            }

            const unitIdOverrideValue = this.decideOverrideValue(
                this.currentInvoice.unit?.unitId,
                extractedData.unitid,
                overrideData
            );

            if (unitIdOverrideValue) {
                const unitFromCustomer = this.rootStore.unitSearchStore.unitData.find((unit) => {
                    return unit.unitId === unitIdOverrideValue;
                });

                if (unitFromCustomer) {
                    this.updateInvoiceAndFormWithSelectedUnit(unitFromCustomer);
                } else {
                    this.resetUnitFormField();
                }
            } else {
                this.resetUnitFormField();
            }

            const ticketIdOverrideValue = this.decideOverrideValue(
                this.currentInvoice.ticket?.id,
                extractedData.incidentid,
                overrideData
            );

            if (ticketIdOverrideValue) {
                const ticketFromCustomer = this.rootStore.ticketSearchStore.ticketData.find((ticket) => {
                    return ticket.id === ticketIdOverrideValue;
                });

                if (ticketFromCustomer) {
                    this.updateInvoiceAndFormWithSelectedTicket(ticketFromCustomer);
                } else {
                    this.resetTicketFormField();
                }
            } else {
                this.resetTicketFormField();
            }

            const orderIdOverrideValue = this.decideOverrideValue(
                this.currentInvoice.order?.id,
                extractedData.orderid,
                overrideData
            );

            if (orderIdOverrideValue) {
                const orderFromCustomer = this.rootStore.orderSearchStore.orderData.find((order) => {
                    return order.id === orderIdOverrideValue;
                });

                if (orderFromCustomer) {
                    this.updateInvoiceAndFormWithSelectedOrder(orderFromCustomer);
                } else {
                    this.resetOrderFormField();
                }
            } else {
                this.resetOrderFormField();
            }

            this.currentInvoice.paymentPurpose = this.decideOverrideValue(
                this.currentInvoice.paymentPurpose,
                extractedData.paymentpurpose ?? undefined,
                overrideData
            );
        }
    };

    saveCurrentInvoiceComment = async (): Promise<boolean> => {
        if (this.currentInvoice?.comment && this.currentInvoice?.comment.length > 0) {
            this.setLoadingType(LOADING_TYPE.SAVING_COMMENT);

            const hasSuccessfullySavedComment = await this.currentInvoice?.saveComment(
                this.currentInvoice?.id ?? "",
                this.currentInvoice?.comment ?? ""
            );

            this.setLoadingType(LOADING_TYPE.NONE);

            return hasSuccessfullySavedComment ?? false;
        }

        return false;
    };

    /**
     * Fills the QR-optional invoice fields (amount, date, dueDate, invoiceNumber)
     * from the AI-extracted document, but only for the fields the Swiss QR code did
     * NOT provide. Uses the same override semantics as mapExtractedDataOnCurrentInvoice,
     * so QR stays authoritative and this only acts as a fallback for scanned / QR-less
     * invoices. Runs after the QR mapping, so a value QR already wrote is never touched.
     */
    mapAiDocumentFallbackOnCurrentInvoice = (
        details: IAiDocumentDetails,
        qrResult: IQRExtractInvoiceData | undefined,
        overrideData: boolean
    ) => {
        if (!this.currentInvoice) {
            return;
        }

        // Guard against a malformed AI date so it can never throw / wipe the field.
        const toFormDate = (iso: string | null | undefined): string | undefined => {
            if (!iso) {
                return undefined;
            }
            const parsed = new Date(iso);
            return isNaN(parsed.getTime()) ? undefined : format(parsed, "yyyy-MM-dd");
        };

        // "Provided by QR" mirrors decideOverrideValue's has-value rules: the QR
        // mapper writes amount 0 and null strings when it has no value.
        const qrHasAmount = qrResult !== undefined && qrResult.amount !== undefined && qrResult.amount !== 0;
        const qrHasInvoiceNumber = !!qrResult?.invoicenumber;
        const qrHasDate = !!qrResult?.date;
        const qrHasDueDate = !!qrResult?.duedate;

        if (!qrHasAmount && details.total_amount) {
            const newAmount = this.decideOverrideValue(
                this.currentInvoice.amount,
                details.total_amount,
                overrideData
            );
            this.currentInvoice.amount = newAmount;
            if (newAmount === details.total_amount) {
                this.currentInvoice.setAiFilledField(INVOICE_PROPERTY.AMOUNT, true);
            }
        }

        if (!qrHasInvoiceNumber && details.invoice_number) {
            const newInvoiceNumber = this.decideOverrideValue(
                this.currentInvoice.invoiceNumber,
                details.invoice_number,
                overrideData
            );
            this.currentInvoice.invoiceNumber = newInvoiceNumber;
            if (newInvoiceNumber === details.invoice_number) {
                this.currentInvoice.setAiFilledField(INVOICE_PROPERTY.INVOICE_NUMBER, true);
            }
        }

        // date -- only when QR provided none.
        if (!qrHasDate) {
            const aiDate = toFormDate(details.invoice_date);
            if (aiDate) {
                const dateOverrideValue = this.decideOverrideValue(this.currentInvoice.date, aiDate, overrideData);
                this.currentInvoice.date = dateOverrideValue
                    ? getDateStringForFormElement(dateOverrideValue)
                    : this.currentInvoice.date;
                this.currentInvoice.followInvoiceDateWithBookingDate(this.currentInvoice.date);
                if (dateOverrideValue === aiDate) {
                    this.currentInvoice.setAiFilledField(INVOICE_PROPERTY.DATE, true);
                }
            }
        }

        // dueDate -- only when QR provided neither a due date nor a date to derive one
        // from. Mirrors the QR rule: a date with no dueDate -> date + 30 days.
        if (!qrHasDueDate && !qrHasDate) {
            const aiDueDate = toFormDate(details.invoice_due_date);
            let dueDateOverrideValue = aiDueDate
                ? this.decideOverrideValue(this.currentInvoice.dueDate, aiDueDate, overrideData)
                : this.currentInvoice.dueDate;

            if (this.currentInvoice.date && !dueDateOverrideValue) {
                dueDateOverrideValue = format(
                    addDays(new Date(getDateStringForFormElement(this.currentInvoice.date)), 30),
                    "yyyy-MM-dd"
                );
            }

            this.currentInvoice.dueDate = dueDateOverrideValue
                ? getDateStringForFormElement(dueDateOverrideValue)
                : this.currentInvoice.dueDate;
            if (aiDueDate && dueDateOverrideValue === aiDueDate) {
                this.currentInvoice.setAiFilledField(INVOICE_PROPERTY.DUE_DATE, true);
            }
        }
    };

    decideOverrideValue = <T>(
        currentValue: T | undefined,
        newValue: T | undefined,
        override: boolean
    ): T | undefined => {
        const currentHasValue =
            typeof currentValue === "number"
                ? currentValue !== undefined && currentValue !== 0
                : typeof currentValue === "string"
                  ? currentValue !== undefined && currentValue !== ""
                  : currentValue !== undefined && currentValue !== null;

        const newHasValue =
            typeof newValue === "number"
                ? newValue !== undefined && newValue !== 0
                : typeof newValue === "string"
                  ? newValue !== undefined && newValue !== ""
                  : newValue !== undefined && newValue !== null;

        if (override) {
            // Override is true, prefer newValue if it exists, otherwise keep the currentValue
            return newHasValue ? newValue : currentValue;
        } else {
            // Override is false, only set newValue if currentValue is not set and newValue exists
            return !currentHasValue && newHasValue ? newValue : currentValue;
        }
    };

    isinvoiceId = (invoiceid: string): boolean => {
        let isValid = false;
        if (invoiceid) {
            isValid = true;
        } else {
            isValid = false;
        }

        if (isValid) {
            if (uuidRegex.INVOICE_ID.test(invoiceid)) {
                isValid = true;
            } else {
                isValid = false;
            }
        }
        return isValid;
    };

    reRoutePathToInvoiceNumber = async (invoiceid: string) => {
        const { data }: { data: GetInvoiceNumberById } = await apolloClientInstance.query<
            GetInvoiceNumberById,
            GetInvoiceNumberByIdVariables
        >({
            query: GET_INVOICE_NUMBER_BY_ID,
            fetchPolicy: "no-cache",
            variables: {
                id: invoiceid
            }
        });

        if (data.cred_invoices.length === 0) {
            const errorMessage = i18next.t("screens.kredi_flow.invoice_details.error.invoice_not_found");
            this.error = {
                loadingInvoice: errorMessage
            };
        } else {
            const number = data.cred_invoices[0].number;
            window.location.href = `${Route.manager}${Route.creditor}/${number.toString()}`;
        }
    };

    saveCurrentInvoice = async (closeEditModeAfterSave: boolean = true): Promise<boolean> => {
        // Scenario (e): if the AI detection is still in-flight when the user saves,
        // abort it so the save falls through to the base64 upload path.
        this.rootStore.invoiceFilePreviewStore.abortAiDetection();

        this.setLoadingType(LOADING_TYPE.SAVING_INVOICE);
        let hasSuccessfullySavedCurrentInvoice = false;
        const savingNewInvoice = this.currentInvoice?.isNewInvoice ? true : false;

        // Snapshot the load-time realestate id BEFORE save() runs. Invoice.save()
        // refreshes invoiceFieldsOriginal to mirror the just-saved state, so reading
        // it inside fireAuditStampIfNeeded would always match the current value and
        // wrongly conclude the realestate didn't change.
        const realestateIdAtLoadTime = this.currentInvoice?.invoiceFieldsOriginal.realestate?.id;
        const creditorIdAtLoadTime = this.currentInvoice?.invoiceFieldsOriginal.creditor?.id;

        if (this.currentInvoice?.hasChanges) {
            hasSuccessfullySavedCurrentInvoice = await this.currentInvoice?.save();

            if (hasSuccessfullySavedCurrentInvoice) {
                this.currentInvoice.resetAllFieldsWithChanges();

                if (closeEditModeAfterSave) {
                    this.setIsEditing(false);
                }

                await this.fireAuditStampIfNeeded(realestateIdAtLoadTime, creditorIdAtLoadTime);

                if (this.userDefinedAfterSaveFunction) {
                    this.userDefinedAfterSaveFunction();
                }

                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.kredi_flow.action.save.success"),
                    MessageType.SUCCESS
                );
            } else {
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.kredi_flow.form.error.save_form_error"),
                    MessageType.ERROR
                );
            }
        } else {
            if (closeEditModeAfterSave) {
                this.setIsEditing(false);
            }

            hasSuccessfullySavedCurrentInvoice = true;
        }

        this.setLoadingType(LOADING_TYPE.NONE);

        // Signal the InvoiceDetails screen to swap the /new-invoice URL for the real
        // invoice number. The navigation must happen in the component (after React has
        // committed the clean form state), otherwise the unsaved-changes Prompt of
        // useSaveUnsavedChangesInForm can silently block the route change.
        if (savingNewInvoice && hasSuccessfullySavedCurrentInvoice && this.currentInvoice?.number !== undefined) {
            this.setNewlyCreatedInvoiceNumber(this.currentInvoice.number);
        }

        return hasSuccessfullySavedCurrentInvoice ?? false;
    };

    /**
     * After a successful save, stamp the AI detection row with the user's choice so the
     * audit trail can distinguish "user confirmed AI" vs "user picked something else".
     * No-op if there was no AI detection or the user left the realestate field empty.
     */
    fireAuditStampIfNeeded = async (
        originalRealestateIdBeforeSave: string | undefined,
        originalCreditorIdBeforeSave: string | undefined
    ): Promise<void> => {
        const aiDetection = this.currentInvoice?.aiDetection;
        const customerid = this.rootStore.authStore.user?.customerid;

        if (!aiDetection || !customerid) {
            return;
        }

        // Realestate: stamp only when the user actually changed the field (vs. its
        // load-time value). This preserves acceptedsource='ai_auto' for existing
        // invoices when the user saves unrelated changes. The original id must come
        // from the caller because Invoice.save() refreshes invoiceFieldsOriginal to
        // mirror the just-saved state.
        const chosenRealestateId = this.currentInvoice?.realestate?.id;
        const realestateDetection = aiDetection.results?.realestateid;
        if (
            chosenRealestateId &&
            realestateDetection?.detectionid &&
            chosenRealestateId !== originalRealestateIdBeforeSave
        ) {
            // candidates may be empty (no_match / no_query) — still a valid stamp
            // target: it records "user picked something outside the AI list".
            const matchedCandidate = realestateDetection.candidates?.find(
                (candidate) => candidate.entityid === chosenRealestateId
            );
            await this.stampDetectionUserSource(
                realestateDetection.detectionid,
                customerid,
                matchedCandidate?.id ?? null,
                "realestate"
            );
        }

        // Creditor: same rule, against the creditor detection row. Independent of
        // realestate, so a creditor-only change still gets stamped.
        const chosenCreditorId = this.currentInvoice?.creditor?.id;
        const creditorDetection = aiDetection.results?.creditorid;
        if (
            chosenCreditorId &&
            creditorDetection?.detectionid &&
            chosenCreditorId !== originalCreditorIdBeforeSave
        ) {
            const matchedCandidate = creditorDetection.candidates?.find(
                (candidate) => candidate.entityid === chosenCreditorId
            );
            await this.stampDetectionUserSource(
                creditorDetection.detectionid,
                customerid,
                matchedCandidate?.id ?? null,
                "creditor"
            );
        }
    };

    /**
     * Fire MarkDetectionUserSource for one detection row and mirror the result
     * into the matching in-memory field state so the banner / candidate-list stay
     * correct without a page reload. Audit-stamp failure is not user-facing.
     */
    private stampDetectionUserSource = async (
        detectionid: string,
        customerid: string,
        candidateid: string | null,
        field: "realestate" | "creditor"
    ): Promise<void> => {
        try {
            await apolloClientInstance.mutate<MarkDetectionUserSource, MarkDetectionUserSourceVariables>({
                mutation: MARK_DETECTION_USER_SOURCE,
                variables: {
                    detectionid,
                    customerid,
                    candidateid
                }
            });
            if (field === "realestate") {
                this.currentInvoice?.applyAuditStampResult(candidateid);
            } else {
                this.currentInvoice?.applyCreditorAuditStampResult(candidateid);
            }
        } catch (error) {
            console.error("Error stamping AI detection audit source: ", error);
        }
    };

    validateAndSaveInvoiceAccountingsWithoutEvent = async (closeEditModeAfterSave: boolean = true) => {
        if (this.loadingTypeAccounting === LOADING_TYPE_ACCOUNTING.SAVING_ACCOUNTINGS) {
            return;
        }

        this.setLoadingTypeAccounting(LOADING_TYPE_ACCOUNTING.SAVING_ACCOUNTINGS);

        if (this.isAccountingEditing) {
            const accountingsSuccessfullyValidated = this.currentInvoice?.validateAccountings();

            if (accountingsSuccessfullyValidated) {
                await this.saveCurrentInvoiceAccountings(closeEditModeAfterSave);
                this.currentInvoice?.setValidatingAccountingsOnFieldLevel(false);
            } else {
                this.currentInvoice?.setValidatingAccountingsOnFieldLevel(true);
            }
        }

        this.setLoadingTypeAccounting(LOADING_TYPE_ACCOUNTING.NONE);
    };

    validateAndSaveInvoiceAccountings =
        (closeEditModeAfterSave: boolean = true) =>
        async (e: any) => {
            if (e) {
                e.preventDefault();
            }

            await this.validateAndSaveInvoiceAccountingsWithoutEvent(closeEditModeAfterSave);
        };

    saveCurrentInvoiceAccountings = async (closeEditModeAfterSave: boolean = true): Promise<boolean> => {
        let hasSuccessfullySavedCurrentInvoiceAccountings: boolean | undefined = false;

        if (this.currentInvoice?.hasChangesAccounting) {
            hasSuccessfullySavedCurrentInvoiceAccountings = await this.currentInvoice?.saveAccountings();

            if (hasSuccessfullySavedCurrentInvoiceAccountings) {
                if (closeEditModeAfterSave) {
                    this.setIsAccountingEditing(false);
                }

                if (this.userDefinedAfterSaveAccountingsFunction) {
                    this.userDefinedAfterSaveAccountingsFunction();
                }

                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.kredi_flow.action.save_accountings.success"),
                    MessageType.SUCCESS
                );
            } else {
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.kredi_flow.accountings.error.failed_saving"),
                    MessageType.ERROR
                );
            }
        } else {
            if (closeEditModeAfterSave) {
                this.setIsAccountingEditing(false);
            }

            hasSuccessfullySavedCurrentInvoiceAccountings = true;
        }

        return hasSuccessfullySavedCurrentInvoiceAccountings ?? false;
    };

    // Setters
    setCurrentInvoice = (invoice: Invoice) => {
        this.currentInvoice = invoice;
    };

    setIsEditing = (isEditing: boolean) => {
        this.isEditing = isEditing;
    };

    setIsAccountingEditing = (isAccountingEditing: boolean) => {
        this.isAccountingEditing = isAccountingEditing;
    };

    // Entry point for the "Bearbeiten" button of the accounting section. Switches to edit mode and
    // loads the account / booking-text tag suggestions for the existing rows exactly once (the
    // stores deduplicate identical requests, so this is a handful of round trips regardless of the
    // number of rows). Loading them during initialisation was the O(n²) freeze of #23888.
    startAccountingEditing = () => {
        this.setIsAccountingEditing(true);

        const currentInvoice = this.currentInvoice;

        if (currentInvoice && currentInvoice.accountings && currentInvoice.accountings.length > 0) {
            currentInvoice.loadAccountTagsAll().catch((error) => Sentry.captureException(error));
            currentInvoice.loadAccountingHistoryTagsAll().catch((error) => Sentry.captureException(error));
        }
    };

    setLoadingType = (loadingType: LOADING_TYPE) => {
        this.loadingType = loadingType;
    };

    setLoadingTypeAccounting = (loadingTypeAccounting: LOADING_TYPE_ACCOUNTING) => {
        this.loadingTypeAccounting = loadingTypeAccounting;
    };

    setRunningSubscriptionType = (runningSubscriptionType: RUNNING_SUBSCRIPTION_TPYE) => {
        this.runningSubscriptionType = runningSubscriptionType;
    };

    setNewlyCreatedInvoiceNumber = (invoiceNumber?: number) => {
        this.newlyCreatedInvoiceNumber = invoiceNumber;
    };

    setNumberOfDisplayedItems = (numberOfDisplayedItems: number) => {
        this.numberOfDisplayedItems = numberOfDisplayedItems;
    };

    setSliceSizeOfDisplayedItems = (sliceSizeOfDisplayedItems: number) => {
        this.sliceSizeOfDisplayedItems = sliceSizeOfDisplayedItems;
    };

    setUserDefinedAfterSaveFunction = (userDefinedAfterSaveFunction: () => void) => {
        this.userDefinedAfterSaveFunction = userDefinedAfterSaveFunction;
    };

    setScrollToInvoiceDetails = (scrollToInvoiceDetails: boolean) => {
        this.scrollToInvoiceDetails = scrollToInvoiceDetails;
    };

    get isInvoiceBannerDisplayed(): boolean {
        let displayInvoiceBanner = false;

        if (this.currentInvoice) {
            const currentInvoiceState = this.currentInvoice.state;

            if (currentInvoiceState === INVOICE_STATE.PAID || currentInvoiceState === INVOICE_STATE.CANCELLED) {
                displayInvoiceBanner = true;
            }
        }

        return displayInvoiceBanner;
    }

    get isInvoiceLocked(): boolean {
        let invoiceLocked = false;

        if (this.currentInvoice) {
            const currentInvoiceState = this.currentInvoice.state;

            if (
                currentInvoiceState === INVOICE_STATE.PAID ||
                currentInvoiceState === INVOICE_STATE.CANCELLED ||
                this.currentInvoice.isProcessing
            ) {
                invoiceLocked = true;
            }
        }

        return invoiceLocked;
    }
}

export const MIN_DATE = "2000-01-01";
export const MAX_DATE = "2100-12-31";

export class Invoice implements IInvoiceProperties {
    rootStore: RootStore;

    id?: string;
    origin?: number;
    workflowInstance?: string;
    invoiceNumber?: string | undefined;
    number?: number;
    amount?: number;
    date?: string;
    dueDate?: string;
    paidDate?: string;
    state?: INVOICE_STATE;
    invoiceType?: INVOICE_TYPE;
    realestate?: IRealestate;
    realestateThumbnailFileId?: string;
    unit?: IUnitData;
    assignedUser?: IAssignedUser | null;
    creditor?: ICreditor;
    paymentaccountid?: string;
    payoutaccountid?: string;
    paymentInfo?: IInvoicePaymentInfo;
    paymentAccount?: IPaymentAccount;
    ticket?: ITicket;
    order?: IOrder;
    creditorInfo?: ICreditorInfo;
    insertUserId?: string;
    invoiceInfo?: IInvoiceInfo;
    workflowUsers?: IWorkflowUser[];
    comment?: string;
    accountings?: IAccounting[];
    duplicates?: IInvoiceDuplicate[];
    invoiceFile?: IInvoiceFile;
    invoiceAttachmentFiles: IFile[] = [];
    aiDetection?: IAiDetection;
    // Tracks form fields whose current value was filled from the AI fallback
    // path (mapAiDocumentFallbackOnCurrentInvoice). Keyed by INVOICE_PROPERTY.
    // Cleared per-field when the user edits (see updateProperty) and en-masse
    // when a new AI result is applied or the file is deleted.
    aiFilledFields: { [key: string]: boolean } = {};
    isDuplicateModalDisplayed: boolean = false;
    numberOfVisa?: number; // Number of visas set by the workflow engine. This, if set has always the highest priority.
    invoiceFieldsOriginal: IInvoiceProperties = {};
    isValid: boolean = false;
    isProcessing: boolean = false;
    paymentPurpose?: string = "";
    fieldsWithChanges?: string[] = [];
    fieldsWithChangesPaymentInfo?: string[] = [];
    fieldsWithChangesCreditorInfo?: string[] = [];
    fieldsWithChangesInvoiceInfo?: string[] = [];
    fieldsWithChangesInvoiceAttachmentFiles?: string[] = [];
    fieldsWithChangesInvoiceAccountings?: string[][] = []; // Is defined as array of arrays because an invoice can have multiple accountings
    fieldsWithChangesInvoiceAccountingsAccount?: string[][] = [];
    fieldsWithChangesInvoiceAccountingsCostCenter?: string[][] = [];

    displayedAmounts: string[] = [];
    displayedNetAmounts: string[] = [];
    focusedAmountFields: boolean[] = [];
    focusedNetAmountFields: boolean[] = [];

    manualSaveOfInvoiceIsNeeded = false;

    errors = {
        ibanFormatInvalid: "",
        referenceNumberFormatInvalid: "",
        realestateRequired: "",
        dueDateIsBeforeInvoiceDate: "",
        dateIsRequired: "",
        dateIsOutOfRange: "",
        dueDateIsOutOfRange: ""
    };

    isValidatingOnFieldLevel: boolean = false;

    accountingValidationErrors: {
        isDateMissing?: boolean;
        isDateRangeValid?: boolean;
        isRealestateMissing?: boolean;
        isAccountMissing?: boolean;
        isAccountRealestateDifferentFromAccountingRealestate?: boolean;
        isCostCenterMissing?: boolean;
        isMissingRequiredCostCenterData?: boolean;
        isBookingTextMissing?: boolean;
        isAmountMissing?: boolean;
        isQuantityMissing?: boolean;
        isExtraCostDateMissing?: boolean;
        isExtraCostDateRangeValid?: boolean;
        isVatCodeMissing?: boolean;
        isNetAmountMissing?: boolean;
        isVatProportionInvalid?: boolean;
    }[] = [{}];

    isValidatingAccountingsOnFieldLevel: boolean = false;

    history?: IHistory[];

    invoiceWorkflowUsersSubscription?: Subscription;
    invoiceStateUpdateSubscription?: Subscription;
    invoiceIsWorkflowProcessingSubscription?: Subscription;
    invoiceHistoryUpdateSubscription?: Subscription;

    defaultAccountingRealestate: IRealestate | undefined = undefined;
    defaultAccountingUnit: IUnitData | undefined = undefined;
    defaultAccountingBookingText: string | undefined = undefined;
    defaultAccountingDate: string | undefined = undefined;
    defaultAccountingAmount: string | undefined = undefined;

    accountingIdsToDelete: string[] = [];

    isConcludingHumanTaskAssignRealestateAndOtherData: boolean = false;

    numberOfVisasArray: INumberOfVisas[] = [];
    numberOfVisas: number = 0; // Reference for the number of visas in the current invoice

    getDefaultPaymentInfoObject = (): IInvoicePaymentInfo => {
        return {
            iban: "",
            type: "",
            reference: ""
        };
    };

    getDefaultCreditorInfoObject = (): ICreditorInfo => {
        return {
            name: "",
            number: undefined,
            street: "",
            housenumber: "",
            zip: "",
            city: "",
            country: "",
            uid: ""
        };
    };

    getDefaultInvoiceInfoObject = (): IInvoiceInfo => {
        return {
            vatrate: 0,
            vatdate: "",
            conditions: {
                discountRates: [],
                ZahlungsfristTage: 0
            }
        };
    };

    getDefaultAccountingObject = (): IAccounting => {
        return {
            id: uuidv4(),
            amount: "0",
            date: "",
            text: "",
            account: this.getDefaultAccountObject(),
            costCenter: this.getDefaultCostCenterObject()
        };
    };

    getDefaultAccountObject = (): IAccount => {
        return {
            name: "",
            number: "",
            validFrom: ""
        };
    };

    getDefaultCostCenterObject = (): ICostCenter => {
        return {
            name: "",
            short: ""
        };
    };

    setInvoiceFieldsOnObjectWithAccountings = (
        invoiceFieldObject: IInvoiceProperties,
        invoiceFields?: IInvoiceProperties
    ) => {
        this.setInvoiceFieldsOnObjectCommon(invoiceFieldObject, invoiceFields);

        invoiceFieldObject.accountings = invoiceFields?.accountings
            ? [...invoiceFields.accountings]
            : [this.getDefaultAccountingObject()];
    };

    setInvoiceFieldsOnObjectWithoutAccountings = (
        invoiceFieldObject: IInvoiceProperties,
        invoiceFields?: IInvoiceProperties
    ) => {
        this.setInvoiceFieldsOnObjectCommon(invoiceFieldObject, invoiceFields);
    };

    setInvoiceFieldsOnObjectCommon = (invoiceFieldObject: IInvoiceProperties, invoiceFields?: IInvoiceProperties) => {
        if (invoiceFields) {
            invoiceFieldObject.id = invoiceFields.id ?? undefined;
            invoiceFieldObject.origin = invoiceFields.origin ?? INVOICE_ORIGIN.MANUAL;
            invoiceFieldObject.workflowInstance = invoiceFields.workflowInstance ?? undefined;
            invoiceFieldObject.number = invoiceFields.number ?? undefined;
            invoiceFieldObject.amount = invoiceFields.amount ?? undefined;
            invoiceFieldObject.date = invoiceFields.date ?? undefined;
            invoiceFieldObject.dueDate = invoiceFields.dueDate ?? undefined;
            invoiceFieldObject.paidDate = invoiceFields.paidDate ?? undefined;
            invoiceFieldObject.state = invoiceFields.state ?? INVOICE_STATE.NEW;
            invoiceFieldObject.invoiceType = invoiceFields.invoiceType ?? INVOICE_TYPE.INVOICE;
            invoiceFieldObject.realestate = invoiceFields.realestate ?? undefined;
            invoiceFieldObject.realestateThumbnailFileId = invoiceFields.realestateThumbnailFileId ?? undefined;
            invoiceFieldObject.unit = invoiceFields.unit ?? undefined;
            invoiceFieldObject.assignedUser = invoiceFields.assignedUser ?? undefined;
            invoiceFieldObject.creditor = invoiceFields.creditor ?? undefined;
            invoiceFieldObject.invoiceNumber = invoiceFields.invoiceNumber ?? undefined;
            invoiceFieldObject.paymentaccountid = invoiceFields.paymentaccountid ?? undefined;
            invoiceFieldObject.payoutaccountid = invoiceFields.payoutaccountid ?? undefined;
            invoiceFieldObject.comment = invoiceFields.comment ?? "";
            invoiceFieldObject.paymentInfo = invoiceFields.paymentInfo
                ? { ...invoiceFields.paymentInfo }
                : this.getDefaultPaymentInfoObject();
            invoiceFieldObject.creditorInfo = invoiceFields.creditorInfo
                ? { ...invoiceFields.creditorInfo }
                : this.getDefaultCreditorInfoObject();
            invoiceFieldObject.invoiceInfo = invoiceFields.invoiceInfo
                ? { ...invoiceFields.invoiceInfo }
                : this.getDefaultInvoiceInfoObject();
            invoiceFieldObject.paymentAccount = invoiceFields.paymentAccount ?? undefined;
            invoiceFieldObject.ticket = invoiceFields.ticket ?? undefined;
            invoiceFieldObject.order = invoiceFields.order ?? undefined;
            invoiceFieldObject.history = invoiceFields.history ?? [];
            invoiceFieldObject.invoiceFile = invoiceFields.invoiceFile ?? undefined;
            invoiceFieldObject.invoiceAttachmentFiles = invoiceFields.invoiceAttachmentFiles ?? [];
            invoiceFieldObject.aiDetection = invoiceFields.aiDetection ?? undefined;
            invoiceFieldObject.workflowUsers = invoiceFields.workflowUsers ?? [];
            invoiceFieldObject.duplicates = invoiceFields.duplicates ?? [];
            invoiceFieldObject.numberOfVisa = invoiceFields.numberOfVisa ?? undefined;
            invoiceFieldObject.isProcessing = invoiceFields.isProcessing ?? false;
            invoiceFieldObject.paymentPurpose = invoiceFields.paymentPurpose ?? "";
        } else {
            invoiceFieldObject.id = undefined;
            invoiceFieldObject.origin = INVOICE_ORIGIN.MANUAL;
            invoiceFieldObject.workflowInstance = undefined;
            invoiceFieldObject.number = undefined;
            invoiceFieldObject.amount = undefined;
            invoiceFieldObject.date = undefined;
            invoiceFieldObject.dueDate = undefined;
            invoiceFieldObject.paidDate = undefined;
            invoiceFieldObject.state = INVOICE_STATE.NEW;
            invoiceFieldObject.invoiceType = INVOICE_TYPE.INVOICE;
            invoiceFieldObject.realestate = undefined;
            invoiceFieldObject.realestateThumbnailFileId = undefined;
            invoiceFieldObject.unit = undefined;
            invoiceFieldObject.assignedUser = undefined;
            invoiceFieldObject.creditor = undefined;
            invoiceFieldObject.invoiceNumber = undefined;
            invoiceFieldObject.paymentaccountid = undefined;
            invoiceFieldObject.payoutaccountid = undefined;
            invoiceFieldObject.paymentInfo = this.getDefaultPaymentInfoObject();
            invoiceFieldObject.paymentAccount = undefined;
            invoiceFieldObject.ticket = undefined;
            invoiceFieldObject.order = undefined;
            invoiceFieldObject.invoiceFile = undefined;
            invoiceFieldObject.invoiceAttachmentFiles = [];
            invoiceFieldObject.aiDetection = undefined;
            invoiceFieldObject.history = [];
            invoiceFieldObject.comment = undefined;
            invoiceFieldObject.creditorInfo = this.getDefaultCreditorInfoObject();
            invoiceFieldObject.invoiceInfo = this.getDefaultInvoiceInfoObject();
            invoiceFieldObject.workflowUsers = [];
            invoiceFieldObject.duplicates = [];
            invoiceFieldObject.numberOfVisa = undefined;
            invoiceFieldObject.isProcessing = false;
            invoiceFieldObject.paymentPurpose = "";
        }
    };

    constructor(rootStore: RootStore, ticketFields?: IInvoiceProperties) {
        this.rootStore = rootStore;

        this.setInvoiceFieldsOnObjectWithAccountings(this, ticketFields);
        this.setInvoiceFieldsOnObjectWithAccountings(this.invoiceFieldsOriginal, ticketFields);

        makeObservable(this, {
            id: observable,
            origin: observable,
            workflowInstance: observable,
            number: observable,
            amount: observable,
            date: observable,
            dueDate: observable,
            paidDate: observable,
            state: observable,
            invoiceNumber: observable,
            invoiceType: observable,
            realestate: observable,
            unit: observable,
            history: observable,
            comment: observable,
            realestateThumbnailFileId: observable,
            invoiceFieldsOriginal: observable,
            fieldsWithChanges: observable,
            fieldsWithChangesPaymentInfo: observable,
            fieldsWithChangesCreditorInfo: observable,
            fieldsWithChangesInvoiceInfo: observable,
            fieldsWithChangesInvoiceAttachmentFiles: observable,
            fieldsWithChangesInvoiceAccountings: observable,
            fieldsWithChangesInvoiceAccountingsAccount: observable,
            fieldsWithChangesInvoiceAccountingsCostCenter: observable,
            paymentAccount: observable,
            paymentaccountid: observable,
            paymentInfo: observable,
            creditor: observable,
            assignedUser: observable,
            payoutaccountid: observable,
            isValid: observable,
            isProcessing: observable,
            paymentPurpose: observable,
            errors: observable,
            accountingValidationErrors: observable,
            ticket: observable,
            order: observable,
            invoiceFile: observable,
            invoiceAttachmentFiles: observable,
            aiDetection: observable,
            aiFilledFields: observable,
            setAiDetection: action,
            setCreditorAutoAppliedByAi: action,
            applyCreditorAuditStampResult: action,
            clearAiDetection: action,
            setRealestateAutoAppliedByAi: action,
            applyAuditStampResult: action,
            setAiFilledField: action,
            clearAiFilledFields: action,
            creditorInfo: observable,
            invoiceInfo: observable,
            insertUserId: observable,
            workflowUsers: observable,
            invoiceWorkflowUsersSubscription: observable,
            invoiceIsWorkflowProcessingSubscription: observable,
            accountings: observable,
            defaultAccountingRealestate: observable,
            defaultAccountingBookingText: observable,
            defaultAccountingDate: observable,
            defaultAccountingAmount: observable,
            invoiceStateUpdateSubscription: observable,
            invoiceHistoryUpdateSubscription: observable,
            isValidatingOnFieldLevel: observable,
            isValidatingAccountingsOnFieldLevel: observable,
            accountingIdsToDelete: observable,
            manualSaveOfInvoiceIsNeeded: observable,
            isConcludingHumanTaskAssignRealestateAndOtherData: observable,
            numberOfVisasArray: observable,
            numberOfVisas: observable,
            duplicates: observable,
            isDuplicateModalDisplayed: observable,
            numberOfVisa: observable,
            displayedAmounts: observable,
            displayedNetAmounts: observable,
            focusedAmountFields: observable,
            focusedNetAmountFields: observable,

            setInvoiceFieldsOnObjectCommon: action,
            setInvoiceFieldsOnObjectWithAccountings: action,
            setInvoiceFieldsOnObjectWithoutAccountings: action,
            updateProperty: action,
            updatePaymentInfoProperty: action,
            updateCreditorInfoProperty: action,
            updateInvoiceInfoProperty: action,
            updateFieldsWithChanges: action,
            updateFieldsWithChangesAccounting: action,
            updateInvoiceAccountingProperty: action,
            updateInvoiceAccountingAccountProperty: action,
            updateInvoiceAccoutingCostCenterProperty: action,
            resetAllFieldsWithChanges: action,
            resetAllFieldsWithChangesAccounting: action,
            reset: action,
            validate: action,
            validateIBAN: action,
            validateInvoiceReferenceNumber: action,
            validateBaseInvoiceDetails: action,
            validatePaymentAccountDetails: action,
            validatePaymentInfoDetails: action,
            validateIbanAndReferenceNumber: action,
            validateRealestateInvoiceDetails: action,
            validateAccountings: action,
            validateAccounting: action,
            validateOnFieldLevel: action,
            validateAccountingsOnFieldLevel: action,
            resetAccountingValidationErrors: action,
            setValidatingAccountingsOnFieldLevel: action,
            setValidatingOnFieldLevel: action,
            save: action,
            saveAccountings: action,
            createInvoiceBackend: action,
            updateInvoiceHasura: action,
            upsertAccountingsHasura: action,
            startInvoiceWorkflowUsersSubscription: action,
            stopInvoiceWorkflowUsersSubscription: action,
            setInvoiceWorkflowUsersSubscription: action,
            onInvoiceWorkflowUsersDataReceived: action,
            startInvoiceIsWorkflowProcessingSubscription: action,
            stopInvoiceIsWorkflowProcessingSubscription: action,
            setInvoiceIsWorkflowProcessingSubscription: action,
            onInvoiceIsWorkflowProcessingDataReceived: action,
            reloadHistory: action,
            refreshInvoiceSearchVector: action,
            addAccountingRow: action,
            removeAccountingRow: action,
            removeAccountingRowFromSearchStores: action,
            setAccountingDate: action,
            setAccountingId: action,
            setAccountingRealestate: action,
            setAccountingUnit: action,
            setAccountingAccount: action,
            setIsMissingRequiredCostCenterData: action,
            setAccountingCostcenter: action,
            setAccountingBookingText: action,
            setAccountingAmount: action,
            setAccountingVatAmount: action,
            setAccountingQuantity: action,
            setAccountingExtraCostDate: action,
            setAccountingVatCode: action,
            setAccountingVatRate: action,
            setAccountingVatProportion: action,
            setDefaultAccountingRealestate: action,
            setDefaultAccountingUnit: action,
            setDefaultAccountingBookingText: action,
            setDefaultAccountingDate: action,
            setManualSaveOfInvoiceIsNeeded: action,
            initializeDefaultAccountingObjects: action,
            initializeDefaultAccountingObjectForRealestate: action,
            initializeDefaultAccountingObjectForUnit: action,
            initializeDefaultAccountingObjectForAmount: action,
            initializeDefaultAccountingObjectForDate: action,
            followInvoiceDateWithBookingDate: action,
            initializeDefaultAccountingObjectForBookingText: action,
            initializeDefaultAccountingVariables: action,
            resetAccountings: action,
            startInvoiceStateUpdateSubscription: action,
            stopInvoiceStateUpdateSubscription: action,
            setInvoiceStateUpdateSubscription: action,
            onInvoiceStateUpdateDataReceived: action,
            startInvoiceHistoryUpdateSubscription: action,
            stopInvoiceHistoryUpdateSubscription: action,
            setInvoiceHistoryUpdateSubscription: action,
            loadAccountTags: action,
            loadAccountTagsAll: action,
            loadAccountTagsAfterBookingDateChange: action,
            loadAccountTagsAfterCreditorChange: action,
            loadAccountTagsAfterRealestateChange: action,
            loadAccountingHistoryTags: action,
            loadAccountingHistoryTagsAll: action,
            loadAccountingHistoryTagsAfterAccountChange: action,
            loadAccountingHistoryTagsAfterCreditorChange: action,
            loadAccountingHistoryTagsAfterRealestateChange: action,
            concludeHumanTaskAssignRealestateAndOtherData: action,
            loadPayoutAccountDataAndSetIfNeeded: action,
            validateIfAccountsAreValidForBookingDate: action,
            setIsConcludingHumanTaskAssignRealestateAndOtherData: action,
            abortInvoiceProcess: action,
            loadNumberOfVisas: action,
            setNumberOfVisas: action,
            setNumberOfVisasArray: action,
            getDuplicates: action,
            setDuplicates: action,
            setIsDuplicateModalDisplayed: action,
            handleInvoiceFormValidationError: action,
            deleteAttachmentFile: action,
            saveInvoiceAttachmentFiles: action,
            setDisplayedAmount: action,
            setDisplayedNetAmount: action,
            setAmountFieldFocused: action,
            setNetAmountFieldFocused: action,
            initializeDisplayAmounts: action,

            hasChanges: computed,
            hasChangesAccounting: computed,
            isNewInvoice: computed,
            displayInvoiceAccountingsSection: computed,
            isCreditorSelected: computed,
            isRealestateSelected: computed,
            isUnitSelected: computed,
            isTicketSelected: computed,
            isOrderSelected: computed,
            canCurrentUserApproveOrReject: computed,
            hasCurrentUserApproveOrRejectRightsForCurrentInvoiceState: computed,
            accountingDate: computed,
            canCurrentUserSeeStartWorkflowButton: computed,
            totalAccountingAmount: computed,
            totalAccountingAmountMatchesInvoiceAmount: computed,
            hasSavedAccountings: computed,
            numberOfVisasCustomerSetting: computed,
            isLastVisaToApprove: computed,
            isInvoiceStateAllowingToChangeInvoiceType: computed,
            canCurrentUserRestartOrCancel: computed,
            canCurrentUserDelete: computed,
            realestateRequiredToContinueWorkflow: computed,
            iban: computed,
            canDeleteDocument: computed,
            hasDuplicates: computed,
            searchString: computed
        });
    }

    validateInvoiceDate = (date: string | undefined): boolean => {
        let isValid = true;

        if (!date || date === "") {
            isValid = false;
        }

        return isValid;
    };

    validateInvoiceDateRange = (date: string | undefined): boolean => {
        let isValid = true;

        if (date && (date < MIN_DATE || date > MAX_DATE)) {
            isValid = false;
        }

        return isValid;
    };

    validateDateBeforeDueDate = (date: string | undefined, dueDate: string | undefined): boolean => {
        let isValid = true;

        if (date && dueDate && new Date(date) > new Date(dueDate)) {
            isValid = false;
        }

        return isValid;
    };

    validateBaseInvoiceDetails = (isValidPreviousCheck: boolean): boolean => {
        const isValid = isValidPreviousCheck ?? true;

        const isValidDate = this.validateInvoiceDate(this.date);
        if (isValidDate === false) {
            this.errors.dateIsRequired = i18next.t("screens.kredi_flow.form.invoice.error.date_required");
        } else {
            this.errors.dateIsRequired = "";
        }

        const isValidDateRange = this.validateInvoiceDateRange(this.date);
        if (isValidDateRange === false) {
            this.errors.dateIsOutOfRange = i18next.t("screens.kredi_flow.form.invoice.error.date_range_invalid");
        } else {
            this.errors.dateIsOutOfRange = "";
        }

        const isValidDueDate = this.validateInvoiceDate(this.dueDate);
        let isValidDueDateRange = true;
        if (isValidDueDate) {
            isValidDueDateRange = this.validateInvoiceDateRange(this.dueDate);
            if (isValidDueDateRange === false) {
                this.errors.dueDateIsOutOfRange = i18next.t("screens.kredi_flow.form.invoice.error.date_range_invalid");
            } else {
                this.errors.dueDateIsOutOfRange = "";
            }
        } else {
            this.errors.dueDateIsOutOfRange = "";
        }

        const isDateBeforeDueDate = this.validateDateBeforeDueDate(this.date, this.dueDate);
        if (isDateBeforeDueDate === false) {
            this.errors.dueDateIsBeforeInvoiceDate = i18next.t(
                "screens.kredi_flow.form.invoice.error.due_date_is_before_invoice_date"
            );
        } else {
            this.errors.dueDateIsBeforeInvoiceDate = "";
        }

        return isValid && isValidDate && isValidDateRange && isValidDueDateRange && isDateBeforeDueDate;
    };

    validateIBAN = (iban: string): boolean => {
        const isValid = validateIBAN(iban);

        if (!isValid) {
            this.errors.ibanFormatInvalid = i18next.t("screens.kredi_flow.form.invoice.error.invalid_iban");
        } else {
            this.errors.ibanFormatInvalid = "";
        }

        return isValid;
    };

    validateInvoiceReferenceNumber = (reference: string): boolean => {
        let isValid = true;

        const currentIBAN = this.iban;

        if (currentIBAN) {
            const ibanType = getIBANType(currentIBAN);

            switch (ibanType) {
                case IBAN_TYPE.NORMAL:
                    isValid = validateInvoiceReferenceNumberWithType(reference, PAYMENT_INFO_TYPE.SCOR);
                    break;
                case IBAN_TYPE.QR:
                    isValid = validateInvoiceReferenceNumberWithType(reference, PAYMENT_INFO_TYPE.QRR);
                    break;
            }
        }

        if (!isValid) {
            this.errors.referenceNumberFormatInvalid = i18next.t(
                "screens.kredi_flow.form.invoice.error.invalid_reference_number"
            );
            isValid = false;
        } else {
            this.errors.referenceNumberFormatInvalid = "";
        }

        return isValid;
    };

    validateIbanAndReferenceNumber = (isValidPreviousCheck: boolean): boolean => {
        let isValid = isValidPreviousCheck ?? true;

        if (this.paymentInfo) {
            const currentIBAN = this.iban;

            if (currentIBAN) {
                isValid = this.validateIBAN(currentIBAN);

                const ibanType = getIBANType(currentIBAN);

                if (ibanType === IBAN_TYPE.QR && !this.paymentInfo.reference) {
                    this.errors.referenceNumberFormatInvalid = i18next.t(
                        "screens.kredi_flow.form.invoice.error.reference_number_required"
                    );
                    isValid = false;
                } else {
                    this.errors.referenceNumberFormatInvalid = "";
                    isValid = true;
                }
            }

            if (currentIBAN && this.paymentInfo.reference && this.paymentInfo.reference.length > 0) {
                isValid = this.validateInvoiceReferenceNumber(this.paymentInfo.reference);
            }
        }

        return isValid;
    };

    validatePaymentInfoDetails = (isValidPreviousCheck: boolean): boolean => {
        let isValid = isValidPreviousCheck ?? true;

        if (this.paymentInfo) {
            if (this.paymentInfo.iban) {
                const isIBANValid = validateIBAN(this.paymentInfo.iban);

                if (!isIBANValid) {
                    this.errors.ibanFormatInvalid = i18next.t("screens.kredi_flow.form.invoice.error.invalid_iban");
                    isValid = false;
                } else {
                    this.errors.ibanFormatInvalid = "";
                }
            }

            if (this.paymentInfo.reference) {
                const isReferenceValid = this.paymentInfo.type
                    ? validateInvoiceReferenceNumberWithType(this.paymentInfo.reference, this.paymentInfo.type)
                    : false;

                if (!isReferenceValid) {
                    this.errors.referenceNumberFormatInvalid = i18next.t(
                        "screens.kredi_flow.form.invoice.error.invalid_reference_number"
                    );
                    isValid = false;
                } else {
                    this.errors.referenceNumberFormatInvalid = "";
                }
            }
        }

        return isValid;
    };

    validatePaymentAccountDetails = (isValidPreviousCheck: boolean): boolean => {
        let isValid = isValidPreviousCheck ?? true;

        if (this.paymentAccount && this.paymentAccount.iban) {
            const isIBANValid = validateIBAN(this.paymentAccount.iban);

            if (!isIBANValid) {
                this.errors.ibanFormatInvalid = i18next.t("screens.kredi_flow.form.invoice.error.invalid_iban");
                isValid = false;
            } else {
                this.errors.ibanFormatInvalid = "";
            }
        }

        return isValid;
    };

    validateRealestateInvoiceDetails = (isValidPreviousCheck?: boolean): boolean => {
        let isValid = isValidPreviousCheck ?? true;

        if (!this.realestate) {
            this.errors.realestateRequired = i18next.t("screens.kredi_flow.form.invoice.error.realestate_required");
            isValid = false;
        } else {
            this.errors.realestateRequired = "";
        }

        return isValid;
    };

    validate = (isValidPreviousCheck?: boolean): boolean => {
        const isValid = isValidPreviousCheck ?? true;

        const isValidBaseInvoiceDetail = this.validateBaseInvoiceDetails(isValid);
        const isValidIbanAndReferenceNumber = this.validateIbanAndReferenceNumber(isValid);

        return isValid && isValidBaseInvoiceDetail && isValidIbanAndReferenceNumber;
    };

    validateOnFieldLevel = () => {
        if (this.isValidatingOnFieldLevel) {
            const successfullyValidated = this.validate();

            if (successfullyValidated) {
                this.setValidatingOnFieldLevel(false);
            }
        }
    };

    setValidatingOnFieldLevel = (isValidatingOnFieldLevel: boolean) => {
        this.isValidatingOnFieldLevel = isValidatingOnFieldLevel;
    };

    handleInvoiceFormValidationError = () => {
        const errorMessages: string[] = [];

        if (this.errors.dateIsRequired) {
            errorMessages.push(i18next.t("screens.kredi_flow.form.invoice.date"));
        }

        if (this.errors.realestateRequired) {
            errorMessages.push(i18next.t("screens.realestate.title"));
        }

        if (errorMessages.length > 0) {
            const missingInformationMessage = errorMessages.map((errorMessage) => `- ${errorMessage}`).join("\n");

            this.rootStore.uiStore.printFixedErrorMessage(
                `${i18next.t(
                    "screens.kredi_flow.form.invoice.missing_mandatory_information"
                )}:\n${missingInformationMessage}`,
                "krediflow-invoice-form-validation-error"
            );

            this.rootStore.invoiceStore.setScrollToInvoiceDetails(true);
        }

        return errorMessages.length > 0;
    };

    validateAccountingsOnFieldLevel = () => {
        if (this.isValidatingAccountingsOnFieldLevel) {
            const accountingsSuccessfullyValidated = this.validateAccountings();

            if (accountingsSuccessfullyValidated) {
                this.setValidatingAccountingsOnFieldLevel(false);
            }
        }
    };

    validateAccountings = (isValidPreviousCheck?: boolean): boolean => {
        let isValid = isValidPreviousCheck ?? true;

        let isValidAccountings = true;

        if (this.accountings) {
            this.accountings.forEach((accounting, index) => {
                const isAccountingValid = this.validateAccounting(index, isValid);
                if (!isAccountingValid) {
                    isValidAccountings = false;
                }
            });
        }

        isValid = isValidAccountings === false ? false : this.totalAccountingAmountMatchesInvoiceAmount;

        return isValid;
    };

    validateAccounting = (accountingIndex: number, isValidPreviousCheck?: boolean): boolean => {
        let isValid = isValidPreviousCheck ?? true;

        if (this.accountings && this.accountings[accountingIndex]) {
            if (!this.accountingValidationErrors[accountingIndex]) {
                this.accountingValidationErrors[accountingIndex] = {};
            }

            const accountingToValidate = this.accountings[accountingIndex];

            const isValidDate = this.validateAccountingDate(accountingToValidate);
            if (isValidDate === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isDateMissing = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isDateMissing = false;
            }

            const isValidDateRange = this.validateAccountingDateRange(accountingToValidate);
            if (isValidDateRange === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isDateRangeValid = false;
            } else {
                this.accountingValidationErrors[accountingIndex].isDateRangeValid = true;
            }

            const isValidRealestate = this.validateAccountingRealestate(accountingToValidate);
            if (isValidRealestate === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isRealestateMissing = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isRealestateMissing = false;
            }

            const isValidAccount = this.validateAccountingAccount(accountingToValidate);
            if (isValidAccount === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isAccountMissing = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isAccountMissing = false;
            }

            // Do this Check only for Rimo Accountings since there can be a different realestate selected per accounting entry
            const isValidAccountRealestate =
                this.rootStore.authStore.user?.erpType === ErpType.IT2
                    ? true
                    : this.validateAccountingAccountRealestate(accountingToValidate);

            if (isValidAccountRealestate === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isAccountRealestateDifferentFromAccountingRealestate =
                    true;
            } else {
                this.accountingValidationErrors[accountingIndex].isAccountRealestateDifferentFromAccountingRealestate =
                    false;
            }

            const isValidCostcenter = this.validateAccountingCostcenter(accountingToValidate);
            if (isValidCostcenter === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isCostCenterMissing = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isCostCenterMissing = false;
            }

            const isMissingRequiredCostCenterData = accountingToValidate.isMissingRequiredCostCenterData;
            if (isMissingRequiredCostCenterData === true) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isMissingRequiredCostCenterData = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isMissingRequiredCostCenterData = false;
            }

            const isValidBookingText = this.validateAccountingBookingText(accountingToValidate);
            if (isValidBookingText === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isBookingTextMissing = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isBookingTextMissing = false;
            }

            const isValidAmount = this.validateAccountingAmount(accountingToValidate);
            if (isValidAmount === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isAmountMissing = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isAmountMissing = false;
            }

            const isValidQuantity = this.validateAccountingQuantity(accountingToValidate);
            if (isValidQuantity === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isQuantityMissing = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isQuantityMissing = false;
            }

            const isValidExtraCostDate = this.validateAccountingExtraCostDate(accountingToValidate);
            if (isValidExtraCostDate === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isExtraCostDateMissing = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isExtraCostDateMissing = false;
            }

            let isValidExtraCostDateRange = true;
            if (isValidExtraCostDate) {
                isValidExtraCostDateRange = this.validateAccountingExtraCostDateRange(accountingToValidate);
                if (isValidExtraCostDateRange === false) {
                    isValid = false;
                    this.accountingValidationErrors[accountingIndex].isExtraCostDateRangeValid = false;
                } else {
                    this.accountingValidationErrors[accountingIndex].isExtraCostDateRangeValid = true;
                }
            }

            const isValidVatCode = this.validateAccountingVatCode(accountingToValidate);
            if (isValidVatCode === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isVatCodeMissing = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isVatCodeMissing = false;
            }

            const isValidNetAmount = this.validateAccountingNetAmount(accountingToValidate);
            if (isValidNetAmount === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isNetAmountMissing = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isNetAmountMissing = false;
            }

            const hasValidVatProportion = isValidVatProportion(accountingToValidate.vatProportion);
            if (hasValidVatProportion === false) {
                isValid = false;
                this.accountingValidationErrors[accountingIndex].isVatProportionInvalid = true;
            } else {
                this.accountingValidationErrors[accountingIndex].isVatProportionInvalid = false;
            }
        }

        return isValid;
    };

    validateAccountingObject = (accountingToValidate: IAccounting, isValidPreviousCheck?: boolean): boolean => {
        const isValid = isValidPreviousCheck ?? true;

        const isValidDate = this.validateAccountingDate(accountingToValidate);
        const isValidRealestate = this.validateAccountingRealestate(accountingToValidate);
        const isValidAccount = this.validateAccountingAccount(accountingToValidate);
        const isValidAccountRealestate =
            this.rootStore.authStore.user?.erpType === ErpType.IT2
                ? true
                : this.validateAccountingAccountRealestate(accountingToValidate);
        const isValidCostcenter = this.validateAccountingCostcenter(accountingToValidate);
        const isValidBookingText = this.validateAccountingBookingText(accountingToValidate);
        const isValidAmount = this.validateAccountingAmount(accountingToValidate);
        const isValidQuantity = this.validateAccountingQuantity(accountingToValidate);
        const isValidExtraCostDate = this.validateAccountingExtraCostDate(accountingToValidate);
        const isValidExtraCostDateRange = this.validateAccountingExtraCostDateRange(accountingToValidate);
        const isValidVatCode = this.validateAccountingVatCode(accountingToValidate);
        const isValidNetAmount = this.validateAccountingNetAmount(accountingToValidate);

        return (
            isValid &&
            isValidDate &&
            isValidRealestate &&
            isValidAccount &&
            isValidAccountRealestate &&
            isValidCostcenter &&
            isValidBookingText &&
            isValidAmount &&
            isValidQuantity &&
            isValidExtraCostDate &&
            isValidExtraCostDateRange &&
            isValidVatCode &&
            isValidNetAmount
        );
    };

    validateAccountingDate = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        if (accountingToValidate.date === undefined || accountingToValidate.date === "") {
            isValid = false;
        }

        return isValid;
    };

    validateAccountingDateRange = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        if (
            accountingToValidate.date &&
            (accountingToValidate.date < MIN_DATE || accountingToValidate.date > MAX_DATE)
        ) {
            isValid = false;
        }

        return isValid;
    };

    validateAccountingRealestate = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        if (
            accountingToValidate.realestate === undefined ||
            (accountingToValidate.realestate && !accountingToValidate.realestate.id)
        ) {
            isValid = false;
        }

        return isValid;
    };

    validateAccountingAccount = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        if (
            accountingToValidate.account === undefined ||
            (accountingToValidate.account && accountingToValidate.account.id === undefined)
        ) {
            isValid = false;
        }

        return isValid;
    };

    validateAccountingAccountRealestate = (accountingToValidate: IAccounting): boolean => {
        let realestateOfAccountingDoesNotDifferFromAccount = true;

        if (
            accountingToValidate.account &&
            accountingToValidate.account.id &&
            accountingToValidate.realestate?.id &&
            accountingToValidate.account?.realestate?.id &&
            accountingToValidate.realestate?.id !== accountingToValidate.account?.realestate?.id
        ) {
            realestateOfAccountingDoesNotDifferFromAccount = false;
        }

        return realestateOfAccountingDoesNotDifferFromAccount;
    };

    validateAccountingCostcenter = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        const erpType = this.rootStore.authStore.user?.erpType;

        if (erpType === ErpType.IT2) {
            const accountIsSetAndRequiredCostcenter =
                accountingToValidate.account &&
                accountingToValidate.account.id &&
                accountingToValidate.account.requiresCostCenter;

            if (
                accountIsSetAndRequiredCostcenter &&
                (accountingToValidate.costCenter === undefined ||
                    (accountingToValidate.costCenter && accountingToValidate.costCenter.id === undefined))
            ) {
                isValid = false;
            }
        }

        return isValid;
    };

    validateAccountingBookingText = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        if (accountingToValidate.text === undefined || accountingToValidate.text === "") {
            isValid = false;
        }

        return isValid;
    };

    validateAccountingAmount = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        if (
            accountingToValidate.amount === undefined ||
            accountingToValidate.amount === "" ||
            accountingToValidate.amount === "0" ||
            accountingToValidate.amount === "0.00"
        ) {
            isValid = false;
        }

        return isValid;
    };

    validateAccountingQuantity = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        const accountIsSetAndRequiresQuantity =
            accountingToValidate.account &&
            accountingToValidate.account.id &&
            accountingToValidate.account.requiresQuantity;

        if (
            accountIsSetAndRequiresQuantity &&
            (accountingToValidate.quantity === undefined || accountingToValidate.quantity === "")
        ) {
            isValid = false;
        }

        return isValid;
    };

    validateAccountingExtraCostDate = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        const accountIsSetAndRequiresExtraCostDate =
            accountingToValidate.account &&
            accountingToValidate.account.id &&
            accountingToValidate.account.extraCostDate;

        if (
            accountIsSetAndRequiresExtraCostDate &&
            (accountingToValidate.extraCostDate === undefined || accountingToValidate.extraCostDate === "")
        ) {
            isValid = false;
        }

        return isValid;
    };

    validateAccountingExtraCostDateRange = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        const accountIsSetAndRequiresExtraCostDate =
            accountingToValidate.account &&
            accountingToValidate.account.id &&
            accountingToValidate.account.extraCostDate;

        if (
            accountIsSetAndRequiresExtraCostDate &&
            (accountingToValidate.extraCostDate === undefined ||
                accountingToValidate.extraCostDate === "" ||
                accountingToValidate.extraCostDate < MIN_DATE ||
                accountingToValidate.extraCostDate > MAX_DATE)
        ) {
            isValid = false;
        }

        return isValid;
    };

    validateAccountingVatCode = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        const accountIsSetAndRequiredVatCode =
            accountingToValidate.account &&
            accountingToValidate.account.id &&
            (accountingToValidate.account.vatConfig === INVOICE_ACCOUNT_VAT_CONFIG.REQUIRES_VAT ||
                accountingToValidate.account.vatConfig === INVOICE_ACCOUNT_VAT_CONFIG.ONLY_DEFAULT_VATCODE);

        if (
            accountIsSetAndRequiredVatCode &&
            (accountingToValidate.vatCodeId === undefined || accountingToValidate.vatCodeId === "")
        ) {
            isValid = false;
        }

        return isValid;
    };

    validateAccountingNetAmount = (accountingToValidate: IAccounting): boolean => {
        let isValid = true;

        const accountingIndex = this.accountings?.findIndex((accounting) => accounting.id === accountingToValidate.id);

        if (accountingIndex) {
            const netAmountRequired = this.canSeeAccountingNetAmountField(accountingIndex);

            if (
                netAmountRequired &&
                (accountingToValidate.netAmount === undefined ||
                    accountingToValidate.netAmount === "" ||
                    accountingToValidate.netAmount === "0" ||
                    accountingToValidate.netAmount === "0.00")
            ) {
                isValid = false;
            }
        }

        return isValid;
    };

    resetAccountingValidationErrors = () => {
        this.accountingValidationErrors = [{}];
    };

    setValidatingAccountingsOnFieldLevel = (isValidatingAccountingsOnFieldLevel: boolean) => {
        this.isValidatingAccountingsOnFieldLevel = isValidatingAccountingsOnFieldLevel;
    };

    getAccountingValuationErrorString = (accountingIndex: number): string => {
        const accountingValuationMissingFields = [];

        if (
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isDateMissing
        ) {
            accountingValuationMissingFields.push(i18next.t("screens.kredi_flow.accountings.date"));
        }

        if (
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isRealestateMissing
        ) {
            accountingValuationMissingFields.push(i18next.t("screens.realestate.title"));
        }

        if (
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isAccountMissing
        ) {
            accountingValuationMissingFields.push(i18next.t("screens.kredi_flow.accountings.account"));
        }

        /*  If the account has no costcenter assigned in ImmoTop2, the costcenter field is not displayed at all.
            In that case only the more specific error about the missing ImmoTop2 configuration is shown.
        */
        if (
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isCostCenterMissing &&
            !this.accountingValidationErrors[accountingIndex].isMissingRequiredCostCenterData
        ) {
            accountingValuationMissingFields.push(i18next.t("screens.kredi_flow.accountings.costcenter"));
        }

        if (
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isBookingTextMissing
        ) {
            accountingValuationMissingFields.push(i18next.t("screens.kredi_flow.accountings.text"));
        }

        if (
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isAmountMissing
        ) {
            accountingValuationMissingFields.push(i18next.t("screens.kredi_flow.accountings.amount"));
        }

        if (
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isQuantityMissing
        ) {
            accountingValuationMissingFields.push(i18next.t("screens.kredi_flow.accountings.quantity"));
        }

        if (
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isExtraCostDateMissing
        ) {
            accountingValuationMissingFields.push(i18next.t("screens.kredi_flow.accountings.extracostdate"));
        }

        if (
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isVatCodeMissing
        ) {
            accountingValuationMissingFields.push(i18next.t("screens.kredi_flow.accountings.vat_code"));
        }

        if (
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isNetAmountMissing
        ) {
            accountingValuationMissingFields.push(
                `${i18next.t("screens.kredi_flow.accountings.amount")} (${i18next.t(
                    "screens.kredi_flow.accountings.excl_vat"
                )})`
            );
        }

        const missingFieldsCount = accountingValuationMissingFields.length;

        let missingFieldsString = "";
        if (missingFieldsCount === 1) {
            missingFieldsString = accountingValuationMissingFields[0];
        } else if (missingFieldsCount === 2) {
            missingFieldsString = accountingValuationMissingFields.join(` ${i18next.t("labels.and")} `);
        } else if (missingFieldsCount > 2) {
            missingFieldsString =
                accountingValuationMissingFields.slice(0, -1).join(", ") +
                ` ${i18next.t("labels.and")} ` +
                accountingValuationMissingFields[missingFieldsCount - 1];
        }

        const accountingValuationErrorString =
            missingFieldsCount === 0
                ? ""
                : missingFieldsCount === 1
                  ? i18next.t("screens.kredi_flow.accountings.error.missing_field", {
                        missing_field_string: missingFieldsString
                    })
                  : i18next.t("screens.kredi_flow.accountings.error.missing_fields", {
                        missing_fields_string: missingFieldsString
                    });

        return accountingValuationErrorString;
    };

    getAccountingIsMissingRequiredCostCenterDataErrorString = (accountingIndex: number): string | undefined => {
        let missingRequiredCostCenterDataErrorString: string | undefined = undefined;

        const isMissingRequiredCostCenterData =
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isMissingRequiredCostCenterData;

        if (isMissingRequiredCostCenterData) {
            missingRequiredCostCenterDataErrorString = i18next
                .t("screens.kredi_flow.accountings.error.missing_required_costcenter_data")
                .toString();
        }

        return missingRequiredCostCenterDataErrorString;
    };

    getAccountingVatProportionErrorString = (accountingIndex: number): string | undefined => {
        return this.accountingValidationErrors[accountingIndex]?.isVatProportionInvalid
            ? i18next.t("screens.kredi_flow.accountings.error.vat_proportion_invalid")
            : undefined;
    };

    getAccountingValuationRealestateMismatchErrorString = (accountingIndex: number): string | undefined => {
        let errorMessage = undefined;

        if (
            this.accountingValidationErrors[accountingIndex] &&
            this.accountingValidationErrors[accountingIndex].isAccountRealestateDifferentFromAccountingRealestate
        ) {
            errorMessage = i18next.t("screens.kredi_flow.accountings.error.realestate_mismatch_error");
        }

        return errorMessage;
    };

    reset = () => {
        this.id = undefined;
        this.origin = INVOICE_ORIGIN.MANUAL;
        this.workflowInstance = undefined;
        this.number = undefined;
        this.amount = undefined;
        this.date = undefined;
        this.dueDate = undefined;
        this.paidDate = undefined;
        this.state = INVOICE_STATE.NEW;
        this.invoiceType = INVOICE_TYPE.CREDIT;
        this.realestate = undefined;
        this.realestateThumbnailFileId = undefined;
        this.assignedUser = undefined;
        this.creditor = undefined;
        this.invoiceNumber = undefined;
        this.paymentaccountid = undefined;
        this.payoutaccountid = undefined;
        this.paymentInfo = {};
        this.paymentAccount = {};
        this.unit = undefined;
        this.ticket = undefined;
        this.order = undefined;
        this.invoiceFile = undefined;
        this.insertUserId = undefined;
        this.creditorInfo = {};
        this.insertUserId = undefined;
        this.invoiceInfo = {};
        this.comment = undefined;
        this.history = [];
        this.accountings = [this.getDefaultAccountingObject()];
    };

    updatePaymentInfoProperty = (field: keyof IInvoicePaymentInfo | string, value: any) => {
        // Initialize this.paymentInfo if it's undefined
        if (!this.paymentInfo) {
            this.paymentInfo = {} as IInvoicePaymentInfo;
        }

        if (this.paymentInfo) {
            (this.paymentInfo as any)[field] = value;
        }

        this.updateFieldsWithChanges(INVOICE_FIELD_TYPE.PAYMENT_INFO);
    };

    updateCreditorInfoProperty = (field: keyof ICreditorInfo | string, value: any) => {
        // Initialize this.creditorInfo if it's undefined
        if (!this.creditorInfo) {
            this.creditorInfo = {} as ICreditorInfo;
        }

        if (this.creditorInfo) {
            (this.creditorInfo as any)[field] = value;
        }

        this.updateFieldsWithChanges(INVOICE_FIELD_TYPE.CREDITOR_INFO);
    };

    updateInvoiceInfoProperty = (field: keyof IInvoiceInfo | string, value: any) => {
        // Initialize this.creditorInfo if it's undefined
        if (!this.invoiceInfo) {
            this.invoiceInfo = {} as IInvoiceInfo;
        }

        if (this.invoiceInfo) {
            (this.invoiceInfo as any)[field] = value;
        }

        this.updateFieldsWithChanges(INVOICE_FIELD_TYPE.INVOICE_INFO);
    };

    updateInvoiceAccountingProperty = (index: number, field: keyof IAccounting | string, value: any) => {
        if (this.accountings && this.accountings[index]) {
            (this.accountings[index] as any)[field] = value;
        }

        this.updateFieldsWithChangesAccounting(index, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    };

    updateInvoiceAccountingAccountProperty = (index: number, field: keyof IAccount | string, value: any) => {
        if (this.accountings && this.accountings[index] && this.accountings[index].account) {
            (this.accountings[index].account as any)[field] = value;
        }

        this.updateFieldsWithChangesAccounting(index, INVOICE_ACCOUNTING_FIELD_TYPE.ACCOUNT);
    };

    updateInvoiceAccoutingCostCenterProperty = (index: number, field: keyof ICostCenter | string, value: any) => {
        if (this.accountings && this.accountings[index] && this.accountings[index].costCenter) {
            (this.accountings[index].costCenter as any)[field] = value;
        }

        this.updateFieldsWithChangesAccounting(index, INVOICE_ACCOUNTING_FIELD_TYPE.COST_CENTER);
    };

    updateProperty = (field: keyof Invoice | string, value: any) => {
        if (field === INVOICE_PROPERTY.INVOICE_ATTACHMENT_FILES) {
            this.invoiceAttachmentFiles?.push(value);
        } else if (field in this) {
            (this as any)[field] = value;
        }

        // A user-driven change on a field previously auto-filled by AI drops
        // the "Mit KI erkannt" hint — the mapper writes fields via direct
        // assignment, so this only fires for edits routed through the form.
        if (this.aiFilledFields[field as string]) {
            this.setAiFilledField(field as string, false);
        }

        this.updateFieldsWithChanges(INVOICE_FIELD_TYPE.GENERAL);
        this.validateOnFieldLevel();
    };

    updateFieldsWithChanges = (fieldType: INVOICE_FIELD_TYPE) => {
        if (this.rootStore.invoiceStore.isEditing) {
            switch (fieldType) {
                case INVOICE_FIELD_TYPE.GENERAL:
                    this.fieldsWithChanges = findDifferences(this.invoiceFieldsOriginal, this, true, [
                        INVOICE_PROPERTY.REALESTATE_THUMBNAIL_FILEID,
                        INVOICE_PROPERTY.ASSIGNED_USER,
                        INVOICE_PROPERTY.STATE,
                        INVOICE_PROPERTY.HISTORY,
                        INVOICE_PROPERTY.COMMENT,
                        INVOICE_PROPERTY.WORKFLOW_USERS,
                        INVOICE_PROPERTY.STATE
                    ]);
                    break;
                case INVOICE_FIELD_TYPE.PAYMENT_INFO:
                    this.fieldsWithChangesPaymentInfo = findDifferences(
                        this.invoiceFieldsOriginal.paymentInfo,
                        this.paymentInfo,
                        true
                    );
                    break;
                case INVOICE_FIELD_TYPE.CREDITOR_INFO:
                    this.fieldsWithChangesCreditorInfo = findDifferences(
                        this.invoiceFieldsOriginal.creditorInfo,
                        this.creditorInfo,
                        true
                    );
                    break;
                case INVOICE_FIELD_TYPE.INVOICE_INFO:
                    this.fieldsWithChangesInvoiceInfo = findDifferences(
                        this.invoiceFieldsOriginal.invoiceInfo,
                        this.invoiceInfo,
                        true
                    );
                    break;
                case INVOICE_FIELD_TYPE.INVOICE_ATTACHMENT_FILES:
                    this.fieldsWithChangesInvoiceAttachmentFiles = findDifferences(
                        this.invoiceFieldsOriginal.invoiceAttachmentFiles,
                        this.invoiceAttachmentFiles,
                        false
                    );
                    break;
            }
        }
    };

    saveComment = async (upsertInvoiceId: string, comment: string) => {
        let success = false;

        if (comment && comment.length > 0) {
            try {
                await apolloClientInstance.mutate<UpsertInvoiceComment, UpsertInvoiceCommentVariables>({
                    mutation: UPSERT_INVOICE_COMMENT,
                    variables: {
                        comment: {
                            userid: this.rootStore.authStore.user?.userid,
                            invoiceid: upsertInvoiceId,
                            language: "de",
                            text: comment
                        }
                    }
                });
            } catch (error) {
                console.error(`Error commenting ticket: ${upsertInvoiceId}`, error);
            }

            this.updateProperty(INVOICE_PROPERTY.COMMENT, "");
            success = true;
        }
        return success;
    };

    updateCommentIsPinned = async (invoiceCommentId: string, newIsPinnedState: boolean) => {
        let success = false;
        const oldIsPinnedState = !newIsPinnedState;
        try {
            runInAction(() => {
                this.history?.forEach((entry) => {
                    if (entry.entityid === invoiceCommentId) {
                        entry.isPinned = newIsPinnedState;
                    }
                });
            });

            await apolloClientInstance.mutate<UpdateInvoiceCommentIsPinned, UpdateInvoiceCommentIsPinnedVariables>({
                mutation: UPDATE_INVOICE_COMMENT_IS_PINNED,
                variables: {
                    id: invoiceCommentId,
                    is_pinned: newIsPinnedState
                }
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t(`screens.kredi_flow.history.comment.${newIsPinnedState === true ? "pin" : "unpin"}_success`),
                MessageType.SUCCESS
            );
        } catch (error) {
            console.error(`Error setting 'is_pinned' for: ${invoiceCommentId}`, error);

            this.rootStore.uiStore.printStatusMessage(
                i18next.t(`screens.kredi_flow.history.comment.${newIsPinnedState === true ? "pin" : "unpin"}_failure`),
                MessageType.ERROR
            );

            runInAction(() => {
                this.history?.forEach((entry) => {
                    if (entry.entityid === invoiceCommentId) {
                        entry.isPinned = oldIsPinnedState;
                    }
                });
            });
        }

        success = true;

        return success;
    };

    updateFieldsWithChangesAccounting = (accountingIndex: number, fieldType: INVOICE_ACCOUNTING_FIELD_TYPE) => {
        if (this.rootStore.invoiceStore.isAccountingEditing) {
            switch (fieldType) {
                case INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL:
                    if (
                        this.accountings &&
                        this.accountings[accountingIndex] &&
                        this.invoiceFieldsOriginal.accountings &&
                        this.invoiceFieldsOriginal.accountings[accountingIndex]
                    ) {
                        if (this.fieldsWithChangesInvoiceAccountings === undefined) {
                            this.fieldsWithChangesInvoiceAccountings = [];
                        }

                        if (this.fieldsWithChangesInvoiceAccountings[accountingIndex] === undefined) {
                            this.fieldsWithChangesInvoiceAccountings[accountingIndex] = [];
                        }

                        this.fieldsWithChangesInvoiceAccountings[accountingIndex] = findDifferences(
                            this.invoiceFieldsOriginal.accountings[accountingIndex],
                            this.accountings[accountingIndex],
                            true
                        );
                    }
                    break;
                case INVOICE_ACCOUNTING_FIELD_TYPE.ACCOUNT:
                    // TODO: Implement
                    break;
                case INVOICE_ACCOUNTING_FIELD_TYPE.COST_CENTER:
                    // TODO: Implement
                    break;
            }
        }
    };

    resetAllFieldsWithChanges = () => {
        this.fieldsWithChanges = [];
        this.fieldsWithChangesPaymentInfo = [];
        this.fieldsWithChangesCreditorInfo = [];
        this.fieldsWithChangesInvoiceInfo = [];
        this.fieldsWithChangesInvoiceAttachmentFiles = [];
    };

    resetAllFieldsWithChangesAccounting = () => {
        this.fieldsWithChangesInvoiceAccountings = [];
    };

    setIsValid = (isValid: boolean) => {
        this.isValid = isValid;
    };

    setDefaultAccountingRealestate = (realestate?: IRealestate) => {
        this.defaultAccountingRealestate = realestate;
    };

    setDefaultAccountingUnit = (unit?: IUnitData) => {
        this.defaultAccountingUnit = unit;
    };

    setDefaultAccountingBookingText = (bookingText: string) => {
        this.defaultAccountingBookingText = bookingText;
    };

    setDefaultAccountingDate = (date: string) => {
        this.defaultAccountingDate = date;
    };

    setManualSaveOfInvoiceIsNeeded = (manualSaveOfInvoiceIsNeeded: boolean) => {
        this.manualSaveOfInvoiceIsNeeded = manualSaveOfInvoiceIsNeeded;
    };

    setAiDetection = (detection: IAiDetection) => {
        this.aiDetection = detection;
    };

    clearAiDetection = () => {
        this.aiDetection = undefined;
        this.aiFilledFields = {};
    };

    setAiFilledField = (field: string, value: boolean) => {
        if (value) {
            this.aiFilledFields = { ...this.aiFilledFields, [field]: true };
        } else if (this.aiFilledFields[field]) {
            const next = { ...this.aiFilledFields };
            delete next[field];
            this.aiFilledFields = next;
        }
    };

    clearAiFilledFields = () => {
        this.aiFilledFields = {};
    };

    setRealestateAutoAppliedByAi = (value: boolean) => {
        if (this.aiDetection?.results.realestateid) {
            this.aiDetection.results.realestateid.wasAutoAppliedByAi = value;
        }
    };

    setCreditorAutoAppliedByAi = (value: boolean) => {
        if (this.aiDetection?.results.creditorid) {
            this.aiDetection.results.creditorid.wasAutoAppliedByAi = value;
        }
    };

    /**
     * Mirror a just-fired MarkDetectionUserSource mutation for the realestate
     * detection into the per-field in-memory state so the banner / candidate-list
     * logic stays consistent without a page reload. Always sets
     * acceptedsource='user'; passes the chosen candidate id (or null if the user
     * picked outside the AI list).
     */
    applyAuditStampResult = (candidateid: string | null) => {
        if (this.aiDetection?.results.realestateid) {
            this.aiDetection.results.realestateid.acceptedsource = "user";
            this.aiDetection.results.realestateid.acceptedcandidateid = candidateid;
            this.aiDetection.results.realestateid.wasAutoAppliedByAi = false;
        }
    };

    /**
     * Creditor counterpart of applyAuditStampResult — mirrors a just-fired
     * MarkDetectionUserSource for the creditor detection into the per-field
     * in-memory state so the creditor banner / candidate-list stays consistent.
     */
    applyCreditorAuditStampResult = (candidateid: string | null) => {
        if (this.aiDetection?.results.creditorid) {
            this.aiDetection.results.creditorid.acceptedsource = "user";
            this.aiDetection.results.creditorid.acceptedcandidateid = candidateid;
            this.aiDetection.results.creditorid.wasAutoAppliedByAi = false;
        }
    };

    save = async (): Promise<boolean> => {
        let upsertInvoiceId: string | undefined = undefined;
        let wasSuccessfullySaved = false;
        const savingNewInvoice = this.isNewInvoice ? true : false;

        const realestateWasChanged = this.fieldsWithChanges?.includes(INVOICE_PROPERTY.REALESTATE);
        const needsToConcludeHumanTaskAssignRealestateAndOtherData =
            !savingNewInvoice && this.realestate?.id !== undefined && this.state === INVOICE_STATE.ASSIGN_REALESTATE;

        const invoiceVariables: IInvoiceUpsertVariables =
            await this.getInvoiceInsertInputQueryVariables(savingNewInvoice);

        if (savingNewInvoice) {
            upsertInvoiceId = await this.createInvoiceBackend(invoiceVariables);
        } else {
            upsertInvoiceId = await this.updateInvoiceHasura(invoiceVariables);
        }

        if (upsertInvoiceId) {
            if (!savingNewInvoice) {
                if (this.invoiceFile && this.invoiceFile.state === INVOICE_FILE_STATE.HAS_CHANGED) {
                    await this.saveInvoiceImage(this.invoiceFile, upsertInvoiceId);
                }
            }

            if (
                this.invoiceAttachmentFiles &&
                this.invoiceAttachmentFiles.length > 0 &&
                this.hasNewInvoiceAttachmentFiles
            ) {
                await this.saveInvoiceAttachmentFiles(upsertInvoiceId);
            }
        }

        wasSuccessfullySaved = upsertInvoiceId !== undefined;

        if (wasSuccessfullySaved) {
            this.setInvoiceFieldsOnObjectWithoutAccountings(this.invoiceFieldsOriginal, this);
            this.resetAllFieldsWithChanges();
            await this.reloadHistory();
            await this.refreshInvoiceSearchVector();

            const erpType = this.rootStore.authStore.user?.erpType;
            const hasSavedAccountings =
                this.accountings &&
                this.accountings?.length > 0 &&
                this.accountings[0].account &&
                this.accountings[0].account.id !== undefined;

            if (realestateWasChanged) {
                if (erpType === ErpType.IT2 || (erpType === ErpType.RIMO && !hasSavedAccountings)) {
                    this.initializeDefaultAccountingObjectForRealestate();
                }
            }

            if (needsToConcludeHumanTaskAssignRealestateAndOtherData) {
                this.concludeHumanTaskAssignRealestateAndOtherData();
            }
        }

        return wasSuccessfullySaved;
    };

    saveAccountings = async (): Promise<boolean> => {
        let upsertedAccountingsIds: string[] = [];
        let wasSuccessfullySaved = false;

        if (this.manualSaveOfInvoiceIsNeeded) {
            this.save();
            this.setManualSaveOfInvoiceIsNeeded(false);
        }

        const accountingVariables: cred_accountings_insert_input[] =
            await this.getAccountingsInsertInputQueryVariables();

        const existingAccountingsByOtherUser = await this.checkExistingAccountingsByOtherUser();

        if (existingAccountingsByOtherUser) {
            await this.deleteAccountings();
        }

        upsertedAccountingsIds = await this.upsertAccountingsHasura(accountingVariables);

        wasSuccessfullySaved = upsertedAccountingsIds.length > 0;

        if (wasSuccessfullySaved) {
            if (this.accountings) {
                runInAction(() => {
                    this.invoiceFieldsOriginal.accountings = cloneDeep(toJS(this.accountings));
                });
            }

            this.resetAllFieldsWithChangesAccounting();

            this.accountingIdsToDelete.forEach(async (accountingId) => {
                await this.deleteAccounting(accountingId);
            });

            runInAction(() => {
                this.accountingIdsToDelete = [];
            });
        }

        return wasSuccessfullySaved;
    };

    checkExistingAccountingsByOtherUser = async (): Promise<boolean> => {
        let existingAccountingsByOtherUser = false;

        try {
            const { data } = await apolloClientInstance.query<
                CheckExistingAccountingsByOtherUser,
                CheckExistingAccountingsByOtherUserVariables
            >({
                query: CHECK_EXISTING_ACCOUNTINGS_BY_OTHER_USER,
                fetchPolicy: "no-cache",
                variables: {
                    invoiceid: this.id,
                    userid: this.rootStore.authStore.user?.userid ?? ""
                }
            });

            const accountingsByOtherUsersExist = data.cred_accountings.length > 0;

            if (accountingsByOtherUsersExist) {
                existingAccountingsByOtherUser = true;
            }
        } catch (error) {
            console.error("Error checking if accountings from another user exist: ", error);
        }

        return existingAccountingsByOtherUser;
    };

    deleteAccountings = async (): Promise<boolean> => {
        let deletedAccountings = false;

        try {
            await apolloClientInstance.mutate<DeleteAccountingsByInvoiceId, DeleteAccountingsByInvoiceIdVariables>({
                mutation: DELETE_ACCOUNTINGS_BY_INVCOICE_ID,
                variables: {
                    invoiceid: this.id
                }
            });

            deletedAccountings = true;
        } catch (error) {
            console.error("Error deleting accountings: ", error);
        }

        return deletedAccountings;
    };

    createInvoiceBackend = async (invoiceVariables: IInvoiceUpsertVariables): Promise<string | undefined> => {
        let upsertIncidentId = undefined;

        try {
            const response = await Axios.getAuthInstance().post(NetworkConfig.createInvoiceUrl, invoiceVariables, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });

            const createIncidentResult = response.data as ICreateInvoiceResponse;
            upsertIncidentId = createIncidentResult.data.invoiceId;
            this.updateProperty(INVOICE_PROPERTY.ID, createIncidentResult.data.invoiceId);
            this.updateProperty(INVOICE_PROPERTY.NUMBER, createIncidentResult.data.number);
            this.updateProperty(INVOICE_PROPERTY.WORKFLOW_INSTANCE, createIncidentResult.data.workflowInstanceId);
        } catch (error) {
            console.error("Error creating invoice: ", error);
        }

        return upsertIncidentId;
    };

    updateInvoiceHasura = async (invoiceVariables: IInvoiceUpsertVariables): Promise<string> => {
        let upsertInvoiceId = undefined;

        try {
            const { data: upsertInvoiceResult } = await apolloClientInstance.mutate<
                UpsertInvoice,
                UpsertInvoiceVariables
            >({
                mutation: UPSERT_INVOICE,
                variables: {
                    invoice: invoiceVariables
                }
            });

            if (upsertInvoiceResult && upsertInvoiceResult.upserted_invoice_data) {
                upsertInvoiceId = upsertInvoiceResult.upserted_invoice_data.id;
            }
        } catch (error) {
            console.error("Error updating invoice: ", error);
        }

        return upsertInvoiceId;
    };

    upsertAccountingsHasura = async (accountingsInsertObjects: cred_accountings_insert_input[]): Promise<string[]> => {
        const upsertedAccountingsIds: string[] = [];

        try {
            const { data: upsertAccountingsResult } = await apolloClientInstance.mutate<
                UpsertAccountings,
                UpsertAccountingsVariables
            >({
                mutation: UPSERT_ACCOUNTINGS,
                variables: {
                    accountingObjects: accountingsInsertObjects
                }
            });

            if (
                upsertAccountingsResult &&
                upsertAccountingsResult.upserted_accounting_data &&
                upsertAccountingsResult.upserted_accounting_data.returning.length > 0
            ) {
                upsertAccountingsResult.upserted_accounting_data.returning.forEach((accounting) => {
                    if (accounting.id) {
                        upsertedAccountingsIds.push(accounting.id);
                    }
                });
            }
        } catch (error) {
            console.error("Error upserting accountings: ", error);
        }

        return upsertedAccountingsIds;
    };

    deleteAccounting = async (accountingId: string) => {
        try {
            await apolloClientInstance.mutate<DeleteAccounting, DeleteAccountingVariables>({
                mutation: DELETE_ACCOUNTING,
                variables: {
                    accountingid: accountingId
                }
            });
        } catch (error) {
            console.error("Error upserting accountings: ", error);
        }
    };

    getInvoiceInsertInputQueryVariables = async (savingNewInvoice: boolean): Promise<IInvoiceUpsertVariables> => {
        const baseInvoiceVariables: IInvoiceUpsertVariables = {
            origin: savingNewInvoice ? INVOICE_ORIGIN.MANUAL : this.origin,
            type: this.invoiceType,
            date: this.date !== "" ? this.date : null,
            duedate: this.dueDate !== "" ? this.dueDate : null,
            realestateid: this.realestate?.id,
            unitid: this.unit?.unitId,
            incidentid: this.ticket?.id,
            orderid: this.order?.id,
            number: this.number,
            amount: this.amount,
            creditorid: this.creditor?.id,
            workflowinstance: this.workflowInstance,
            creditorinfo: isEmpty(this.creditorInfo) ? undefined : this.creditorInfo,
            insertuserid: this.insertUserId
                ? this.insertUserId
                : this.rootStore.authStore.user?.userid
                  ? this.rootStore.authStore.user?.userid
                  : undefined,
            invoiceinfo: isEmpty(this.invoiceInfo) ? undefined : this.invoiceInfo,
            invoicenumber: this.invoiceNumber,
            paiddate: this.paidDate,
            paymentaccountid: this.paymentaccountid,
            payoutaccountid: this.payoutaccountid,
            paymentinfo: isEmpty(this.paymentInfo) ? undefined : this.paymentInfo,
            paymentpurpose: this.paymentPurpose
        };

        if (this.paymentInfo?.iban || this.paymentAccount?.iban) {
            let paymentInfoType: PAYMENT_INFO_TYPE | undefined = undefined;

            if (this.paymentInfo && this.paymentInfo.iban) {
                paymentInfoType = getPaymentInfoType(this.paymentInfo.iban, this.paymentInfo.reference);
                this.paymentInfo.type = paymentInfoType;
            } else if (this.paymentAccount?.iban && this.paymentInfo) {
                paymentInfoType = getPaymentInfoType(this.paymentAccount.iban, this.paymentInfo.reference);
                this.paymentInfo.type = paymentInfoType;
            }

            baseInvoiceVariables.paymentinfo = {
                reference: this.paymentInfo?.reference,
                iban:
                    this.paymentInfo?.iban && this.paymentInfo.iban !== ""
                        ? this.paymentInfo.iban
                        : this.paymentAccount?.iban,
                type: paymentInfoType
            };
        }

        let invoiceVariables: IInvoiceUpsertVariables = {};

        if (this.isNewInvoice) {
            if (this.aiDetection?.uploadMetadata) {
                // AI flow (pre-invoice): blob was already uploaded by /ai-detect-with-file;
                // reference it. Existing invoices loaded from DB have no uploadMetadata
                // so they never hit this branch.
                const uploadMetadata = this.aiDetection.uploadMetadata;
                invoiceVariables = {
                    ...baseInvoiceVariables,
                    existingfileid: uploadMetadata.fileid,
                    existingfile: {
                        name: uploadMetadata.filename,
                        extension: uploadMetadata.extension,
                        mimetype: uploadMetadata.mimetype,
                        hash: uploadMetadata.hash
                    }
                };
            } else if (this.invoiceFile && this.invoiceFile.base64String && this.invoiceFile.base64String.length > 0) {
                // Legacy / fallback path: upload base64 with the invoice.
                invoiceVariables = {
                    ...baseInvoiceVariables,
                    file: {
                        invoicepdf: this.invoiceFile.base64String,
                        name: this.invoiceFile.name ?? ""
                    }
                };
            } else {
                invoiceVariables = {
                    ...baseInvoiceVariables
                };
            }
        } else {
            // Updating an existing invoice
            invoiceVariables = {
                ...baseInvoiceVariables,
                id: this.id,
                origin: this.origin
            };
        }

        return invoiceVariables;
    };

    getAccountingsInsertInputQueryVariables = async (): Promise<cred_accountings_insert_input[]> => {
        const accountingsInsertObjects: cred_accountings_insert_input[] = [];

        this.accountings?.forEach((accounting: IAccounting) => {
            let vatAmount = undefined;

            if (accounting.netAmount && accounting.amount) {
                vatAmount = Number(accounting.amount) - Number(accounting.netAmount);
            }

            const accountingsInsertObject: cred_accountings_insert_input = {
                id: accounting.id,
                invoiceid: this.id,
                amount: accounting.amount,
                date: accounting.date,
                text: accounting.text,
                accountid: accounting.account?.id,
                costcenterid: accounting.costCenter?.id,
                realestateid: accounting.realestate?.id,
                unitid: accounting.unit?.unitId,
                extracostdate: accounting.extraCostDate,
                quantity: accounting.quantity ?? null, // is set to'null' so that if an entry was already saved with a quantity and then changed, it will be removed
                unitofmeasure: accounting.account?.unitOfMeasure,
                vatcodeid: accounting.vatCodeId,
                vatrate: accounting.vatRate ? Number(accounting.vatRate) : null,
                netamount: accounting.netAmount ? Number(accounting.netAmount) : null,
                vatamount: vatAmount ? Number(vatAmount) : null,
                vatproportion: vatProportionToNumber(accounting.vatProportion)
            };

            accountingsInsertObjects.push(accountingsInsertObject);
        });

        return accountingsInsertObjects;
    };

    reloadHistory = async (): Promise<boolean> => {
        let successfullyReloadedHistory = false;

        try {
            const { data } = await apolloClientInstance.query<GetHistoryOfInvoice, GetHistoryOfInvoiceVariables>({
                query: GET_HISTORY_OF_INVOICE,
                fetchPolicy: "no-cache",
                variables: {
                    invoiceId: this.id
                }
            });

            const history = this.rootStore.invoiceStore.getHistoryObjectFromQueryData(data.cred_v_invoice_history);

            this.updateProperty(INVOICE_PROPERTY.HISTORY, history);

            successfullyReloadedHistory = true;
        } catch (error) {
            console.error("Error reloading history: ", error);
        }

        return successfullyReloadedHistory;
    };

    refreshInvoiceSearchVector = async (): Promise<boolean> => {
        let success = false;

        try {
            const { data: refreshInvoiceSearchVectorResult } = await apolloClientInstance.mutate<
                RefreshInvoiceSearchVector,
                RefreshInvoiceSearchVectorVariables
            >({
                mutation: REFRESH_INVOICE_SEARCH_VECTOR,
                variables: {
                    invoiceid: this.id,
                    customerid: this.rootStore.authStore.user?.customerid
                }
            });

            if (
                refreshInvoiceSearchVectorResult &&
                refreshInvoiceSearchVectorResult.cred_refresh_invoice_search_vector &&
                refreshInvoiceSearchVectorResult.cred_refresh_invoice_search_vector.length > 0 &&
                refreshInvoiceSearchVectorResult.cred_refresh_invoice_search_vector[0].success === true
            ) {
                success = true;
            }
        } catch (error) {
            console.error("Error refreshing invoice search vector: ", error);
        }

        return success;
    };

    getNextState = (): INVOICE_STATE | undefined => {
        let nextState: INVOICE_STATE | undefined = undefined;

        const navStore = this.rootStore.navStore;

        const numberOfVisas =
            navStore.customer.settings &&
            navStore.customer.settings.cred &&
            navStore.customer.settings.cred.numberOfVisas
                ? navStore.customer.settings.cred.numberOfVisas
                : 0;

        switch (this.state) {
            case INVOICE_STATE.NEW:
                nextState = INVOICE_STATE.ASSIGN_WORKFLOW_USERS;
                break;
            case INVOICE_STATE.ASSIGN_WORKFLOW_USERS:
                nextState = INVOICE_STATE.READY_FOR_VISA;
                break;
            case INVOICE_STATE.READY_FOR_VISA:
                nextState = numberOfVisas > 0 ? INVOICE_STATE.VISA_1_OK : INVOICE_STATE.FULLY_SIGHTED;
                break;
            case INVOICE_STATE.VISA_1_OK:
                nextState = numberOfVisas >= 2 ? INVOICE_STATE.VISA_2_OK : INVOICE_STATE.FULLY_SIGHTED;
                break;
            case INVOICE_STATE.VISA_2_OK:
                nextState = numberOfVisas >= 3 ? INVOICE_STATE.VISA_3_OK : INVOICE_STATE.FULLY_SIGHTED;
                break;
            case INVOICE_STATE.VISA_3_OK:
                nextState = INVOICE_STATE.FULLY_SIGHTED;
                break;
            case INVOICE_STATE.FULLY_SIGHTED:
                nextState = INVOICE_STATE.SENT_TO_ERP;
                break;
            case INVOICE_STATE.SENT_TO_ERP:
                nextState = INVOICE_STATE.IMPORTED_IN_ERP;
                break;
            case INVOICE_STATE.IMPORTED_IN_ERP:
                nextState = INVOICE_STATE.PAID;
                break;
            case INVOICE_STATE.PAID:
            case INVOICE_STATE.REJECTED:
            case INVOICE_STATE.CANCELLED:
                nextState = undefined; // No next state
                break;
            default:
                nextState = undefined;
                break;
        }

        return nextState;
    };

    saveInvoiceImage = async (image: IInvoiceFile, invoiceId: string): Promise<string | undefined> => {
        let newFileId = undefined;

        try {
            const requestBody = {
                version: "2.0",
                id: image.fileId,
                origin: INVOICE_ORIGIN.MANUAL,
                mimetype: image.mimetype,
                extension: image.extension,
                name: image.name,
                filedate: image.fileDate,
                file: image.base64String,
                links: [
                    {
                        id: image.invoiceFileId,
                        entityid: invoiceId,
                        entity: "cred.invoices",
                        roleid: getRoleId(Role.MANAGER),
                        visible: true,
                        type: image.type
                    }
                ]
            };

            const response = await Axios.getAuthInstance().put(NetworkConfig.uploadFileUrl, requestBody, {
                responseType: "text",
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });
            newFileId = response.data;
        } catch (error) {
            console.error("Error occured while trying to upload image: ", error, image);
        }

        return newFileId;
    };

    saveInvoiceAttachmentFiles = async (invoiceId: string): Promise<void> => {
        if (
            this.invoiceAttachmentFiles &&
            this.invoiceAttachmentFiles.length > 0 &&
            this.hasNewInvoiceAttachmentFiles
        ) {
            await Promise.all(
                this.invoiceAttachmentFiles.map(async (file) => {
                    if (file.state === FILE_STATE.NEW) {
                        const newFileId = await this.rootStore.fileStore.uploadFileBulk(
                            file,
                            invoiceId,
                            FILE_ENTITY.CRED_INVOICES
                        );

                        if (newFileId) {
                            runInAction(() => {
                                file.state = FILE_STATE.SAVED_TO_DB;
                            });
                        }
                    }
                })
            );
        }
    };

    deleteFile = async (fileId?: string): Promise<boolean> => {
        let success = false;
        if (fileId) {
            try {
                const { data: deleteFileResult } = await apolloClientInstance.mutate<DeleteFile, DeleteFileVariables>({
                    mutation: DELETE_FILE,
                    variables: {
                        fileid: fileId
                    }
                });

                if (
                    deleteFileResult &&
                    deleteFileResult?.deleted_files &&
                    deleteFileResult?.deleted_files?.affected_rows > 0
                ) {
                    success = true;
                }
            } catch (error) {
                console.error("error deleting file from db: ", error);
            } finally {
                return success;
            }
        }

        return success;
    };

    deleteAttachmentFile = async (fileId?: string): Promise<void> => {
        const attachmentFileToDelete = this.invoiceAttachmentFiles?.find((file) => file.fileId === fileId);

        if (attachmentFileToDelete) {
            this.invoiceAttachmentFiles = this.invoiceAttachmentFiles?.filter((file) => file.fileId !== fileId);

            if (
                attachmentFileToDelete.state === FILE_STATE.LOADED_FROM_DB ||
                attachmentFileToDelete.state === FILE_STATE.SAVED_TO_DB
            ) {
                await this.deleteFile(fileId);
            }
        }
    };

    setInvoiceFileStateDeleted = async (fileId?: string): Promise<boolean> => {
        let success = false;
        if (fileId && this.id) {
            try {
                const { data: setInvoiceFileStateDeletedResult } = await apolloClientInstance.mutate<
                    SetInvoiceFileStateDeleted,
                    SetInvoiceFileStateDeletedVariables
                >({
                    mutation: SET_INVOICE_FILE_STATE_DELETED,
                    variables: {
                        fileId: fileId,
                        invoiceId: this.id
                    }
                });

                if (
                    setInvoiceFileStateDeletedResult &&
                    setInvoiceFileStateDeletedResult?.update_file_invoicefiles &&
                    setInvoiceFileStateDeletedResult?.update_file_invoicefiles?.returning.length > 0
                ) {
                    success = true;
                }
            } catch (error) {
                console.error("error setting invoice file state to deleted: ", error);
            } finally {
                return success;
            }
        }

        return success;
    };

    startInvoiceWorkflowUsersSubscription() {
        if (this.id && this.id !== "" && this.realestate?.id) {
            this.rootStore.invoiceStore.setRunningSubscriptionType(RUNNING_SUBSCRIPTION_TPYE.WORKFLOW_USERS);

            if (this.invoiceWorkflowUsersSubscription) {
                this.stopInvoiceWorkflowUsersSubscription();
            }

            const invoiceWorkflowUsersSubscription = apolloClientInstance
                .subscribe<SubscribeToInvoiceWorkflowUserUpdates, SubscribeToInvoiceWorkflowUserUpdatesVariables>({
                    query: SUBSCRIBE_TO_INVOICE_WORKFLOWUSER_UPDATES,
                    variables: { invoiceid: this.id }
                })
                .subscribe({
                    next: ({ data }) => {
                        if (data?.workflowusers && data.workflowusers.length > 0) {
                            this.onInvoiceWorkflowUsersDataReceived(data.workflowusers);
                        }
                    },
                    error: () => {
                        // Error is logged by Apollo errorLink
                        this.stopInvoiceWorkflowUsersSubscription();
                    }
                });

            this.setInvoiceWorkflowUsersSubscription(invoiceWorkflowUsersSubscription);
        }
    }

    stopInvoiceWorkflowUsersSubscription() {
        this.rootStore.invoiceStore.setRunningSubscriptionType(RUNNING_SUBSCRIPTION_TPYE.NONE);

        if (this.invoiceWorkflowUsersSubscription) {
            this.invoiceWorkflowUsersSubscription.unsubscribe();
            this.setInvoiceWorkflowUsersSubscription(undefined);
        }
    }

    onInvoiceWorkflowUsersDataReceived = (workflowusers: SubscribeToInvoiceWorkflowUserUpdates_workflowusers[]) => {
        const newWorkflowUsers: IWorkflowUser[] = [];

        const hasReceivedWorkflowUsers = workflowusers.length > 0;

        // In the initial call to the subscription it's possible that no workflow users are returned
        if (!hasReceivedWorkflowUsers) {
            return;
        }

        workflowusers.forEach((workflowUser) => {
            const workflowUserAlreadyExists =
                this.workflowUsers?.find((user) => user.role === workflowUser.role) !== undefined;

            if (!workflowUserAlreadyExists) {
                const newWorkflowUser: IWorkflowUser = {
                    id: workflowUser.id,
                    role: workflowUser.role as WORKFLOWUSER_ROLE,
                    signaturestamp: workflowUser.signaturestamp,
                    user: {
                        id: workflowUser.user.id,
                        name1: workflowUser.user.name1,
                        name2: workflowUser.user.name2 ?? ""
                    }
                };

                newWorkflowUsers.push(newWorkflowUser);
            }
        });

        const newWorkflowUserArray = [...(this.workflowUsers ?? []), ...newWorkflowUsers];
        this.updateProperty(INVOICE_PROPERTY.WORKFLOW_USERS, newWorkflowUserArray);

        this.resetAllFieldsWithChanges();

        // Stop the subscription if the invoice is ready for visa - no more workflowusers will come
        if (this.state === INVOICE_STATE.READY_FOR_VISA) {
            this.stopInvoiceWorkflowUsersSubscription();
        }
    };

    setInvoiceWorkflowUsersSubscription = (invoiceWorkflowUsersSubscription: Subscription | undefined) => {
        this.invoiceWorkflowUsersSubscription = invoiceWorkflowUsersSubscription;
    };

    /* IS WORKFLOW PROCESSING SUBSCRIPTION */

    startInvoiceIsWorkflowProcessingSubscription() {
        if (this.invoiceIsWorkflowProcessingSubscription) {
            this.stopInvoiceIsWorkflowProcessingSubscription();
        }

        const invoiceIsWorkflowProcessingSubscription = apolloClientInstance
            .subscribe<SubscribeToInvoiceIsProcessingUpdate, SubscribeToInvoiceIsProcessingUpdateVariables>({
                query: SUBSCRIBE_TO_INVOICE_IS_PROCESSING_UPDATE,
                variables: { invoiceid: this.id }
            })
            .subscribe({
                next: ({ data }) => {
                    if (data?.invoice && data.invoice.length > 0) {
                        this.onInvoiceIsWorkflowProcessingDataReceived(data.invoice[0]);
                    }
                },
                error: () => {
                    // Error is logged by Apollo errorLink
                    this.stopInvoiceStateUpdateSubscription();
                }
            });

        this.setInvoiceIsWorkflowProcessingSubscription(invoiceIsWorkflowProcessingSubscription);
    }

    stopInvoiceIsWorkflowProcessingSubscription() {
        this.rootStore.invoiceStore.setRunningSubscriptionType(RUNNING_SUBSCRIPTION_TPYE.NONE);

        if (this.invoiceIsWorkflowProcessingSubscription) {
            this.invoiceIsWorkflowProcessingSubscription.unsubscribe();
            this.setInvoiceIsWorkflowProcessingSubscription(undefined);
        }
    }

    onInvoiceIsWorkflowProcessingDataReceived = (invoice: SubscribeToInvoiceIsProcessingUpdate_invoice) => {
        const newState = invoice.state;
        const isProcessing = invoice.isprocessing;

        if (isProcessing !== null && isProcessing !== undefined) {
            this.updateProperty(INVOICE_PROPERTY.IS_PROCESSING, isProcessing);
        }

        if (newState >= INVOICE_STATE.PAID) {
            this.stopInvoiceIsWorkflowProcessingSubscription();
        }
    };

    setInvoiceIsWorkflowProcessingSubscription = (
        invoiceIsWorkflowProcessingSubscription: Subscription | undefined
    ) => {
        this.invoiceIsWorkflowProcessingSubscription = invoiceIsWorkflowProcessingSubscription;
    };

    /* ACCOUNTING (KONTIERUNG) */

    resetAccountings = (keepBookingDate: boolean = false) => {
        this.setDefaultAccountingRealestate(undefined);
        const bookingDate = this.accountings && this.accountings[0].date;

        this.accountings?.forEach((accounting) => {
            if (accounting.id) {
                this.accountingIdsToDelete.push(accounting.id);
            }
        });

        if (keepBookingDate) {
            this.accountings = [];
            this.addAccountingRow();

            this.accountings[0].date = bookingDate;
        } else {
            this.accountings = [];
            this.addAccountingRow();
        }
    };

    initializeDefaultAccountingVariables = () => {
        this.defaultAccountingDate = this.getDefaultAccountingDate();
        this.defaultAccountingRealestate = this.getDefaultAccountingRealestate();

        this.defaultAccountingBookingText = this.getDefaultAccountingBookingText() ?? "";
        this.defaultAccountingAmount = this.amount?.toFixed(2) ?? "0";
    };

    initializeDefaultAccountingObjects = () => {
        this.initializeDefaultAccountingObjectForDate();
        this.initializeDefaultAccountingObjectForRealestate();
        this.initializeDefaultAccountingObjectForUnit();
        this.initializeDefaultAccountingObjectForBookingText();
        this.initializeDefaultAccountingObjectForAmount();
    };

    initializeDefaultAccountingObjectForRealestate = () => {
        const erpType = this.rootStore.authStore.user?.erpType;

        this.defaultAccountingRealestate = this.getDefaultAccountingRealestate();

        // This is only valid for IT2 because for IT2 there is only 1 field where the realestate can be set. Every accounting entry will then have the same realestate
        // So in case the ERP is IT2 and the realestate was never set (no accounting exists) a default realestate 'suggestion' will be set
        if (erpType === ErpType.IT2 && this.defaultAccountingRealestate) {
            if (this.accountings && this.accountings.length === 1) {
                this.rootStore.invoiceStore.updateAccountingWithSelectedRealestate(
                    this.defaultAccountingRealestate,
                    false,
                    0
                );
            }
        }

        if (erpType === ErpType.RIMO && this.defaultAccountingRealestate) {
            if (this.accountings) {
                const lastAccountingIndex = this.accountings.length - 1;

                this.rootStore.invoiceStore.updateAccountingWithSelectedRealestate(
                    this.defaultAccountingRealestate,
                    false,
                    lastAccountingIndex,
                    true // CAREFUL
                );
            } else {
                this.accountings = [this.getDefaultAccountingObject()];

                this.rootStore.invoiceStore.updateAccountingWithSelectedRealestate(
                    this.defaultAccountingRealestate,
                    false,
                    0,
                    true // CAREFUL
                );
            }
        }
    };

    initializeDefaultAccountingObjectForUnit = () => {
        const erpType = this.rootStore.authStore.user?.erpType;

        this.defaultAccountingRealestate = this.getDefaultAccountingRealestate();

        if (erpType === ErpType.RIMO && this.defaultAccountingRealestate && this.unit) {
            if (this.accountings) {
                const lastAccountingIndex = this.accountings.length - 1;

                this.rootStore.invoiceStore.updateAccountingWithSelectedUnit(this.unit, false, lastAccountingIndex);
            } else {
                this.accountings = [this.getDefaultAccountingObject()];

                this.rootStore.invoiceStore.updateAccountingWithSelectedUnit(this.unit, false, 0);
            }
        }
    };

    initializeDefaultAccountingObjectForBookingText = () => {
        this.defaultAccountingBookingText = this.getDefaultAccountingBookingText() ?? "";
        if (this.accountings) {
            const lastAccountingIndex = this.accountings.length - 1;

            this.rootStore.invoiceStore.updateAccountingWithBookingText(
                this.defaultAccountingBookingText,
                lastAccountingIndex
            );
        } else {
            this.accountings = [this.getDefaultAccountingObject()];

            this.rootStore.invoiceStore.updateAccountingWithBookingText(this.defaultAccountingBookingText, 0);
        }
    };

    initializeDefaultAccountingObjectForAmount = () => {
        this.defaultAccountingAmount = this.amount?.toFixed(2) ?? "0";

        if (this.accountings) {
            const lastAccountingIndex = this.accountings.length - 1;

            this.rootStore.invoiceStore.currentInvoice?.setAccountingAmount(
                this.defaultAccountingAmount,
                lastAccountingIndex
            );
        } else {
            this.accountings = [this.getDefaultAccountingObject()];

            this.rootStore.invoiceStore.currentInvoice?.setAccountingAmount(this.defaultAccountingAmount, 0);
        }
    };

    initializeDefaultAccountingObjectForDate = () => {
        this.defaultAccountingDate = this.getDefaultAccountingDate();

        if (this.accountings) {
            this.setAccountingDate(this.defaultAccountingDate);
        } else {
            this.accountings = [this.getDefaultAccountingObject()];

            this.setAccountingDate(this.defaultAccountingDate);
        }
    };

    addAccountingRow = () => {
        if (this.accountings) {
            const newAccountingObject = this.getDefaultAccountingObject();

            if (this.accountingDate) {
                newAccountingObject.date = this.accountingDate;
            }

            const calculatedAmount = (Number(this.amount) - this.totalAccountingAmount).toFixed(2);
            newAccountingObject.amount = calculatedAmount;

            this.accountings.push(newAccountingObject);

            // Extend display arrays with proper initial values
            const formattedAmount = formatAmountNumberToCurrencyString(parseFloat(calculatedAmount));
            this.displayedAmounts.push(formattedAmount);
            this.displayedNetAmounts.push("");
            this.focusedAmountFields.push(false);
            this.focusedNetAmountFields.push(false);

            if (this.defaultAccountingRealestate) {
                this.rootStore.invoiceStore.updateAccountingWithSelectedRealestate(
                    this.defaultAccountingRealestate,
                    false,
                    this.accountings.length - 1,
                    true // CAREFUL
                );
            }

            if (this.defaultAccountingRealestate?.id === this.realestate?.id && this.unit) {
                this.rootStore.invoiceStore.updateAccountingWithSelectedUnit(
                    this.unit,
                    false,
                    this.accountings.length - 1
                );
            }

            if (this.defaultAccountingBookingText) {
                this.rootStore.invoiceStore.updateAccountingWithBookingText(
                    this.defaultAccountingBookingText,
                    this.accountings.length - 1
                );
            }

            this.rootStore.accountingAccountSearchStore.setTriggerFocusAccountInput(true, this.accountings.length - 1);
        }
    };

    getDefaultAccountingRealestate = (): IRealestate | undefined => {
        let defaultAccountingRealestate: IRealestate | undefined = undefined;

        const erpType = this.rootStore.authStore.user?.erpType;

        if (!this.realestate) {
            return undefined;
        }

        if (erpType === ErpType.IT2) {
            const isConsolidation = this.realestate?.consolidation;
            if (isConsolidation) {
                defaultAccountingRealestate = this.realestate;
            } else {
                const consolidationRealestate = this.rootStore.realestateSearchStore.realestateData.find(
                    (realestate) => {
                        return realestate.id === this.realestate?.consolidationId;
                    }
                );

                if (consolidationRealestate) {
                    defaultAccountingRealestate = consolidationRealestate;
                }
            }
        } else if (erpType === ErpType.RIMO) {
            defaultAccountingRealestate = this.realestate;
        }

        return defaultAccountingRealestate;
    };

    getDefaultAccountingUnit = (): IUnitData | undefined => {
        return this.unit;
    };

    getDefaultAccountingRealestateId = (): string | undefined => {
        let defaultAccountingRealestateId: string | undefined = undefined;

        const defaultAccountingRealestate = this.getDefaultAccountingRealestate();

        if (defaultAccountingRealestate) {
            defaultAccountingRealestateId = defaultAccountingRealestate.id;
        }

        return defaultAccountingRealestateId;
    };

    getDefaultAccountingRealestateByRealestate = (searchRealestate: IRealestate): IRealestate | undefined => {
        let defaultAccountingRealestate: IRealestate | undefined = undefined;

        const erpType = this.rootStore.authStore.user?.erpType;

        if (erpType === ErpType.IT2) {
            const isConsolidation = searchRealestate?.consolidation;
            if (isConsolidation) {
                defaultAccountingRealestate = searchRealestate;
            } else {
                const consolidationRealestate = this.rootStore.realestateSearchStore.realestateData.find(
                    (realestate) => {
                        return realestate.id === searchRealestate?.consolidationId;
                    }
                );

                if (consolidationRealestate) {
                    defaultAccountingRealestate = consolidationRealestate;
                }
            }
        } else if (erpType === ErpType.RIMO) {
            defaultAccountingRealestate = this.realestate;
        }

        return defaultAccountingRealestate;
    };

    loadPayoutAccountDataAndSetIfNeeded = async (realestateId: string) => {
        const payoutAccountStore = this.rootStore.accountingPayoutAccountStore;
        await payoutAccountStore.loadPayoutAccountData(realestateId);

        if (payoutAccountStore.selectablePayoutAccountDataIT2) {
            if (payoutAccountStore.selectablePayoutAccountDataIT2.length === 1) {
                this.rootStore.invoiceStore.updateInvoiceWithSelectedPayoutAccountIT2(
                    payoutAccountStore.selectablePayoutAccountDataIT2[0]
                );

                this.setManualSaveOfInvoiceIsNeeded(true);
            } else if (payoutAccountStore.selectablePayoutAccountDataIT2.length > 1) {
                const defaultPayoutAccount = payoutAccountStore.getDefaultPayoutAccount();

                if (defaultPayoutAccount) {
                    this.rootStore.invoiceStore.updateInvoiceWithSelectedPayoutAccountIT2(defaultPayoutAccount);

                    this.setManualSaveOfInvoiceIsNeeded(true);
                }
            }
        }
    };

    getDefaultAccountingBookingText = (): string | undefined => {
        let defaultAccountingBookingText: string | undefined = undefined;

        if (!this.creditor) {
            return undefined;
        }

        if (this.creditor && this.creditor.name) {
            defaultAccountingBookingText = this.creditor.name;
        }

        return defaultAccountingBookingText;
    };

    getDefaultAccountingDate = (): string => {
        let defaultAccountingDate: string = "";

        if (!this.date) {
            // if invoice date is not set, use current date
            defaultAccountingDate = getDateStringForFormElement(Date());
        } else {
            defaultAccountingDate = this.date;
        }

        return defaultAccountingDate;
    };

    /**
     * The proposed booking date is the invoice date, but it is only computed when the invoice is opened. An invoice
     * date entered afterwards (typed, or filled from QR/AI) has to carry over to the booking date - as long as the
     * accountings are not saved yet and the user has not chosen a booking date of their own (#26283).
     */
    followInvoiceDateWithBookingDate = (newInvoiceDate: string | undefined) => {
        if (!newInvoiceDate || !/^\d{4}-\d{2}-\d{2}$/.test(newInvoiceDate) || isNaN(Date.parse(newInvoiceDate))) {
            return;
        }

        // the originals always hold at least one empty default row; saved accountings carry a booking date
        const hasSavedAccountings = this.invoiceFieldsOriginal?.accountings?.some((accounting) => accounting.date) ?? false;
        const isBookingDateUntouched = !this.accountingDate || this.accountingDate === this.defaultAccountingDate;

        if (hasSavedAccountings || !isBookingDateUntouched) {
            return;
        }

        this.defaultAccountingDate = newInvoiceDate;
        this.setAccountingDate(newInvoiceDate);
    };

    removeAccountingRow = (index: number) => {
        if (this.accountings) {
            const removedAccountings = this.accountings.splice(index, 1);

            // Remove from display arrays
            this.displayedAmounts.splice(index, 1);
            this.displayedNetAmounts.splice(index, 1);
            this.focusedAmountFields.splice(index, 1);
            this.focusedNetAmountFields.splice(index, 1);

            this.removeAccountingRowFromSearchStores(index);

            removedAccountings.forEach((accounting) => {
                if (accounting.id) {
                    this.accountingIdsToDelete.push(accounting.id);
                }
            });
        }
    };

    /*  The per row values of the accounting search stores have to be removed and not just cleared,
        otherwise the values of all following accounting rows stay attached to their previous row index.
    */
    removeAccountingRowFromSearchStores = (accountingRowIndex: number) => {
        this.rootStore.accountingAccountSearchStore.removeAccountingRow(accountingRowIndex);
        this.rootStore.accountingCostcenterStore.removeAccountingRow(accountingRowIndex);
        this.rootStore.accountingVatStore.removeAccountingRow(accountingRowIndex);
        this.rootStore.accountingRealestateSearchStore.removeAccountingRow(accountingRowIndex);
        this.rootStore.accountingUnitSearchStore.removeAccountingRow(accountingRowIndex);
        this.rootStore.accountingBookingTextStore.removeAccountingRow(accountingRowIndex);
    };

    getAccountingRealestate = (accountingIndex: number): IRealestate | undefined => {
        const accountingRealestate: IRealestate | undefined =
            this.accountings && this.accountings[accountingIndex]
                ? this.accountings[accountingIndex].realestate
                : undefined;
        return accountingRealestate;
    };

    getAccountingAccount = (accountingIndex: number): IAccount | undefined => {
        const accountingAccount: IAccount | undefined =
            this.accountings && this.accountings[accountingIndex]
                ? this.accountings[accountingIndex].account
                : undefined;
        return accountingAccount;
    };

    getAccountingCostcenter = (accountingIndex: number): ICostCenter | undefined => {
        const accountingCostcenter: ICostCenter | undefined =
            this.accountings && this.accountings[accountingIndex]
                ? this.accountings[accountingIndex].costCenter
                : undefined;

        return accountingCostcenter;
    };

    getAccountingBookingText = (accountingIndex: number): string | undefined => {
        const accountingBookingText: string | undefined =
            this.accountings && this.accountings[accountingIndex] ? this.accountings[accountingIndex].text : undefined;

        return accountingBookingText;
    };

    getAccountingQuantity = (accountingIndex: number): string | undefined => {
        const accountingBookingQuantity: string | undefined =
            this.accountings && this.accountings[accountingIndex]
                ? this.accountings[accountingIndex].quantity
                : undefined;

        return accountingBookingQuantity;
    };

    getAccountingExtraCostDate = (accountingIndex: number): string | undefined => {
        const accountingExtraCostDate: string | undefined =
            this.accountings && this.accountings[accountingIndex]
                ? this.accountings[accountingIndex].extraCostDate
                : undefined;

        return accountingExtraCostDate;
    };

    getAccountingAmount = (accountingIndex: number): string | undefined => {
        const accountingBookingAmount: string | undefined =
            this.accountings && this.accountings[accountingIndex]
                ? this.accountings[accountingIndex].amount
                : undefined;

        return accountingBookingAmount;
    };

    getAccountingNetAmount = (accountingIndex: number): string | undefined => {
        const accountingNetAmount: string | undefined =
            this.accountings && this.accountings[accountingIndex]
                ? this.accountings[accountingIndex].netAmount
                : undefined;

        return accountingNetAmount;
    };

    getAccountingVatAmount = (accountingIndex: number): string | undefined => {
        const accountingVatAmount: string | undefined =
            this.accountings && this.accountings[accountingIndex]
                ? this.accountings[accountingIndex].vatAmount
                : undefined;

        return accountingVatAmount;
    };

    getAccountingDate = (accountingIndex: number): string | undefined => {
        const accountingDate: string | undefined =
            this.accountings && this.accountings[accountingIndex] ? this.accountings[accountingIndex].date : undefined;
        return accountingDate;
    };

    getAccountingVatCodeId = (accountingIndex: number): string | undefined => {
        const accountingVatCodeId =
            this.accountings !== undefined &&
            this.accountings[accountingIndex] &&
            this.accountings[accountingIndex].vatCodeId
                ? this.accountings[accountingIndex].vatCodeId
                : undefined;

        return accountingVatCodeId;
    };

    getAccountingVatRate = (accountingIndex: number): string | undefined => {
        const accountingVatRate =
            this.accountings !== undefined &&
            this.accountings[accountingIndex] &&
            this.accountings[accountingIndex].vatRate
                ? this.accountings[accountingIndex].vatRate
                : undefined;

        return accountingVatRate;
    };

    getAccountingVatProportion = (accountingIndex: number): string | undefined => {
        return this.accountings?.[accountingIndex]?.vatProportion;
    };

    getAccountingVatRateType = (accountingIndex: number): VAT_RATE_TYPE | undefined => {
        const selectedVatCodeVatRateType =
            this.rootStore.accountingVatStore.getSelectedVatCodeVatRateType(accountingIndex);

        return selectedVatCodeVatRateType;
    };

    setAccountingId = (id: string, accountingIndex: number) => {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].id = id;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].id = id;
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    };

    // An invoice can have multiple accountings but all of them will have the same date
    setAccountingDate = (date: string) => {
        if (this.accountings) {
            this.accountings.forEach((accounting, index) => {
                accounting.date = date;
                this.updateFieldsWithChangesAccounting(index, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
            });
        }
    };

    setAccountingRealestate = (realestate: IRealestate, accountingIndex?: number) => {
        if (this.rootStore.authStore.user?.erpType === ErpType.IT2) {
            if (this.accountings) {
                this.accountings.forEach((accounting) => {
                    accounting.realestate = realestate;
                });
            }
        } else if (this.rootStore.authStore.user?.erpType === ErpType.RIMO) {
            if (this.accountings && accountingIndex !== undefined) {
                if (this.accountings[accountingIndex]) {
                    this.accountings[accountingIndex].realestate = realestate;
                } else {
                    this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                    this.accountings[accountingIndex].realestate = realestate;
                }
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex ?? 0, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    };

    setAccountingUnit = (unit: IUnitData | undefined, accountingIndex?: number) => {
        if (this.rootStore.authStore.user?.erpType === ErpType.IT2) {
            if (this.accountings) {
                this.accountings.forEach((accounting) => {
                    accounting.unit = unit;
                });
            }
        } else if (this.rootStore.authStore.user?.erpType === ErpType.RIMO) {
            if (this.accountings && accountingIndex !== undefined) {
                if (this.accountings[accountingIndex]) {
                    this.accountings[accountingIndex].unit = unit;
                } else {
                    this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                    this.accountings[accountingIndex].unit = unit;
                }
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex ?? 0, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    };

    setAccountingAccount = (account: IAccount | undefined, accountingIndex: number) => {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].account = account;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].account = account;
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    };

    setIsMissingRequiredCostCenterData = (setIsMissingRequiredCostCenterData: boolean, accountingIndex: number) => {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].isMissingRequiredCostCenterData = setIsMissingRequiredCostCenterData;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].isMissingRequiredCostCenterData = setIsMissingRequiredCostCenterData;
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    };

    setAccountingCostcenter = (costCenter: ICostCenter | undefined, accountingIndex: number) => {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].costCenter = costCenter;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].costCenter = costCenter;
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    };

    setAccountingBookingText(bookingText: string, accountingIndex: number) {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].text = bookingText;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].text = bookingText;
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    }

    hasBookingTextNewline = (accountingIndex: number): boolean => {
        let hasBookingTextNewline = false;

        if (this.accountings && accountingIndex !== undefined) {
            const bookingText = this.accountings[accountingIndex].text;
            if (bookingText) {
                hasBookingTextNewline = bookingText.includes("\n");
            }
        }

        return hasBookingTextNewline;
    };

    setAccountingQuantity(quantity: string | undefined, accountingIndex: number) {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].quantity = quantity;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].quantity = quantity;
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    }

    setAccountingExtraCostDate(extraCostDate: string | undefined, accountingIndex: number) {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].extraCostDate = extraCostDate;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].extraCostDate = extraCostDate;
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    }

    initializeDisplayAmounts = () => {
        if (this.accountings) {
            this.displayedAmounts = this.accountings.map((accounting, index) => {
                if (accounting.amount) {
                    return this.focusedAmountFields[index]
                        ? accounting.amount
                        : formatAmountNumberToCurrencyString(parseFloat(accounting.amount));
                }
                return "";
            });

            this.displayedNetAmounts = this.accountings.map((accounting, index) => {
                if (accounting.netAmount) {
                    return this.focusedNetAmountFields[index]
                        ? accounting.netAmount
                        : formatAmountNumberToCurrencyString(parseFloat(accounting.netAmount));
                }
                return "";
            });

            // Initialize focus states
            this.focusedAmountFields = new Array(this.accountings.length).fill(false);
            this.focusedNetAmountFields = new Array(this.accountings.length).fill(false);
        }
    };

    setDisplayedAmount = (amount: string, accountingIndex: number) => {
        if (!this.displayedAmounts[accountingIndex] && this.displayedAmounts.length <= accountingIndex) {
            // Extend array if needed
            this.displayedAmounts = [
                ...this.displayedAmounts,
                ...new Array(accountingIndex - this.displayedAmounts.length + 1).fill("")
            ];
        }
        this.displayedAmounts[accountingIndex] = amount;
    };

    setDisplayedNetAmount = (netAmount: string, accountingIndex: number) => {
        if (!this.displayedNetAmounts[accountingIndex] && this.displayedNetAmounts.length <= accountingIndex) {
            // Extend array if needed
            this.displayedNetAmounts = [
                ...this.displayedNetAmounts,
                ...new Array(accountingIndex - this.displayedNetAmounts.length + 1).fill("")
            ];
        }
        this.displayedNetAmounts[accountingIndex] = netAmount;
    };

    setAmountFieldFocused = (focused: boolean, accountingIndex: number) => {
        if (!this.focusedAmountFields[accountingIndex] && this.focusedAmountFields.length <= accountingIndex) {
            // Extend array if needed
            this.focusedAmountFields = [
                ...this.focusedAmountFields,
                ...new Array(accountingIndex - this.focusedAmountFields.length + 1).fill(false)
            ];
        }
        this.focusedAmountFields[accountingIndex] = focused;

        // Update displayed amount based on focus state
        const storeAmount = this.getAccountingAmount(accountingIndex);
        if (storeAmount) {
            if (focused) {
                // Show raw value for editing
                this.setDisplayedAmount(storeAmount, accountingIndex);
            } else {
                // Show formatted value
                const numericValue = parseFloat(storeAmount);
                this.setDisplayedAmount(formatAmountNumberToCurrencyString(numericValue), accountingIndex);
            }
        } else {
            this.setDisplayedAmount("", accountingIndex);
        }
    };

    setNetAmountFieldFocused = (focused: boolean, accountingIndex: number) => {
        if (!this.focusedNetAmountFields[accountingIndex] && this.focusedNetAmountFields.length <= accountingIndex) {
            // Extend array if needed
            this.focusedNetAmountFields = [
                ...this.focusedNetAmountFields,
                ...new Array(accountingIndex - this.focusedNetAmountFields.length + 1).fill(false)
            ];
        }
        this.focusedNetAmountFields[accountingIndex] = focused;

        // Update displayed net amount based on focus state
        const storeNetAmount = this.getAccountingNetAmount(accountingIndex);
        if (storeNetAmount) {
            if (focused) {
                // Show raw value for editing
                this.setDisplayedNetAmount(storeNetAmount, accountingIndex);
            } else {
                // Show formatted value
                const numericValue = parseFloat(storeNetAmount);
                this.setDisplayedNetAmount(formatAmountNumberToCurrencyString(numericValue), accountingIndex);
            }
        } else {
            this.setDisplayedNetAmount("", accountingIndex);
        }
    };

    setAccountingAmount(amount: string, accountingIndex: number) {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].amount = amount;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].amount = amount;
            }
        }

        // Update displayed amount if not focused (to reflect changes from net amount calculations)
        if (!this.focusedAmountFields[accountingIndex]) {
            if (amount) {
                const numericValue = parseFloat(amount);
                this.setDisplayedAmount(formatAmountNumberToCurrencyString(numericValue), accountingIndex);
            } else {
                this.setDisplayedAmount("", accountingIndex);
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    }

    setAccountingNetAmount(netAmount: string, accountingIndex: number) {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].netAmount = netAmount;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].netAmount = netAmount;
            }
        }

        // Update displayed net amount if not focused (to reflect changes from amount calculations)
        if (!this.focusedNetAmountFields[accountingIndex]) {
            if (netAmount) {
                const numericValue = parseFloat(netAmount);
                this.setDisplayedNetAmount(formatAmountNumberToCurrencyString(numericValue), accountingIndex);
            } else {
                this.setDisplayedNetAmount("", accountingIndex);
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    }

    setAccountingVatAmount(vatAmount: string, accountingIndex: number) {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].vatAmount = vatAmount;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].vatAmount = vatAmount;
            }
        }
        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    }

    setAccountingVatCode(vatCodeId: string | undefined, accountingIndex: number) {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].vatCodeId = vatCodeId;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].vatCodeId = vatCodeId;
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    }

    setAccountingVatRate(vatRate: string | undefined, accountingIndex: number) {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].vatRate = vatRate;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].vatRate = vatRate;
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    }

    setAccountingVatProportion(vatProportion: string | undefined, accountingIndex: number) {
        if (this.accountings && accountingIndex !== undefined) {
            if (this.accountings[accountingIndex]) {
                this.accountings[accountingIndex].vatProportion = vatProportion;
            } else {
                this.accountings[accountingIndex] = this.getDefaultAccountingObject();
                this.accountings[accountingIndex].vatProportion = vatProportion;
            }
        }

        this.updateFieldsWithChangesAccounting(accountingIndex, INVOICE_ACCOUNTING_FIELD_TYPE.GENERAL);
    }

    isAccountingRealestateSelected = (accountingRowIndex?: number): boolean => {
        let isAccountingRealestateSelected = false;
        if (this.rootStore.authStore.user?.erpType === ErpType.IT2) {
            isAccountingRealestateSelected =
                this.accountings !== undefined &&
                this.accountings.length > 0 &&
                this.accountings[0].realestate !== undefined;
        } else if (this.rootStore.authStore.user?.erpType === ErpType.RIMO && accountingRowIndex !== undefined) {
            isAccountingRealestateSelected =
                this.accountings !== undefined &&
                this.accountings[accountingRowIndex] &&
                this.accountings[accountingRowIndex].realestate !== undefined;
        } else {
            isAccountingRealestateSelected = false;
        }

        return isAccountingRealestateSelected;
    };

    isAccountingUnitSelected = (accountingRowIndex?: number): boolean => {
        let isAccountingUnitSelected = false;
        if (this.rootStore.authStore.user?.erpType === ErpType.IT2) {
            isAccountingUnitSelected =
                this.accountings !== undefined && this.accountings.length > 0 && this.accountings[0].unit !== undefined;
        } else if (this.rootStore.authStore.user?.erpType === ErpType.RIMO && accountingRowIndex !== undefined) {
            isAccountingUnitSelected =
                this.accountings !== undefined &&
                this.accountings[accountingRowIndex] &&
                this.accountings[accountingRowIndex].unit !== undefined;
        } else {
            isAccountingUnitSelected = false;
        }

        return isAccountingUnitSelected;
    };

    isAccountingAccountSelected = (accountingRowIndex: number): boolean => {
        const isAccountingAccountSelected =
            this.accountings !== undefined &&
            this.accountings[accountingRowIndex] &&
            this.accountings[accountingRowIndex].account !== undefined &&
            this.accountings[accountingRowIndex].account.id !== undefined;

        return isAccountingAccountSelected;
    };

    isAccountingCostCenterSelected = (accountingRowIndex: number): boolean => {
        const isAccountingCostCenterSelected =
            this.accountings !== undefined &&
            this.accountings[accountingRowIndex] &&
            this.accountings[accountingRowIndex].costCenter !== undefined &&
            this.accountings[accountingRowIndex].costCenter.id !== undefined;

        return isAccountingCostCenterSelected;
    };

    isAccountingDateSelected = (accountingRowIndex: number): boolean => {
        const isAccountingDateSelected =
            this.accountings !== undefined &&
            this.accountings[accountingRowIndex] &&
            this.accountings[accountingRowIndex].date !== undefined &&
            this.accountings[accountingRowIndex].date !== "";

        return isAccountingDateSelected;
    };

    isAccountingVatCodeSelected = (accountingRowIndex: number): boolean => {
        const isAccountingVatCodeSelected =
            this.accountings !== undefined &&
            this.accountings[accountingRowIndex] &&
            this.accountings[accountingRowIndex].vatCodeId !== undefined;

        return isAccountingVatCodeSelected;
    };

    isAccountingVatRateSelected = (accountingRowIndex: number): boolean => {
        const isAccountingVatRateSelected =
            this.accountings !== undefined &&
            this.accountings[accountingRowIndex] &&
            this.accountings[accountingRowIndex].vatRate !== undefined &&
            this.accountings[accountingRowIndex].vatRate !== "";

        return isAccountingVatRateSelected;
    };

    isPayoutAccountIdSelected = (): boolean => {
        return this.payoutaccountid !== undefined;
    };

    canEditAccountingAccountField = (accountingRowIndex: number): boolean => {
        const isRealestateSelected = this.isAccountingRealestateSelected(accountingRowIndex);
        const isDateSelected = this.isAccountingDateSelected(accountingRowIndex);

        return isRealestateSelected && isDateSelected;
    };

    canEditAccountingCostcenterField = (accountingRowIndex: number): boolean => {
        const isAccountSelected = this.isAccountingAccountSelected(accountingRowIndex);

        const costcenterData = this.rootStore.accountingCostcenterStore.getCostcenterData(accountingRowIndex);

        return isAccountSelected && costcenterData.length > 0;
    };

    canEditAccountingQuantityField = (accountingRowIndex: number): boolean => {
        const isAccountSelected = this.isAccountingAccountSelected(accountingRowIndex);
        const selectedAccount = this.getAccountingAccount(accountingRowIndex);

        return (
            isAccountSelected &&
            selectedAccount !== undefined &&
            selectedAccount.unitOfMeasure !== undefined &&
            selectedAccount.unitOfMeasure !== null &&
            selectedAccount.unitOfMeasure !== INVOICE_ACCOUNT_UNIT_OF_MEASURE.ZERO
        );
    };

    canEditExtraCostDateField = (accountingRowIndex: number): boolean => {
        const isAccountSelected = this.isAccountingAccountSelected(accountingRowIndex);
        const selectedAccount = this.getAccountingAccount(accountingRowIndex);

        return (
            isAccountSelected &&
            selectedAccount !== undefined &&
            selectedAccount.extraCostDate !== undefined &&
            selectedAccount.extraCostDate === true
        );
    };

    canSeeAccountingVatCodeField = (accountingRowIndex: number): boolean => {
        const isAccountSelected = this.isAccountingAccountSelected(accountingRowIndex);
        const selectedAccount = this.getAccountingAccount(accountingRowIndex);

        return (
            isAccountSelected &&
            selectedAccount !== undefined &&
            selectedAccount.vatConfig !== undefined &&
            selectedAccount.vatConfig !== INVOICE_ACCOUNT_VAT_CONFIG.WITHOUT_VAT
        );
    };

    canEditAccountingVatCodeField = (accountingRowIndex: number): boolean => {
        const isAccountSelected = this.isAccountingAccountSelected(accountingRowIndex);
        const selectedAccount = this.getAccountingAccount(accountingRowIndex);

        return (
            isAccountSelected &&
            selectedAccount !== undefined &&
            selectedAccount.vatConfig !== undefined &&
            selectedAccount.vatConfig !== INVOICE_ACCOUNT_VAT_CONFIG.WITHOUT_VAT &&
            selectedAccount.vatConfig !== INVOICE_ACCOUNT_VAT_CONFIG.ONLY_DEFAULT_VATCODE
        );
    };

    canEditAccountingVatRateField = (accountingRowIndex: number): boolean => {
        return this.isAccountingVatCodeSelected(accountingRowIndex);
    };

    // #24071: the deviating input-tax share exists only for a VAT code flagged "proportionally".
    canSeeAccountingVatProportionField = (accountingRowIndex: number): boolean => {
        return (
            this.isAccountingVatCodeSelected(accountingRowIndex) &&
            this.rootStore.accountingVatStore.isProportionalVatCode(this.getAccountingVatCodeId(accountingRowIndex))
        );
    };

    canSeeAccountingNetAmountField = (accountingRowIndex: number): boolean => {
        return (
            this.isAccountingVatCodeSelected(accountingRowIndex) &&
            this.isAccountingVatRateSelected(accountingRowIndex) &&
            this.getAccountingVatRateType(accountingRowIndex) !== VAT_RATE_TYPE.NO_VAT
        );
    };

    canSeePayoutAccountField = (): boolean => {
        let canSeePayoutAccountField = false;

        const erpType = this.rootStore.authStore.user?.erpType;
        const accountingPayoutAccountStore = this.rootStore.accountingPayoutAccountStore;

        if (erpType === ErpType.IT2) {
            canSeePayoutAccountField =
                accountingPayoutAccountStore.selectablePayoutAccountDataIT2 &&
                accountingPayoutAccountStore.selectablePayoutAccountDataIT2.length > 0;
        }

        return canSeePayoutAccountField;
    };

    canAssignWorkflowUser = (workflowUserRole: WORKFLOWUSER_ROLE) => {
        let canAssignWorkflowUser = false;

        if (this.state !== INVOICE_STATE.CANCELLED && this.isProcessing === false) {
            switch (workflowUserRole) {
                case WORKFLOWUSER_ROLE.VISA_1:
                    canAssignWorkflowUser =
                        this.state && this.state >= INVOICE_STATE.VISA_1_OK && this.state <= INVOICE_STATE.PAID
                            ? false
                            : true;
                    break;
                case WORKFLOWUSER_ROLE.VISA_2:
                    canAssignWorkflowUser =
                        this.state && this.state >= INVOICE_STATE.VISA_2_OK && this.state <= INVOICE_STATE.PAID
                            ? false
                            : true;
                    break;
                case WORKFLOWUSER_ROLE.VISA_3:
                    canAssignWorkflowUser =
                        this.state && this.state >= INVOICE_STATE.VISA_3_OK && this.state <= INVOICE_STATE.PAID
                            ? false
                            : true;
                    break;
            }
        }

        return canAssignWorkflowUser;
    };

    loadAccountTagsAfterRealestateChange = async (accountingRowIndex?: number, erpType?: ErpType) => {
        // if a realestate was changed in Rimo, only the tags for the current accountings entry have to be (re-)loaded
        if (erpType === ErpType.RIMO && accountingRowIndex !== undefined) {
            await this.loadAccountTags(accountingRowIndex);
        } else {
            await this.loadAccountTagsAll();
        }
    };

    loadAccountTagsAfterBookingDateChange = async () => {
        // if the booking date has changed, the tags for all accountings entries have to be (re-)loaded
        await this.loadAccountTagsAll();
    };

    loadAccountTagsAfterCreditorChange = async () => {
        // if the creditor has changed, the tags for all accountings entries have to be (re-)loaded
        await this.loadAccountTagsAll();
    };

    loadAccountTagsAll = async () => {
        // Rows are loaded in parallel and the store deduplicates identical requests, so this is one
        // round trip per distinct creditor/realestate/date. A failing row must not take the others
        // down (tags are a convenience), hence the per-row catch. The previous forEach(async) never
        // actually awaited anything, so callers could not observe completion or failures.
        await Promise.all(
            (this.accountings ?? []).map(async (accounting, accountingRowIndex) => {
                try {
                    await this.loadAccountTags(accountingRowIndex);
                } catch (error) {
                    Sentry.captureException(error);
                }
            })
        );
    };

    loadAccountTags = async (accountingRowIndex: number) => {
        if (
            this.creditor &&
            this.creditor.id &&
            this.isAccountingRealestateSelected(accountingRowIndex) &&
            this.isAccountingDateSelected(accountingRowIndex)
        ) {
            const creditorId = this.creditor.id;
            const realestateId = this.getAccountingRealestate(accountingRowIndex)?.id ?? "";
            const date = this.getAccountingDate(accountingRowIndex) ?? "";

            await this.rootStore.accountingAccountSearchStore.loadAccountTagData(
                creditorId,
                realestateId,
                date,
                accountingRowIndex
            );
        }
    };

    loadAccountingHistoryTagsAfterRealestateChange = async (accountingRowIndex?: number, erpType?: ErpType) => {
        if (erpType === ErpType.RIMO && accountingRowIndex !== undefined) {
            await this.loadAccountingHistoryTags(accountingRowIndex);
        } else {
            await this.loadAccountingHistoryTagsAll();
        }
    };

    loadAccountingHistoryTagsAfterAccountChange = async (accountingRowIndex?: number) => {
        // An account belongs to one row, so only that row's suggestions change. Without a row index
        // (legacy callers) all rows are refreshed as before.
        if (accountingRowIndex !== undefined) {
            try {
                await this.loadAccountingHistoryTags(accountingRowIndex);
            } catch (error) {
                Sentry.captureException(error);
            }
        } else {
            await this.loadAccountingHistoryTagsAll();
        }
    };

    loadAccountingHistoryTagsAfterCreditorChange = async () => {
        await this.loadAccountingHistoryTagsAll();
    };

    loadAccountingHistoryTagsAll = async () => {
        // See loadAccountTagsAll: parallel, deduplicated by the store, failures isolated per row.
        await Promise.all(
            (this.accountings ?? []).map(async (accounting, accountingRowIndex) => {
                try {
                    await this.loadAccountingHistoryTags(accountingRowIndex);
                } catch (error) {
                    Sentry.captureException(error);
                }
            })
        );
    };

    loadAccountingHistoryTags = async (accountingRowIndex: number) => {
        if (this.creditor && this.creditor.id && this.isAccountingRealestateSelected(accountingRowIndex)) {
            const creditorId = this.creditor.id;
            const realestateId = this.getAccountingRealestate(accountingRowIndex)?.id ?? "";
            const account = this.getAccountingAccount(accountingRowIndex);

            await this.rootStore.accountingBookingTextStore.loadAccountingsHistoryTagData(
                accountingRowIndex,
                creditorId,
                realestateId,
                account?.number
            );
        }
    };

    validateIfAccountsAreValidForBookingDate = () => {
        this.accountings?.forEach((accounting, index) => {
            const account = accounting.account;

            if (account) {
                const isValid = this.isAccountValidForBookingDate(account);

                if (!isValid) {
                    this.rootStore.invoiceStore.resetAccountingAccountFormFields(index);

                    if (this.isAccountingCostCenterSelected(index)) {
                        this.rootStore.invoiceStore.resetAccountingAccountFormFields(index);
                    }
                }
            }
        });
    };

    isAccountValidForBookingDate = (account: IAccount): boolean => {
        let isAccountValidFOrBookingDate = false;

        if (account.validTo) {
            if (account.validFrom <= this.accountingDate && account.validTo >= this.accountingDate) {
                isAccountValidFOrBookingDate = true;
            }
        } else {
            if (
                account.validFrom <= this.accountingDate &&
                (account.validTo === null || account.validTo === undefined)
            ) {
                isAccountValidFOrBookingDate = true;
            }
        }

        return isAccountValidFOrBookingDate;
    };

    startInvoiceStateUpdateSubscription() {
        if (this.invoiceStateUpdateSubscription) {
            this.stopInvoiceStateUpdateSubscription();
        }

        if (this.id && this.id !== "") {
            const invoiceStateUpdateSubscription = apolloClientInstance
                .subscribe<SubscribeToInvoiceStateUpdate, SubscribeToInvoiceStateUpdateVariables>({
                    query: SUBSCRIBE_TO_INVOICE_STATE_UPDATE,
                    variables: { invoiceid: this.id }
                })
                .subscribe({
                    next: ({ data }) => {
                        if (data?.invoice && data.invoice.length > 0) {
                            this.onInvoiceStateUpdateDataReceived(data.invoice[0]);
                        }
                    },
                    error: () => {
                        // Error is logged by Apollo errorLink
                        this.stopInvoiceStateUpdateSubscription();
                    }
                });

            this.setInvoiceStateUpdateSubscription(invoiceStateUpdateSubscription);
        }
    }

    stopInvoiceStateUpdateSubscription() {
        if (this.invoiceStateUpdateSubscription) {
            this.invoiceStateUpdateSubscription.unsubscribe();
            this.setInvoiceStateUpdateSubscription(undefined);
        }
    }

    onInvoiceStateUpdateDataReceived = (invoice: SubscribeToInvoiceStateUpdate_invoice) => {
        const currentState = this.state;
        const newState = invoice.state as INVOICE_STATE;

        if (currentState !== newState) {
            this.updateProperty(INVOICE_PROPERTY.STATE, newState);
        }

        if (
            this.invoiceWorkflowUsersSubscription &&
            (newState === INVOICE_STATE.ASSIGN_WORKFLOW_USERS || newState === INVOICE_STATE.READY_FOR_VISA)
        ) {
            this.stopInvoiceWorkflowUsersSubscription();
        }

        const hasUnsavedRealestateChanges = this.fieldsWithChanges?.includes(INVOICE_PROPERTY.REALESTATE);
        if (
            newState === INVOICE_STATE.ASSIGN_REALESTATE &&
            !hasUnsavedRealestateChanges &&
            this.realestate?.id !== undefined
        ) {
            this.concludeHumanTaskAssignRealestateAndOtherData();
        }
    };

    setInvoiceStateUpdateSubscription = (invoiceStateUpdateSubscription: Subscription | undefined) => {
        this.invoiceStateUpdateSubscription = invoiceStateUpdateSubscription;
    };

    /* HISTORY UPDATE SUBSCRIPTION */

    startInvoiceHistoryUpdateSubscription() {
        if (this.invoiceHistoryUpdateSubscription) {
            this.stopInvoiceHistoryUpdateSubscription();
        }

        if (this.id && this.id !== "") {
            // Watches only the newest history entry. Hasura emits the current state on
            // connect (which refreshes the history when the tab is opened) and again on
            // every change; the full list is then fetched via reloadHistory.
            const invoiceHistoryUpdateSubscription = apolloClientInstance
                .subscribe<SubscribeToInvoiceHistoryUpdate, SubscribeToInvoiceHistoryUpdateVariables>({
                    query: SUBSCRIBE_TO_INVOICE_HISTORY_UPDATE,
                    variables: { invoiceid: this.id }
                })
                .subscribe({
                    next: ({ data }) => {
                        if (data?.history) {
                            this.reloadHistory();
                        }
                    },
                    error: () => {
                        // Error is logged by Apollo errorLink
                        this.stopInvoiceHistoryUpdateSubscription();
                    }
                });

            this.setInvoiceHistoryUpdateSubscription(invoiceHistoryUpdateSubscription);
        }
    }

    stopInvoiceHistoryUpdateSubscription() {
        if (this.invoiceHistoryUpdateSubscription) {
            this.invoiceHistoryUpdateSubscription.unsubscribe();
            this.setInvoiceHistoryUpdateSubscription(undefined);
        }
    }

    setInvoiceHistoryUpdateSubscription = (invoiceHistoryUpdateSubscription: Subscription | undefined) => {
        this.invoiceHistoryUpdateSubscription = invoiceHistoryUpdateSubscription;
    };

    concludeHumanTaskAssignRealestateAndOtherData = async (
        displaySuccessNotification: boolean = true
    ): Promise<boolean> => {
        let wasHumanTaskSuccessfullyConcluded = false;

        const concludeHumanTaskVariables: IConcludeHumanTaskRequestBody = {
            taskname: "AssignRealestateAndOtherData",
            workflowid: this.workflowInstance ?? ""
        };

        try {
            this.setIsConcludingHumanTaskAssignRealestateAndOtherData(true);

            await Axios.getAuthInstance().post(NetworkConfig.concludeHumanTaskUrl, concludeHumanTaskVariables, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });

            wasHumanTaskSuccessfullyConcluded = true;

            this.startInvoiceWorkflowUsersSubscription();

            if (displaySuccessNotification) {
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.kredi_flow.action.start_workflow.success"),
                    MessageType.SUCCESS
                );
            }
        } catch (error) {
            console.error("Error concluding human task 'AssignRealestateAndOtherData': ", error);
        } finally {
            this.setIsConcludingHumanTaskAssignRealestateAndOtherData(false);
        }

        return wasHumanTaskSuccessfullyConcluded;
    };

    setIsConcludingHumanTaskAssignRealestateAndOtherData = (isConcluding: boolean) => {
        this.isConcludingHumanTaskAssignRealestateAndOtherData = isConcluding;
    };

    abortInvoiceProcess = async (): Promise<boolean> => {
        let wasInvoiceProcessSuccessfullyAborted = false;

        const abortInvoiceProcessRequestBody: IAbortInvoiceProcessRequestBody = {
            invoiceids: [this.id ?? ""]
        };

        try {
            await Axios.getAuthInstance().post(NetworkConfig.abortInvoiceProcessUrl, abortInvoiceProcessRequestBody, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });
            wasInvoiceProcessSuccessfullyAborted = true;
            await this.reloadHistory();
        } catch (error) {
            console.error("Error while trying to abort the invoice process: ", error);
        }

        return wasInvoiceProcessSuccessfullyAborted;
    };

    setNumberOfVisasArray = (numberOfVisas: INumberOfVisas[]) => {
        this.numberOfVisasArray = numberOfVisas;
    };

    setNumberOfVisas = (numberOfVisas: number) => {
        this.numberOfVisas = numberOfVisas;
    };

    // This is the only place that 'numberOfVisas' is set
    // If the numberOfVisa field on the invoice is set (set by workflow) this will be used
    // If not, as a backup, first the number of visas will be loaded from a view (cred.v_numberofvisas)
    // or if should also be empty from the customersettings (ums.customersettings -> "CRED.NUMBEROFVISA")
    loadNumberOfVisas = async () => {
        let numberOfVisas: number = 0;

        if (this.numberOfVisa) {
            numberOfVisas = this.numberOfVisa;
        } else {
            const { data }: { data: GetNumberOfVisas } = await apolloClientInstance.query<GetNumberOfVisas>({
                query: GET_NUMBER_OF_VISAS,
                fetchPolicy: "network-only"
            });

            const numberOfVisasArray: INumberOfVisas[] =
                data && data.numberofvisas && data.numberofvisas.length > 0
                    ? data.numberofvisas.map((numberOfVisa) => {
                          return {
                              realestateId: numberOfVisa.realestateid,
                              numberOfVisa: numberOfVisa.numberofvisa ?? 0
                          };
                      })
                    : [];

            this.setNumberOfVisasArray(numberOfVisasArray);

            numberOfVisas = this.getNumberOfVisasFromViewOrCustomerSettings(this.realestate?.id);
        }

        this.setNumberOfVisas(numberOfVisas);
    };

    getNumberOfVisasFromViewOrCustomerSettings = (realestateId: string | undefined): number => {
        let numberOfVisas: number = 0;

        if (realestateId) {
            numberOfVisas =
                this.numberOfVisasArray.find((numberOfVisa) => {
                    return numberOfVisa.realestateId === realestateId;
                })?.numberOfVisa ?? 0;
        }

        if (numberOfVisas === 0) {
            numberOfVisas = this.numberOfVisasCustomerSetting;
        }

        return numberOfVisas;
    };

    setDuplicates = (duplicates: IInvoiceDuplicate[]) => {
        this.duplicates = duplicates;
    };

    setIsDuplicateModalDisplayed = (isDisplayed: boolean) => {
        this.isDuplicateModalDisplayed = isDisplayed;
    };

    getDuplicatesAndDisplayModal = async () => {
        const duplicates = await this.getDuplicates();

        if (duplicates.length > 0) {
            this.setIsDuplicateModalDisplayed(true);
        }
    };

    getDuplicates = async (): Promise<IInvoiceDuplicate[]> => {
        let invoiceDuplicates: IInvoiceDuplicate[] = [];

        const creditorId = this.creditor?.id;
        const invoiceNumber = this.invoiceNumber;
        const invoiceId = this.id;

        if (!creditorId || !invoiceNumber) {
            return [];
        }

        const getDuplicatesVariables: IGetDuplicatesRequestBody = {
            creditorid: creditorId,
            invoicenumber: invoiceNumber.toString(),
            invoiceid: invoiceId
        };

        try {
            const response = await Axios.getAuthInstance().post(
                NetworkConfig.getDuplicatesUrl,
                getDuplicatesVariables,
                {
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );

            const jsonResponse: IGetDuplicatesResponse = response.data;
            if (jsonResponse.data && jsonResponse.data.length > 0) {
                invoiceDuplicates = jsonResponse.data
                    .sort((a, b) => {
                        // sort by InvoiceNumber is ascending order
                        return a.InvoiceNumber - b.InvoiceNumber;
                    })
                    .map((duplicate) => {
                        return {
                            invoiceId: duplicate.InvoiceId,
                            invoiceNumber: duplicate.InvoiceNumber,
                            amount: duplicate.Amount
                        };
                    });

                this.setDuplicates(invoiceDuplicates);
            }
        } catch (error) {
            console.error("Error occured while trying call the 'getDuplicates' endpoint: ", error);
        }

        return invoiceDuplicates;
    };

    get isCreditorSelected(): boolean {
        return this.creditor !== undefined;
    }

    get isRealestateSelected(): boolean {
        return this.realestate !== undefined;
    }

    get isUnitSelected(): boolean {
        return this.unit !== undefined;
    }

    get isTicketSelected(): boolean {
        return this.ticket !== undefined;
    }

    get isOrderSelected(): boolean {
        return this.order !== undefined;
    }

    get isNewInvoice(): boolean {
        return this.id === undefined;
    }

    get displayInvoiceAccountingsSection(): boolean {
        return !this.isNewInvoice && this.invoiceFieldsOriginal.realestate?.id !== undefined;
    }

    get isFileSelected(): boolean {
        return this.invoiceFile !== undefined && this.invoiceFile.fileId !== undefined;
    }

    get hasChanges(): boolean {
        return (this.fieldsWithChanges && this.fieldsWithChanges?.length > 0) ||
            (this.fieldsWithChangesPaymentInfo && this.fieldsWithChangesPaymentInfo?.length > 0) ||
            (this.fieldsWithChangesCreditorInfo && this.fieldsWithChangesCreditorInfo?.length > 0) ||
            (this.fieldsWithChangesInvoiceInfo && this.fieldsWithChangesInvoiceInfo?.length > 0) ||
            (this.fieldsWithChangesInvoiceAttachmentFiles && this.fieldsWithChangesInvoiceAttachmentFiles?.length > 0)
            ? true
            : false;
    }

    get hasChangesAccounting(): boolean {
        let hasChangesAccounting = false;

        if (this.fieldsWithChangesInvoiceAccountings && this.fieldsWithChangesInvoiceAccountings.length > 0) {
            this.fieldsWithChangesInvoiceAccountings.forEach((changesForAccountingArray: string[]) => {
                if (
                    changesForAccountingArray === undefined ||
                    (changesForAccountingArray && changesForAccountingArray.length === 0)
                ) {
                    hasChangesAccounting = false;
                } else {
                    hasChangesAccounting = true;
                }
            });
        }

        return hasChangesAccounting || this.accountings?.length !== this.invoiceFieldsOriginal.accountings?.length
            ? true
            : false;
    }

    get hasCurrentUserApproveOrRejectRightsForCurrentInvoiceState(): boolean {
        let canUserApproveOrReject = false;

        const currentUserId = this.rootStore.authStore.user?.userid;

        let currentUserWorkflowUser: IWorkflowUser | undefined = undefined;

        if (currentUserId) {
            this.workflowUsers
                ?.filter((workflowuser) => {
                    return (
                        workflowuser.role !== WORKFLOWUSER_ROLE.ACCOUNTANT &&
                        workflowuser.role !== WORKFLOWUSER_ROLE.REJECTED_TO
                    );
                })
                .forEach((workflowUser) => {
                    if (workflowUser.user.id === currentUserId) {
                        currentUserWorkflowUser = workflowUser;
                    }
                });
        }

        const currentUserIsWorkflowUser = currentUserWorkflowUser !== undefined;

        if (currentUserIsWorkflowUser && currentUserWorkflowUser) {
            activeRoleStates.forEach((activeRoleState) => {
                if (activeRoleState.role === currentUserWorkflowUser?.role && activeRoleState.state === this.state) {
                    canUserApproveOrReject = true;
                }
            });
        }

        return canUserApproveOrReject;
    }

    get canCurrentUserSeeStartWorkflowButton(): boolean {
        return this.state !== undefined && this.state === INVOICE_STATE.ASSIGN_WORKFLOW_USERS;
    }

    get canCurrentUserSeeRestartCancelWorkflowButton(): boolean {
        return this.state !== undefined && this.state === INVOICE_STATE.REJECTED;
    }

    get canCurrentUserApproveOrReject(): boolean {
        let canUserApproveOrReject = false;

        if (
            this.state === INVOICE_STATE.READY_FOR_VISA ||
            this.state === INVOICE_STATE.VISA_1_OK ||
            this.state === INVOICE_STATE.VISA_2_OK ||
            this.state === INVOICE_STATE.VISA_3_OK
        ) {
            canUserApproveOrReject = true;
        }

        return canUserApproveOrReject;
    }

    get canCurrentUserRestartOrCancel(): boolean {
        return this.state === INVOICE_STATE.REJECTED;
    }

    get canCurrentUserDelete(): boolean {
        return this.state === INVOICE_STATE.CANCELLED;
    }

    get realestateRequiredToContinueWorkflow(): boolean {
        let isRealestateRequired = false;

        if (this.state === INVOICE_STATE.CANCELLED) {
            isRealestateRequired = false;
        } else if (this.state !== undefined && this.realestate === undefined) {
            isRealestateRequired = true;
        }
        return isRealestateRequired;
        // return this.state !== undefined && this.realestate === undefined;
    }

    get canDeleteDocument(): boolean {
        if (this.state && this.state >= INVOICE_STATE.FULLY_SIGHTED) {
            if (this.state !== INVOICE_STATE.REJECTED) {
                return false;
            }
        }

        return true;
    }

    get hasDuplicates(): boolean {
        if (this.duplicates) {
            return this.duplicates.length > 0;
        } else {
            return false;
        }
    }

    get pinnedComments(): IHistory[] {
        if (this.history) {
            return this.history.filter((historyEntry) => historyEntry.isPinned) as IHistory[];
        } else {
            return [];
        }
    }

    static formatDateHistoryEntry = (dateString: string, adjustTimezoneUtcToZH: boolean) => {
        const dateFormat = "d.M.yy HH:mm";

        if (adjustTimezoneUtcToZH) {
            const timeZone = "Europe/Zurich";
            const zonedDate = utcToZonedTime(dateString + "Z", timeZone);
            return format(zonedDate, dateFormat, { timeZone });
        } else {
            return format(new Date(dateString), dateFormat);
        }
    };

    static getTimeStampForHistoryEntry = (dateString: string, adjustTimezoneUtcToZH: boolean) => {
        if (adjustTimezoneUtcToZH) {
            const timeZone = "Europe/Zurich";
            const zonedDate = utcToZonedTime(dateString + "Z", timeZone);
            return zonedDate;
        } else {
            return new Date(dateString);
        }
    };

    // An invoice can have multiple accountings but all of them will have the same date
    get accountingDate(): string {
        let accountingDate = "";

        if (this.accountings && this.accountings.length > 0 && this.accountings[0].date) {
            accountingDate = this.accountings[0].date;
        }

        return accountingDate;
    }

    get totalAccountingAmount(): number {
        let totalAmount: number = 0;

        if (this.accountings) {
            this.accountings.forEach((accounting) => {
                if (accounting.amount) {
                    totalAmount += Number(accounting.amount);
                }
            });
        }

        return Number(totalAmount.toFixed(2));
    }

    get totalAccountingAmountMatchesInvoiceAmount(): boolean {
        return this.totalAccountingAmount === Number(this.amount);
    }

    get hasSavedAccountings(): boolean {
        let areAccountingsValid = true;

        let isValidAccountings: boolean | undefined = undefined;

        if (this.invoiceFieldsOriginal && this.invoiceFieldsOriginal.accountings) {
            this.invoiceFieldsOriginal.accountings.forEach((accounting, index) => {
                isValidAccountings = this.validateAccountingObject(accounting, areAccountingsValid);
            });
        }

        areAccountingsValid = isValidAccountings === false ? false : this.totalAccountingAmountMatchesInvoiceAmount;

        this.resetAccountingValidationErrors();

        return this.invoiceFieldsOriginal.accountings &&
            this.invoiceFieldsOriginal.accountings.length > 0 &&
            areAccountingsValid
            ? true
            : false;
    }

    get isLastVisaToApprove(): boolean {
        return (
            (this.state === INVOICE_STATE.READY_FOR_VISA && this.numberOfVisas === 1) ||
            (this.state === INVOICE_STATE.VISA_1_OK && this.numberOfVisas === 2) ||
            (this.state === INVOICE_STATE.VISA_2_OK && this.numberOfVisas === 3)
        );
    }

    get isInvoiceStateAllowingToChangeInvoiceType(): boolean {
        return (
            this.state !== null &&
            this.state !== undefined &&
            (this.state < INVOICE_STATE.FULLY_SIGHTED || this.state === INVOICE_STATE.REJECTED)
        );
    }

    get numberOfVisasCustomerSetting(): number {
        const navStore = this.rootStore.navStore;

        const numberOfVisas =
            navStore.customer.settings &&
            navStore.customer.settings.cred &&
            navStore.customer.settings.cred.numberOfVisas
                ? navStore.customer.settings.cred.numberOfVisas
                : 0;

        return numberOfVisas;
    }

    get iban(): string | undefined {
        return this.paymentAccount?.iban ?? this.paymentInfo?.iban;
    }

    get hasNewInvoiceAttachmentFiles(): boolean {
        return this.invoiceAttachmentFiles && this.invoiceAttachmentFiles.length > 0
            ? this.invoiceAttachmentFiles.some((file) => file.state === FILE_STATE.NEW)
            : false;
    }

    get searchString() {
        const date = this.date
            ? this.rootStore.uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP
                ? formatDate(this.date)
                : formatDate(this.date, true)
            : "-";
        const dueDate = this.dueDate
            ? this.rootStore.uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP
                ? formatDate(this.dueDate)
                : formatDate(this.dueDate, true)
            : "-";
        const formattedAmount = this.amount !== null && this.amount !== undefined ? this.amount.toFixed(2) : "";
        return [
            this.number ?? "",
            date,
            dueDate,
            this.creditor?.name ?? "",
            this.creditor?.number ?? "",
            this.invoiceNumber ?? "",
            formattedAmount,
            this.realestate?.name ?? "",
            this.realestate?.city ?? "",
            this.realestate?.zip ?? "",
            this.realestate?.number ?? "",
            this.assignedUser?.name1 ?? "",
            this.assignedUser?.name2 ?? ""
        ]
            .join(" ")
            .toLowerCase();
    }
}
